import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Save, Loader2, SlidersHorizontal, Users2, Map, Building2, ScrollText, GitBranch, Database, Trash2, ShieldAlert, CalendarRange, History, HeartHandshake, MapPin, ClipboardList, UserCog, Pencil, GitMerge, Plus, Search, ExternalLink, UserRound, Users, Compass, Barcode, Upload, CheckCircle2, AlertTriangle, Wand2, FileCheck2, Download, Star, Mail, Send, Phone, Activity, Clock, RefreshCw, Wallet, Target, PhoneCall, DatabaseBackup, ShieldCheck, HardDrive, RotateCcw, ChevronDown, DoorOpen, Crosshair, UserX, UserCheck, ChevronLeft, ChevronRight, CalendarDays, CalendarClock, X } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { frenchDate, frenchDateTime } from "@/lib/shariti";
import { toast } from "sonner";
import { ZonesManager } from "@/components/admin/ZonesManager";
import { DispoEditor } from "@/components/DispoEditor";
import { emptyDispo } from "@/lib/dispo";
import {
  startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear,
  addDays, addWeeks, addMonths, addYears, format as fmtDate,
} from "date-fns";
import { fr as frLocale } from "date-fns/locale";

const GROUPS = [
  { title: "Règles de rotation", fields: [
    ["rotation_same_association_days", "Même association — délai (jours)"],
    ["rotation_diff_association_days", "Association différente — délai (jours)"],
  ]},
  { title: "Cycle de vie des spots", fields: [
    ["dormant_after_days", "Passage en dormant (jours)"],
    ["refus_temporaire_default_days", "Retour refus temporaire (jours)"],
    ["nrp_relance_days", "Relance NRP (jours)"],
    ["oui_ghoste_relance_days", "Relance oui ghosté (jours)"],
  ]},
  { title: "Potentiel & bulletins", fields: [
    ["potential_flux_threshold_A", "Seuil potentiel A (flux/j)"],
    ["potential_flux_threshold_B", "Seuil potentiel B (flux/j)"],
    ["bulletins_min_threshold", "Seuil bulletins bruts/jour"],
  ]},
  { title: "Objectifs de remplissage (%)", fields: [
    ["fill_target_s1", "Cible S+1"],
    ["fill_target_s2", "Cible S+2"],
    ["fill_target_s3", "Cible S+3"],
    ["fill_target_s4", "Cible S+4"],
  ]},
  { title: "Planning (capacité)", fields: [
    ["journees_per_ligne_week", "Journées-spot / ligne / semaine (capacité)"],
  ]},
  { title: "Objectifs de pilotage", fields: [
    ["journees_spot_target_week", "Journées-spot / semaine"],
    ["rebooking_rate_target", "Taux de rebooking (%)"],
    ["bulletins_annual_net_target", "Bulletins nets / an"],
    ["spots_actifs_target", "Spots actifs (banque)"],
    ["new_spots_per_week_target", "Nouveaux spots / semaine"],
  ]},
  { title: "Objectifs bulletins — cockpit (0 = automatique)", fields: [
    ["bulletins_target_day", "Objectif / jour"],
    ["bulletins_target_week", "Objectif / semaine"],
    ["bulletins_target_month", "Objectif / mois"],
    ["bulletins_target_year", "Objectif / an"],
  ]},
  { title: "Rythme CDP (défaut)", fields: [
    ["cdp_default_days_per_week", "Jours / semaine"],
    ["cdp_default_hours_per_day", "Heures / jour"],
    ["cdp_default_hourly_rate", "Taux horaire (€)"],
    ["cdp_default_lignes", "Lignes / CDP"],
  ]},
];

export default function Admin() {
  const [sectionId, setSectionId] = useState(ADMIN_SECTIONS[0].id);
  const section = ADMIN_SECTIONS.find((s) => s.id === sectionId) || ADMIN_SECTIONS[0];
  const [sub, setSub] = useState(ADMIN_SECTIONS[0].tabs[0].value);

  const changeSection = (id) => {
    const sec = ADMIN_SECTIONS.find((s) => s.id === id) || ADMIN_SECTIONS[0];
    setSectionId(id);
    setSub(sec.tabs[0].value);
  };
  const activeTab = section.tabs.find((t) => t.value === sub) || section.tabs[0];
  const ActivePanel = activeTab.Panel;

  return (
    <>
      <PageHeader title="Administration" subtitle="Trois espaces clairs : Équipes & Zones, Référentiels & Données, Configuration." />
      <PageBody>
        {/* Sélecteur de section (niveau 1) */}
        <div className="grid gap-2.5 @3xl/page:grid-cols-3" data-testid="admin-sections">
          {ADMIN_SECTIONS.map((s) => {
            const active = s.id === sectionId;
            return (
              <button
                key={s.id} onClick={() => changeSection(s.id)} data-testid={`admin-section-${s.id}`}
                className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition-shadow ${active ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.06)] shadow-[var(--shadow-md)]" : "border-[var(--border-soft)] bg-white hover:shadow-[var(--shadow-sm)]"}`}
              >
                <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${active ? "bg-[var(--shariti-orange)] text-white" : "bg-[var(--surface-2)] text-[var(--shariti-orange)]"}`}>
                  <s.icon className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-bold">{s.label}</div>
                  <div className="text-[11px] leading-snug text-muted-ink">{s.desc}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Sous-navigation (niveau 2) */}
        <div className="mt-5 flex flex-wrap items-center gap-1.5 border-b border-[var(--border-soft)] pb-3" data-testid="admin-subtabs">
          {section.tabs.map((t) => {
            const active = t.value === sub;
            return (
              <button
                key={t.value} onClick={() => setSub(t.value)} data-testid={`admin-tab-${t.value}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${active ? "bg-[var(--shariti-orange)] text-white shadow-[var(--shadow-sm)]" : "bg-[var(--surface-2)] text-[var(--shariti-ink)] hover:bg-[#FFE9DC]"}`}
              >
                <t.icon className="h-3.5 w-3.5" />{t.label}
              </button>
            );
          })}
        </div>

        {/* Contenu du panneau actif */}
        <div className="mt-4" data-testid={`admin-panel-${activeTab.value}`}>
          <ActivePanel />
        </div>
      </PageBody>
    </>
  );
}

// Regroupement des anciens 14 onglets en 3 sections thématiques.
function DataPanel() {
  return (
    <div className="space-y-6">
      <AddressBackfillPanel />
      <MissingGpsPanel />
      <PhoneBackfillPanel />
      <DemoDataPanel />
      <HistoricalDataPanel />
      <EvergivingDataPanel />
    </div>
  );
}

function EnseigneCleanupCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);
  const load = () => {
    setLoading(true);
    api.get("/admin/enseignes/status").then((r) => setStatus(r.data))
      .catch(() => setStatus(null)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const runFix = async () => {
    setFixing(true);
    try {
      const { data } = await api.post("/admin/enseignes/backfill");
      toast.success(`${data.fixed} enseigne(s) recalée(s) sur la vraie marque`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la correction");
    } finally { setFixing(false); }
  };
  const mismatched = status?.mismatched || 0;
  return (
    <div className="mt-4 rounded-2xl border border-[var(--border-soft)] bg-white p-5" data-testid="enseigne-cleanup-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-base font-bold">
            <Wand2 className="h-4 w-4 text-[var(--shariti-orange)]" /> Correction des enseignes
          </h3>
          <p className="text-xs text-muted-ink">
            Recale le champ « enseigne » sur la vraie marque (déduite du nom), en corrigeant les
            enseignes polluées par le terme de recherche Google (ex. un Intersport tagué Decathlon).
            N'écrase jamais une enseigne déjà cohérente.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="enseigne-status-refresh">
          <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>
      {loading ? (
        <p className="mt-3 text-sm text-muted-ink">Analyse…</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2">
            <div className="text-xl font-black" data-testid="enseigne-mismatched-count"
              style={{ color: mismatched > 0 ? "#9B2226" : "#1F9D55" }}>{mismatched}</div>
            <div className="text-[10px] uppercase tracking-wide text-muted-ink">à corriger / {status?.total ?? 0}</div>
          </div>
          <Button onClick={runFix} disabled={fixing || mismatched === 0}
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="enseigne-fix-btn">
            {fixing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Wand2 className="mr-1 h-4 w-4" />}
            {mismatched === 0 ? "Tout est cohérent" : `Corriger ${mismatched} enseigne(s)`}
          </Button>
        </div>
      )}
      {status?.sample?.length > 0 && (
        <div className="mt-3 max-h-40 space-y-1 overflow-y-auto text-[11px]" data-testid="enseigne-sample">
          {status.sample.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 rounded-lg bg-[var(--surface-2)] px-2 py-1">
              <span className="truncate">{s.normalized_name}</span>
              <span className="shrink-0 font-semibold">
                <span className="text-[#9B2226] line-through">{s.enseigne || "—"}</span>
                <span className="mx-1">→</span>
                <span className="text-[#0F5A2F]">{s.derived}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DiagnosticPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/admin/cdp-diagnostic").then((r) => setData(r.data))
      .catch(() => setData({ cdps: [] })).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const cdps = data?.cdps || [];
  return (
    <div data-testid="cdp-diagnostic-panel">
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-bold">Diagnostic des files de prospection</h3>
            <p className="text-xs text-muted-ink">Par CDP : zones attribuées et nombre de spots appelables (par département). Une file vide = 0 zone ou 0 spot appelable.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="cdp-diagnostic-refresh">
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
        </div>
        {loading ? (
          <p className="mt-4 text-sm text-muted-ink">Chargement…</p>
        ) : (
          <div className="mt-4 space-y-2.5">
            {cdps.map((c) => {
              const empty = c.callable_spots === 0;
              return (
                <div key={c.cdp_id}
                  className={`rounded-xl border p-3 ${empty ? "border-[rgba(214,69,69,0.32)] bg-[rgba(214,69,69,0.04)]" : "border-[var(--border-soft)] bg-[var(--surface-2)]"}`}
                  data-testid={`cdp-diag-${c.cdp_id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 font-bold">
                        {c.cdp_name || "—"}
                        {!c.active && <Badge variant="outline" className="text-[10px]">inactif</Badge>}
                      </div>
                      <div className="truncate text-xs text-muted-ink">{c.email}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className={`text-xl font-black ${empty ? "text-[#9B2226]" : "text-[#1F9D55]"}`} data-testid={`cdp-diag-callable-${c.cdp_id}`}>{c.callable_spots}</div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-ink">appelables</div>
                    </div>
                  </div>
                  {c.zone_count === 0 ? (
                    <div className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-[#9B2226]">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Aucune zone attribuée — assignez une zone dans l'onglet « Zones ».
                    </div>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {c.zones.map((z) => (
                        <span key={z.id}
                          className="inline-flex items-center gap-1 rounded-full border border-[var(--border-soft)] bg-white px-2 py-0.5 text-[11px]"
                          title={`Départements ${(z.departments || []).join(", ") || "non définis"}`}>
                          <MapPin className="h-3 w-3 text-[var(--shariti-orange)]" /> {z.name}
                          <span className="text-muted-ink">· {z.callable_spots}/{z.total_spots}</span>
                          {(z.departments || []).length === 0 && <span className="text-[#9B2226]"> · sans dépt !</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {cdps.length === 0 && <p className="text-sm text-muted-ink">Aucun CDP.</p>}
          </div>
        )}
      </div>
      <EnseigneCleanupCard />
    </div>
  );
}

function AddressBackfillPanel() {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = () => api.get("/admin/addresses/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  // Polling tant qu'un job d'enrichissement tourne (progression en direct)
  useEffect(() => {
    if (status?.job?.status !== "running") return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [status?.job?.status]); // eslint-disable-line

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/addresses/test");
      setTestResult(data);
      if (data.ok) toast.success("Clé Google Places opérationnelle");
      else toast.error("Google Places indisponible — voir le détail ci-dessous");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test impossible"); }
    finally { setTesting(false); }
  };

  const startBackfill = async () => {
    setStarting(true);
    try {
      await api.post("/admin/addresses/backfill");
      toast.success("Enrichissement des adresses lancé en arrière-plan");
      setTimeout(load, 800);
    } catch (e) { toast.error(e?.response?.data?.detail || "Lancement impossible"); }
    finally { setStarting(false); }
  };

  if (!status) return <Loading />;
  const running = status.job?.status === "running";
  const job = status.job;
  const pct = job?.total ? Math.round((100 * (job.processed || 0)) / job.total) : 0;
  const stats = [
    { icon: MapPin, label: "Spots avec adresse", value: status.live_with_address, color: "#1F9D55" },
    { icon: AlertTriangle, label: "Spots sans adresse", value: status.live_missing, color: "#EE722E" },
    { icon: Database, label: "Total spots actifs", value: status.live_total, color: "#2F6FED" },
  ];
  return (
    <div data-testid="address-backfill-panel">
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--surface-2)]"><MapPin className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold">Enrichissement des adresses (Google Places)</h3>
            <p className="mt-1 text-sm text-muted-ink">
              Complète automatiquement l'<span className="font-semibold text-ink">adresse complète</span> (et les coordonnées GPS) des spots actifs qui n'en ont pas,
              via l'API Google Places. Les adresses déjà renseignées ne sont <span className="font-semibold text-ink">jamais écrasées</span>.
              La clé API est utilisée côté serveur uniquement.
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 @3xl/page:grid-cols-3">
          {stats.map((st) => (
            <div key={st.label} className="flex items-center gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid={`addr-stat-${st.label}`}>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${st.color}18` }}><st.icon className="h-4 w-4" style={{ color: st.color }} /></div>
              <div><div className="text-lg font-extrabold leading-none" data-testid="addr-stat-value">{st.value ?? "—"}</div><div className="text-[11px] text-muted-ink">{st.label}</div></div>
            </div>
          ))}
        </div>

        {/* Statut clé + actions */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${status.configured ? "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]" : "bg-[rgba(214,69,69,0.1)] text-[#9B2226]"}`} data-testid="addr-key-state">
            {status.configured ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {status.configured ? "Clé configurée (serveur)" : "Clé absente"}
          </span>
          <Button variant="secondary" onClick={runTest} disabled={testing || !status.configured}
            className="border border-[var(--border-soft)] bg-white" data-testid="addr-test-btn">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="mr-1.5 h-4 w-4" />Tester la clé</>}
          </Button>
          <Button onClick={startBackfill} disabled={starting || running || !status.configured || status.live_missing === 0}
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="addr-backfill-btn">
            {starting || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <><MapPin className="mr-1.5 h-4 w-4" />Enrichir les adresses manquantes</>}
          </Button>
        </div>

        {/* Résultat du self-test */}
        {testResult && !testResult.ok && (
          <div className="mt-3 rounded-xl border border-[rgba(214,69,69,0.28)] bg-[rgba(214,69,69,0.04)] p-3 text-xs text-[#9B2226]" data-testid="addr-test-error">
            <div className="font-bold">Google Places indisponible</div>
            <div className="mt-1 text-[#7a2a2d]">{testResult.error}</div>
            <div className="mt-1.5 text-muted-ink">Activez « Places API (New) » sur le projet Google Cloud, puis autorisez cette API dans les restrictions de la clé.</div>
          </div>
        )}
        {testResult && testResult.ok && (
          <div className="mt-3 rounded-xl border border-[rgba(31,157,85,0.28)] bg-[rgba(31,157,85,0.05)] p-3 text-xs text-[#0F5A2F]" data-testid="addr-test-ok">
            <div className="font-bold">Clé opérationnelle ✓</div>
            {testResult.sample?.formatted_address && <div className="mt-1">Exemple : {testResult.sample.formatted_address}</div>}
          </div>
        )}

        {/* Progression / résultat du job */}
        {job && (
          <div className="mt-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="addr-job-state">
            {running ? (
              <>
                <div className="flex items-center justify-between text-xs font-semibold">
                  <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--shariti-orange)]" />Enrichissement en cours…</span>
                  <span>{job.processed || 0}{job.total ? ` / ${job.total}` : ""} traités · {job.matched || 0} trouvées</span>
                </div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white">
                  <div className="h-full rounded-full bg-[var(--shariti-orange)] transition-[width] duration-300" style={{ width: `${pct}%` }} />
                </div>
              </>
            ) : (
              <div className="text-xs text-muted-ink">
                {job.status === "failed"
                  ? <span className="font-semibold text-[#9B2226]">Dernier enrichissement échoué : {job.error}</span>
                  : job.result
                    ? <span>Dernier enrichissement : <span className="font-semibold text-ink">{job.result.matched}</span> adresse(s) trouvée(s) sur {job.result.total} traité(s){job.result.first_error ? ` — erreur : ${job.result.first_error}` : ""}.</span>
                    : <span>Aucun enrichissement lancé pour l'instant.</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MissingGpsPanel() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = () => api.get("/admin/gps/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (status?.job?.status !== "running") return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [status?.job?.status]); // eslint-disable-line

  const startBackfill = async () => {
    setStarting(true);
    try {
      await api.post("/admin/gps/backfill");
      toast.success("Complétion des GPS lancée en arrière-plan");
      setTimeout(load, 800);
    } catch (e) { toast.error(e?.response?.data?.detail || "Lancement impossible"); }
    finally { setStarting(false); }
  };

  if (!status) return <Loading />;
  const running = status.job?.status === "running";
  const job = status.job;
  const pct = job?.total ? Math.round((100 * (job.processed || 0)) / job.total) : 0;
  const missing = status.live_missing_gps ?? 0;
  const sample = status.sample || [];
  const stats = [
    { icon: ShieldCheck, label: "Spots avec GPS", value: status.live_with_gps, color: "#1F9D55", testid: "gps-stat-with" },
    { icon: AlertTriangle, label: "Spots actifs sans GPS", value: missing, color: missing > 0 ? "#9B2226" : "#1F9D55", testid: "gps-stat-missing", danger: missing > 0 },
    { icon: Database, label: "Total spots actifs", value: status.live_total, color: "#2F6FED", testid: "gps-stat-total" },
  ];
  return (
    <div data-testid="missing-gps-panel">
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--surface-2)]"><Crosshair className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold">Coordonnées GPS manquantes (pointage)</h3>
            <p className="mt-1 text-sm text-muted-ink">
              Complète les <span className="font-semibold text-ink">coordonnées GPS</span> des spots actifs qui ont une adresse mais aucune position, via Google Places.
              Un GPS déjà renseigné n'est <span className="font-semibold text-ink">jamais écrasé</span>. Indispensable au pointage géolocalisé.
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 @3xl/page:grid-cols-3">
          {stats.map((st) => (
            <div key={st.label} className="flex items-center gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid={st.testid}>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${st.color}18` }}><st.icon className="h-4 w-4" style={{ color: st.color }} /></div>
              <div><div className="text-lg font-extrabold leading-none" data-testid={st.testid === "gps-stat-missing" ? "gps-missing-count" : undefined} style={st.danger ? { color: "#9B2226" } : undefined}>{st.value ?? "—"}</div><div className="text-[11px] text-muted-ink">{st.label}</div></div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${status.configured ? "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]" : "bg-[rgba(214,69,69,0.1)] text-[#9B2226]"}`} data-testid="gps-key-state">
            {status.configured ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {status.configured ? "Clé configurée (serveur)" : "Clé absente"}
          </span>
          <Button onClick={startBackfill} disabled={starting || running || !status.configured || missing === 0}
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="gps-backfill-btn">
            {starting || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Crosshair className="mr-1.5 h-4 w-4" />Compléter les GPS manquants ({missing})</>}
          </Button>
          <Button variant="secondary" onClick={load} className="border border-[var(--border-soft)] bg-white" data-testid="gps-refresh-btn">
            <RefreshCw className="mr-1.5 h-4 w-4" />Actualiser
          </Button>
        </div>

        {job && (
          <div className="mt-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="gps-job-state">
            {running ? (
              <>
                <div className="flex items-center justify-between text-xs font-semibold">
                  <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--shariti-orange)]" />Complétion en cours…</span>
                  <span>{job.processed || 0}{job.total ? ` / ${job.total}` : ""} traités · {job.matched || 0} trouvées</span>
                </div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white">
                  <div className="h-full rounded-full bg-[var(--shariti-orange)] transition-[width] duration-300" style={{ width: `${pct}%` }} />
                </div>
              </>
            ) : (
              <div className="text-xs text-muted-ink">
                {job.status === "failed"
                  ? <span className="font-semibold text-[#9B2226]">Dernière complétion échouée : {job.error}</span>
                  : job.result
                    ? <span>Dernière complétion : <span className="font-semibold text-ink">{job.result.matched}</span> GPS ajouté(s) sur {job.result.total} traité(s){job.result.first_error ? ` — erreur : ${job.result.first_error}` : ""}.</span>
                    : <span>Aucune complétion lancée pour l'instant.</span>}
              </div>
            )}
          </div>
        )}

        {missing > 0 && (
          <div className="mt-4" data-testid="gps-missing-list">
            <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
              Spots concernés{sample.length < missing ? ` (${sample.length} premiers sur ${missing})` : ` (${missing})`}
            </div>
            <div className="max-h-64 space-y-1 overflow-y-auto rounded-xl border border-[var(--border-soft)] p-1">
              {sample.map((s) => (
                <button key={s.id} onClick={() => navigate(`/spots/${s.id}`)} data-testid={`gps-missing-row-${s.id}`}
                  className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors hover:bg-[var(--surface-2)]">
                  <span className="min-w-0">
                    <span className="block truncate font-semibold text-ink">{s.normalized_name || s.id}</span>
                    <span className="block truncate text-[11px] text-muted-ink">{s.address_full || s.ville || "—"}</span>
                  </span>
                  <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[var(--shariti-orange)]"><ExternalLink className="h-3 w-3" />Ouvrir</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function PhoneBackfillPanel() {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [includeHist, setIncludeHist] = useState(true);

  const load = () => api.get("/admin/phones/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (status?.job?.status !== "running") return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [status?.job?.status]); // eslint-disable-line

  const startBackfill = async () => {
    setStarting(true);
    try {
      const scope = includeHist ? "all" : "live";
      await api.post("/admin/phones/backfill", null, { params: { scope } });
      toast.success("Enrichissement des téléphones lancé en arrière-plan");
      setTimeout(load, 800);
    } catch (e) { toast.error(e?.response?.data?.detail || "Lancement impossible"); }
    finally { setStarting(false); }
  };

  if (!status) return <Loading />;
  const running = status.job?.status === "running";
  const job = status.job;
  const pct = job?.total ? Math.round((100 * (job.processed || 0)) / job.total) : 0;
  const missingTotal = (status.live_missing || 0) + (includeHist ? (status.hist_missing || 0) : 0);
  const stats = [
    { icon: Phone, label: "Spots actifs avec tél.", value: status.live_with_phone, color: "#1F9D55" },
    { icon: AlertTriangle, label: "Spots actifs sans tél.", value: status.live_missing, color: "#EE722E" },
    { icon: History, label: "Historiques avec tél.", value: status.hist_with_phone, color: "#2F6FED" },
    { icon: AlertTriangle, label: "Historiques sans tél.", value: status.hist_missing, color: "#8B5CF6" },
  ];
  return (
    <div data-testid="phone-backfill-panel">
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--surface-2)]"><Phone className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold">Enrichissement des téléphones (Google Places)</h3>
            <p className="mt-1 text-sm text-muted-ink">
              Complète automatiquement le <span className="font-semibold text-ink">numéro de téléphone</span> des spots qui n'en ont pas.
              Les téléphones déjà renseignés ne sont <span className="font-semibold text-ink">jamais écrasés</span>. Activez
              « Inclure l'historique » pour alimenter la <span className="font-semibold text-ink">liste d'appel</span> (spots historiques).
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 @3xl/page:grid-cols-4">
          {stats.map((st) => (
            <div key={st.label} className="flex items-center gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid={`phone-stat-${st.label}`}>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${st.color}18` }}><st.icon className="h-4 w-4" style={{ color: st.color }} /></div>
              <div><div className="text-lg font-extrabold leading-none">{st.value ?? "—"}</div><div className="text-[11px] text-muted-ink">{st.label}</div></div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${status.configured ? "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]" : "bg-[rgba(214,69,69,0.1)] text-[#9B2226]"}`} data-testid="phone-key-state">
            {status.configured ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {status.configured ? "Clé configurée (serveur)" : "Clé absente"}
          </span>
          <label className="flex h-10 items-center gap-2 rounded-lg border border-[var(--border-soft)] bg-white px-3 text-xs font-semibold">
            <Switch checked={includeHist} onCheckedChange={setIncludeHist} data-testid="phone-include-hist" /> Inclure l'historique
          </label>
          <Button onClick={startBackfill} disabled={starting || running || !status.configured || missingTotal === 0}
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="phone-backfill-btn">
            {starting || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Phone className="mr-1.5 h-4 w-4" />Enrichir les téléphones manquants ({missingTotal})</>}
          </Button>
        </div>

        {job && (
          <div className="mt-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="phone-job-state">
            {running ? (
              <>
                <div className="flex items-center justify-between text-xs font-semibold">
                  <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--shariti-orange)]" />Enrichissement en cours…{job.scope ? ` (${job.scope})` : ""}</span>
                  <span>{job.processed || 0}{job.total ? ` / ${job.total}` : ""} traités · {job.matched || 0} trouvés</span>
                </div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white">
                  <div className="h-full rounded-full bg-[var(--shariti-orange)] transition-[width] duration-300" style={{ width: `${pct}%` }} />
                </div>
              </>
            ) : (
              <div className="text-xs text-muted-ink">
                {job.status === "failed"
                  ? <span className="font-semibold text-[#9B2226]">Dernier enrichissement échoué : {job.error}</span>
                  : job.result
                    ? <span>Dernier enrichissement{job.scope ? ` (${job.scope})` : ""} : <span className="font-semibold text-ink">{job.result.matched}</span> téléphone(s) trouvé(s) sur {job.result.total} traité(s){job.result.first_error ? ` — erreur : ${job.result.first_error}` : ""}.</span>
                    : <span>Aucun enrichissement lancé pour l'instant.</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const REBOOK_STATUS = {
  au_crayon: { label: "Au crayon", color: "#8A6D1B", bg: "rgba(234,179,8,0.16)" },
  ferme: { label: "Fermé", color: "#0F5A2F", bg: "rgba(31,157,85,0.12)" },
  a_confirmer: { label: "À confirmer", color: "#B4531B", bg: "rgba(238,114,46,0.14)" },
  confirme: { label: "Confirmé", color: "#0F5A2F", bg: "rgba(31,157,85,0.12)" },
};
function rebookMonthLabel(m) {
  if (!m) return "Tout l'historique";
  try {
    const [y, mo] = m.split("-");
    return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  } catch { return m; }
}
function rebookDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(String(iso).length <= 10 ? iso + "T00:00:00" : iso);
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}
function KpiMini({ icon: Icon, label, value, sub, tone = "orange", testid }) {
  const tones = {
    orange: { bg: "rgba(238,114,46,0.12)", color: "#B4531B" },
    blue: { bg: "rgba(59,130,246,0.12)", color: "#1D4ED8" },
    green: { bg: "rgba(31,157,85,0.12)", color: "#0F5A2F" },
  };
  const t = tones[tone] || tones.orange;
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl" style={{ background: t.bg }}><Icon className="h-4 w-4" style={{ color: t.color }} /></span>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-ink">{label}</span>
      </div>
      <div className="mt-1.5 text-2xl font-extrabold leading-none">{value}</div>
      {sub && <div className="mt-1 truncate text-[11px] text-muted-ink" title={sub}>{sub}</div>}
    </div>
  );
}

// Suivi des rebookings TERRAIN (posés par les RD depuis leur espace), groupés par RD,
// prêt pour le calcul des primes. Filtre par mois + détail par créneau.
export function RebookingsPanel() {
  const [mois, setMois] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openRd, setOpenRd] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [hist, setHist] = useState(null);
  // Journal global (Phase 24) : recherche + filtre action + pagination
  const [view, setView] = useState("suivi");
  const [jq, setJq] = useState("");
  const [jaction, setJaction] = useState("all");
  const [jdata, setJdata] = useState(null);
  const [jloading, setJloading] = useState(false);
  const [jmore, setJmore] = useState(false);

  const loadJournal = async (offset = 0) => {
    const params = { limit: 30, offset };
    if (jq.trim()) params.q = jq.trim();
    if (jaction !== "all") params.action = jaction;
    offset === 0 ? setJloading(true) : setJmore(true);
    try {
      const r = await api.get("/rebookings/history/all", { params });
      setJdata((prev) => (offset === 0 ? r.data
        : { ...r.data, events: [...(prev?.events || []), ...(r.data.events || [])] }));
    } catch { toast.error("Chargement du journal impossible."); }
    finally { setJloading(false); setJmore(false); }
  };
  useEffect(() => {
    if (view !== "journal") return;
    const t = setTimeout(() => loadJournal(0), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, jq, jaction]);

  const load = async (m) => {
    setLoading(true);
    try {
      const r = await api.get("/rebookings/field", { params: m ? { mois: m } : {} });
      setData(r.data);
    } catch { toast.error("Chargement des rebookings impossible."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(mois); }, [mois]);

  const act = async (id, action, okMsg) => {
    setBusyId(id);
    try { await api.post(`/rebookings/${id}/${action}`); toast.success(okMsg); await load(mois); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action impossible."); }
    finally { setBusyId(null); }
  };
  const toggleCancel = (it) => act(it.ref_booking_id, it.cancelled ? "restore" : "cancel",
    it.cancelled ? "Emplacement réactivé." : "Emplacement annulé.");
  const validate = (it) => act(it.ref_booking_id, "validate", "Emplacement validé — prime débloquée.");
  const invalidate = (it) => act(it.ref_booking_id, "invalidate", "Emplacement invalidé — repassé « à valider ».");
  const removeDate = async (di) => {
    setBusyId(di.booking_id);
    try { await api.post(`/rebookings/${di.booking_id}/remove-date`); toast.success("Date supprimée."); await load(mois); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible."); }
    finally { setBusyId(null); }
  };

  // Réattribution (Phase 25) : transférer l'emplacement + sa prime à un autre collaborateur
  const [reassign, setReassign] = useState(null); // {item, rd, results, loading, busy}
  const openReassign = async (it, g) => {
    setReassign({ item: it, rd: g, results: [], loading: true, busy: false });
    try {
      const res = await api.get("/pointage/rds");
      setReassign((r) => (r ? { ...r, results: res.data || [], loading: false } : r));
    } catch { setReassign((r) => (r ? { ...r, loading: false } : r)); }
  };
  const doReassign = async (target) => {
    setReassign((r) => (r ? { ...r, busy: true } : r));
    try {
      await api.post(`/rebookings/${reassign.item.ref_booking_id}/reassign`, { rd_id: target.id });
      toast.success(`Emplacement réattribué à ${target.name} — prime transférée.`);
      setReassign(null);
      await load(mois);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réattribution impossible.");
      setReassign((r) => (r ? { ...r, busy: false } : r));
    }
  };

  // Suppression ADMIN (contrôle total) : retirer définitivement un emplacement (test/erreur)
  const [confirmDelete, setConfirmDelete] = useState(null); // {item, rd}
  const doDelete = async () => {
    if (!confirmDelete) return;
    const it = confirmDelete.item;
    setBusyId(it.ref_booking_id);
    try {
      await api.post(`/rebookings/${it.ref_booking_id}/delete-emplacement`);
      toast.success("Emplacement supprimé définitivement.");
      setConfirmDelete(null);
      await load(mois);
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible."); }
    finally { setBusyId(null); }
  };

  const openHist = async (it, g) => {
    setHist({ loading: true, item: it, rd: g, events: [] });
    try {
      const r = await api.get("/rebookings/history", { params: { spot_id: it.spot_id, rd_id: g.rd_id || "", rd_name: g.rd_name || "" } });
      setHist({ loading: false, item: it, rd: g, events: r.data?.events || [] });
    } catch { setHist({ loading: false, item: it, rd: g, events: [] }); }
  };
  const HIST_CFG = {
    created: { title: "Rebooking posé (RD)", color: "#EE722E" },
    validated: { title: "Validé", color: "#1F9D55" },
    invalidated: { title: "Invalidé", color: "#E9B800" },
    cancelled: { title: "Annulé", color: "#D64545" },
    restored: { title: "Réactivé", color: "#2563EB" },
    date_removed: { title: "Date supprimée", color: "#8A6D00" },
    reassigned: { title: "Réattribué (repris)", color: "#7C3AED" },
    reassigned_out: { title: "Réattribué (transféré)", color: "#9333EA" },
    deleted: { title: "Supprimé (RD)", color: "#B91C1C" },
  };
  const histDateTime = (iso) => {
    try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  if (loading && !data) return <Loading />;
  const groups = data?.groups || [];
  const per = data?.amount_per || 50;
  const months = data?.months || [];

  return (
    <div className="space-y-4" data-testid="rebookings-panel">
      <div>
        <h3 className="flex items-center gap-2 text-base font-bold"><RefreshCw className="h-5 w-5 text-[var(--shariti-orange)]" /> Primes rebooking terrain</h3>
        <p className="mt-0.5 text-xs text-muted-ink">{per} € par emplacement (une seule fois). La prime est <b>à verser</b> une fois la visite effectuée (1re date passée), sinon « à venir ». Vue par mois pour la paie.</p>
      </div>

      {/* Bascule Suivi (par recruteur) / Journal (historique global cherchable) */}
      <div className="inline-flex rounded-xl border border-[var(--border-soft)] bg-white p-0.5" data-testid="rebookings-view-toggle">
        <button onClick={() => setView("suivi")} data-testid="rebookings-view-suivi"
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${view === "suivi" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
          <Wallet className="h-3.5 w-3.5" /> Suivi
        </button>
        <button onClick={() => setView("journal")} data-testid="rebookings-view-journal"
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${view === "journal" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
          <History className="h-3.5 w-3.5" /> Journal
        </button>
      </div>

      {view === "suivi" && (<>
      {/* Segmentation par mois (compacte) */}
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1" data-testid="rebookings-months">
        <button onClick={() => setMois("")} data-testid="rebookings-month-all"
          className={`shrink-0 rounded-xl border px-3 py-2 text-left transition-colors ${!mois ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.08)]" : "border-[var(--border-soft)] bg-white hover:bg-[var(--surface-2)]"}`}>
          <div className="text-[11px] font-bold text-ink">Tout</div>
          <div className="text-[11px] text-muted-ink">historique</div>
        </button>
        {months.map((m) => {
          const on = mois === m.mois;
          return (
            <button key={m.mois} onClick={() => setMois(m.mois)} data-testid={`rebookings-month-${m.mois}`}
              className={`shrink-0 rounded-xl border px-3 py-2 text-left transition-colors ${on ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.08)]" : "border-[var(--border-soft)] bg-white hover:bg-[var(--surface-2)]"}`}>
              <div className="text-[11px] font-bold capitalize text-ink">{rebookMonthLabel(m.mois)}</div>
              <div className="flex items-center gap-1.5 text-[11px]">
                <span className="font-extrabold text-[#0F5A2F]">{m.due_amount} €</span>
                {m.upcoming_amount > 0 && <span className="text-[#1D4ED8]">+{m.upcoming_amount} à venir</span>}
                {m.pending_count > 0 && <span className="text-[#8A6D00]">· {m.pending_count} à valider</span>}
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiMini icon={Wallet} label={mois ? "À verser (ce mois)" : "À verser"} value={`${data?.total_due_amount ?? 0} €`} tone="green" testid="rebookings-kpi-due" />
        <KpiMini icon={CalendarDays} label="À venir" value={`${data?.total_upcoming_amount ?? 0} €`} tone="blue" testid="rebookings-kpi-upcoming" />
        <KpiMini icon={Clock} label="À valider" value={data?.pending_total ?? 0} tone="orange" testid="rebookings-kpi-pending" />
        <KpiMini icon={UserCog} label="RD" value={data?.rd_count ?? 0} sub={data?.top_rd?.name} tone="blue" testid="rebookings-kpi-rd" />
      </div>

      {(data?.pending_total || 0) > 0 && (
        <div className="flex items-start gap-2.5 rounded-2xl border border-[rgba(233,184,0,0.55)] bg-[rgba(233,184,0,0.1)] p-3" data-testid="rebookings-pending-banner">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#8A6D00]" />
          <p className="text-xs text-[#5A4200]"><span className="font-bold">{data.pending_total} emplacement{data.pending_total > 1 ? "s" : ""} à valider</span> — vérifiez la rotation puis <span className="font-semibold">Validez</span> (ou Invalidez à tout moment). Non validé = non compté.</p>
        </div>
      )}

      {groups.length === 0 ? (
        <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-14 text-center" data-testid="rebookings-empty">
          <RefreshCw className="h-7 w-7 text-muted-ink" />
          <p className="mt-2 text-sm font-bold">Aucun rebooking{mois ? " sur ce mois" : ""}</p>
          <p className="mt-1 max-w-xs text-xs text-muted-ink">Les emplacements rebookés par les RD apparaîtront ici, regroupés par recruteur et par mois.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {groups.map((g, i) => {
            const key = g.rd_id || g.rd_name || i;
            const open = openRd === key;
            const subParts = [];
            if (g.upcoming_amount > 0) subParts.push(`${g.upcoming_amount} € à venir`);
            if (g.pending_count > 0) subParts.push(`${g.pending_count} à valider`);
            if (g.cancelled_count > 0) subParts.push(`${g.cancelled_count} annulé${g.cancelled_count > 1 ? "s" : ""}`);
            return (
              <div key={key} className="overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white" data-testid={`rebookings-rd-${i}`}>
                <button className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-[var(--surface-2)]" onClick={() => setOpenRd(open ? null : key)} data-testid={`rebookings-rd-toggle-${i}`}>
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--shariti-orange)] text-xs font-bold text-white">{(g.rd_name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2"><span className="font-bold leading-tight break-words">{g.rd_name}</span>{g.code && <span className="shrink-0 rounded bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-semibold text-muted-ink">{g.code}</span>}</div>
                    <div className="text-[11px] text-muted-ink">{g.due_count} à verser{subParts.length ? ` · ${subParts.join(" · ")}` : ""}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="rounded-full bg-[rgba(31,157,85,0.12)] px-2.5 py-1 text-sm font-extrabold text-[#0F5A2F]">{g.due_amount} €</div>
                    <div className="mt-0.5 text-[10px] text-muted-ink">à verser</div>
                  </div>
                  <ChevronDown className={`h-4 w-4 shrink-0 text-muted-ink transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="border-t border-[var(--border-soft)] bg-[var(--surface-2)]/40">
                    {g.items.map((it) => {
                      const id = it.ref_booking_id || it.booking_ids?.[0];
                      const busy = busyId === id;
                      const badge = it.cancelled
                        ? { txt: "Annulé", cls: "bg-[var(--surface-2)] text-muted-ink", Icon: X }
                        : it.pending ? { txt: "À valider", cls: "bg-[rgba(233,184,0,0.18)] text-[#8A6D00]", Icon: Clock }
                        : it.due ? { txt: "À verser", cls: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]", Icon: CheckCircle2 }
                        : { txt: "À venir", cls: "bg-[rgba(37,99,235,0.12)] text-[#1D4ED8]", Icon: CalendarDays };
                      const euroCls = it.cancelled ? "text-muted-ink line-through" : it.pending ? "text-[#8A6D00]" : it.due ? "text-[#0F5A2F]" : "text-[#1D4ED8]";
                      const euroTxt = it.cancelled ? "0 €" : it.pending ? `(${per} €)` : `${per} €`;
                      return (
                        <div key={it.spot_id || id} className={`border-b border-[var(--border-soft)] px-3 py-2.5 last:border-0 ${it.cancelled ? "opacity-60" : ""}`} data-testid={`rebookings-item-${it.spot_id || id}`}>
                          <div className="flex items-start gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className={`text-[13px] font-semibold leading-tight break-words ${it.cancelled ? "line-through" : ""}`}>{it.spot_name}</span>
                                {it.association_acronym && <span className="shrink-0 rounded px-1 py-0.5 text-[9px] font-bold text-white" style={{ background: it.association_color || "#888" }}>{it.association_acronym}</span>}
                                <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${badge.cls}`}><badge.Icon className="h-3 w-3" />{badge.txt}</span>
                              </div>
                              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-ink">
                                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" />{[it.ville, it.dept ? `(${it.dept})` : null].filter(Boolean).join(" ") || "—"}</span>
                                <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3 shrink-0" />{it.nb_dates} date{it.nb_dates > 1 ? "s" : ""}</span>
                                {it.forced && <span className="inline-flex items-center gap-1 text-[#B4531B]"><AlertTriangle className="h-3 w-3" />Hors rotation</span>}
                              </div>
                            </div>
                            <span className={`shrink-0 text-[13px] font-extrabold ${euroCls}`}>{euroTxt}</span>
                          </div>
                          {(it.date_items || []).length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {it.date_items.map((di) => (
                                <span key={di.booking_id} className="inline-flex items-center gap-1 rounded-md bg-white px-1.5 py-0.5 text-[10px] font-semibold capitalize text-muted-ink ring-1 ring-[var(--border-soft)]">
                                  {rebookDate(di.date)}
                                  {!it.cancelled && (
                                    <button type="button" onClick={() => removeDate(di)} disabled={busyId === di.booking_id}
                                      className="grid h-3.5 w-3.5 place-items-center rounded-full text-muted-ink transition-colors hover:bg-[rgba(214,69,69,0.14)] hover:text-[#D64545] disabled:opacity-50"
                                      data-testid={`rebookings-remove-date-${di.booking_id}`} title="Supprimer cette date">
                                      {busyId === di.booking_id ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <X className="h-2.5 w-2.5" />}
                                    </button>
                                  )}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5">
                            <Button size="sm" variant="ghost" onClick={() => openHist(it, g)} data-testid={`rebookings-history-${it.spot_id || id}`}
                              className="mr-auto h-8 gap-1 px-2.5 text-[11px] text-muted-ink hover:bg-[var(--surface-2)]">
                              <History className="h-3.5 w-3.5" />Historique
                            </Button>
                            {!it.cancelled && (
                              <Button size="sm" variant="outline" disabled={busy} onClick={() => openReassign(it, g)} data-testid={`rebookings-reassign-${it.spot_id || id}`}
                                className="h-8 gap-1 border-[rgba(124,58,237,0.5)] px-2.5 text-[11px] text-[#6D28D9] hover:bg-[rgba(124,58,237,0.08)]">
                                <UserCog className="h-3.5 w-3.5" />Réattribuer
                              </Button>
                            )}
                            {it.pending && !it.cancelled && (
                              <Button size="sm" disabled={busy} onClick={() => validate(it)} data-testid={`rebookings-validate-${it.spot_id || id}`}
                                className="h-8 gap-1 bg-[#1F9D55] px-2.5 text-[11px] text-white hover:bg-[#178047]">
                                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ShieldCheck className="h-3.5 w-3.5" />Valider</>}
                              </Button>
                            )}
                            {!it.pending && !it.cancelled && (
                              <Button size="sm" variant="outline" disabled={busy} onClick={() => invalidate(it)} data-testid={`rebookings-invalidate-${it.spot_id || id}`}
                                className="h-8 gap-1 border-[rgba(233,184,0,0.6)] px-2.5 text-[11px] text-[#8A6D00] hover:bg-[rgba(233,184,0,0.1)]">
                                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RotateCcw className="h-3.5 w-3.5" />Invalider</>}
                              </Button>
                            )}
                            <Button size="sm" variant={it.cancelled ? "outline" : "ghost"} disabled={busy}
                              onClick={() => toggleCancel(it)} data-testid={`rebookings-toggle-${it.spot_id || id}`}
                              className={`h-8 gap-1 px-2.5 text-[11px] ${it.cancelled ? "" : "text-[#D64545] hover:bg-[rgba(214,69,69,0.08)]"}`}>
                              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : it.cancelled ? <><RotateCcw className="h-3.5 w-3.5" />Réactiver</> : <><X className="h-3.5 w-3.5" />Annuler</>}
                            </Button>
                            <Button size="sm" variant="ghost" disabled={busy}
                              onClick={() => setConfirmDelete({ item: it, rd: g })} data-testid={`rebookings-delete-${it.spot_id || id}`}
                              className="h-8 gap-1 px-2.5 text-[11px] text-[#9B2226] hover:bg-[rgba(155,34,38,0.1)]">
                              <Trash2 className="h-3.5 w-3.5" />Supprimer
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>)}

      {view === "journal" && (
        <div className="space-y-3" data-testid="rebookings-journal">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
              <Input value={jq} onChange={(e) => setJq(e.target.value)} data-testid="rebookings-journal-search"
                placeholder="Rechercher par recruteur ou magasin…" className="h-9 pl-8 text-sm" />
            </div>
            <Select value={jaction} onValueChange={setJaction}>
              <SelectTrigger className="h-9 w-full text-sm sm:w-52" data-testid="rebookings-journal-action">
                <SelectValue placeholder="Toutes les actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les actions</SelectItem>
                {Object.entries(HIST_CFG).map(([k, cfg]) => (
                  <SelectItem key={k} value={k}>{cfg.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {jloading ? (
            <div className="grid place-items-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[var(--shariti-orange)]" /></div>
          ) : (jdata?.events || []).length === 0 ? (
            <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-14 text-center" data-testid="rebookings-journal-empty">
              <History className="h-7 w-7 text-muted-ink" />
              <p className="mt-2 text-sm font-bold">Aucun événement</p>
              <p className="mt-1 max-w-xs text-xs text-muted-ink">Le journal se remplit à chaque action sur une prime (pose, validation, annulation, réattribution…).</p>
            </div>
          ) : (
            <>
              <p className="text-[11px] font-semibold text-muted-ink" data-testid="rebookings-journal-count">
                {jdata.total} événement{jdata.total > 1 ? "s" : ""}{jq.trim() || jaction !== "all" ? " (filtré)" : ""}
              </p>
              <ol className="relative ml-1 space-y-3 border-l border-[var(--border-soft)] py-1 pl-4">
                {jdata.events.map((e) => {
                  const cfg = HIST_CFG[e.action] || { title: e.label, color: "#6B7280" };
                  return (
                    <li key={e.id} className="relative" data-testid={`rebookings-journal-ev-${e.id}`}>
                      <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ background: cfg.color }} />
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-[13px] font-bold leading-tight text-ink break-words">{e.spot_name || "Emplacement"}</div>
                          <div className="text-[11px] font-semibold text-muted-ink break-words">{e.rd_name || "—"}</div>
                        </div>
                        <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: cfg.color }}>{cfg.title}</span>
                      </div>
                      {e.label && <div className="mt-0.5 text-[11px] text-muted-ink break-words">{e.label}</div>}
                      {/* Confirmation d'envoi : statut de l'email + accusé « reçu par le directeur ». */}
                      {(e.email_status !== undefined || e.ack_token) && (
                        <div className="mt-1 flex flex-col gap-1" data-testid={`rebookings-journal-mail-${e.id}`}>
                          {e.email_status === "sent" ? (
                            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-[rgba(31,157,85,0.12)] px-2 py-0.5 text-[10px] font-bold text-[#0F5A2F]">
                              <Mail className="h-3 w-3" /> Envoyé{e.recipient ? ` à ${e.recipient}` : ""}
                            </span>
                          ) : e.email_status === "failed" ? (
                            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-[rgba(200,44,44,0.12)] px-2 py-0.5 text-[10px] font-bold text-[#B4231C]">
                              <Mail className="h-3 w-3" /> Échec d'envoi{e.recipient ? ` (${e.recipient})` : ""}
                            </span>
                          ) : (e.email_status === "skipped_no_email" || (e.email_status && String(e.email_status).startsWith("skipped"))) ? (
                            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-[rgba(233,184,0,0.18)] px-2 py-0.5 text-[10px] font-bold text-[#8A6D00]">
                              <Mail className="h-3 w-3" /> Aucune adresse — non envoyé
                            </span>
                          ) : e.email_status === "not_sent" ? (
                            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[10px] font-bold text-muted-ink">
                              <Mail className="h-3 w-3" /> Email non envoyé
                            </span>
                          ) : null}
                          {e.director_confirmed ? (
                            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-[rgba(31,157,85,0.12)] px-2 py-0.5 text-[10px] font-bold text-[#0F5A2F]">
                              <CheckCircle2 className="h-3 w-3" /> Reçu par le directeur{e.director_confirmed_at ? ` le ${histDateTime(e.director_confirmed_at)}` : ""}
                            </span>
                          ) : (e.ack_token && e.email_status === "sent") ? (
                            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-[rgba(233,184,0,0.18)] px-2 py-0.5 text-[10px] font-bold text-[#8A6D00]">
                              <Clock className="h-3 w-3" /> En attente de confirmation du directeur
                            </span>
                          ) : null}
                        </div>
                      )}
                      <div className="mt-0.5 text-[10px] text-muted-ink">{histDateTime(e.created_at)} · {e.actor || "—"} {e.actor_role === "rd" ? "(RD)" : e.actor_role === "responsable" ? "(responsable)" : "(admin)"}</div>
                    </li>
                  );
                })}
              </ol>
              {jdata.has_more && (
                <div className="flex justify-center">
                  <Button size="sm" variant="outline" disabled={jmore} onClick={() => loadJournal(jdata.events.length)}
                    data-testid="rebookings-journal-more" className="h-8 gap-1 text-xs">
                    {jmore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronDown className="h-3.5 w-3.5" />} Charger plus
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <Dialog open={!!hist} onOpenChange={(o) => !o && setHist(null)}>
        <DialogContent className="max-w-md" data-testid="rebookings-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4 text-[var(--shariti-orange)]" />Historique de l'emplacement</DialogTitle>
            <DialogDescription className="break-words">{hist?.item?.spot_name}{hist?.rd?.rd_name ? ` · ${hist.rd.rd_name}` : ""} — traçabilité pour justifier la prime.</DialogDescription>
          </DialogHeader>
          {hist?.loading ? (
            <div className="grid place-items-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[var(--shariti-orange)]" /></div>
          ) : (hist?.events || []).length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-ink">Aucun événement enregistré pour cet emplacement (l'historique se remplit à partir des prochaines actions).</p>
          ) : (
            <ol className="relative ml-1 space-y-3 border-l border-[var(--border-soft)] py-1 pl-4">
              {hist.events.map((e) => {
                const cfg = HIST_CFG[e.action] || { title: e.label, color: "#6B7280" };
                return (
                  <li key={e.id} className="relative" data-testid={`rebookings-hist-ev-${e.id}`}>
                    <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ background: cfg.color }} />
                    <div className="text-[13px] font-bold text-ink">{cfg.title}</div>
                    <div className="text-[11px] text-muted-ink">{e.label}</div>
                    <div className="mt-0.5 text-[10px] text-muted-ink">{histDateTime(e.created_at)} · {e.actor || "—"} {e.actor_role === "rd" ? "(RD)" : "(admin)"}</div>
                  </li>
                );
              })}
            </ol>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!reassign} onOpenChange={(o) => { if (!o && !reassign?.busy) setReassign(null); }}>
        <DialogContent className="max-w-md" data-testid="rebookings-reassign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><UserCog className="h-4 w-4 text-[#6D28D9]" />Réattribuer l'emplacement</DialogTitle>
            <DialogDescription className="break-words">
              {reassign?.item?.spot_name} — actuellement <b>{reassign?.rd?.rd_name}</b>. Choisissez le nouveau collaborateur : la prime ({per} €) et le statut le suivront.
            </DialogDescription>
          </DialogHeader>
          <Command className="rounded-xl border border-[var(--border-soft)]">
            <CommandInput placeholder="Rechercher un recruteur (nom ou matricule)…" data-testid="rebookings-reassign-search" />
            <CommandList className="max-h-64">
              <CommandEmpty>{reassign?.loading ? "Chargement des collaborateurs…" : "Aucun recruteur trouvé."}</CommandEmpty>
              <CommandGroup>
                {(reassign?.results || []).filter((u) => u.id !== reassign?.rd?.rd_id).map((u) => (
                  <CommandItem key={u.id} value={`${u.name} ${u.code || ""}`} disabled={reassign?.busy}
                    onSelect={() => doReassign(u)} data-testid={`rebookings-reassign-opt-${u.id}`}
                    className="flex items-center gap-2.5">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[rgba(124,58,237,0.12)] text-[11px] font-bold text-[#6D28D9]">
                      {(u.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-bold text-ink">{u.name}</div>
                      {u.code && <div className="text-[11px] text-muted-ink">{u.code}</div>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          {reassign?.busy && (
            <div className="flex items-center justify-center gap-2 text-[13px] font-semibold text-muted-ink" data-testid="rebookings-reassign-busy">
              <Loader2 className="h-4 w-4 animate-spin text-[#6D28D9]" /> Réattribution en cours…
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o && !(busyId && confirmDelete && busyId === confirmDelete.item.ref_booking_id)) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="rebookings-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-[#9B2226]" /> Supprimer définitivement cet emplacement ?
            </AlertDialogTitle>
            <AlertDialogDescription className="break-words">
              « {confirmDelete?.item?.spot_name} » — {confirmDelete?.rd?.rd_name}
              {confirmDelete?.item ? ` · ${confirmDelete.item.nb_dates} date${confirmDelete.item.nb_dates > 1 ? "s" : ""}` : ""}.
              {confirmDelete?.item && !confirmDelete.item.cancelled
                ? " ⚠️ Ce bonus est ENCORE ACTIF : sa prime sera retirée."
                : ""} Toutes les dates seront supprimées. Action irréversible (tracée dans le Journal).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rebookings-delete-cancel"
              disabled={!!(busyId && confirmDelete && busyId === confirmDelete.item.ref_booking_id)}>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="rebookings-delete-ok"
              disabled={!!(busyId && confirmDelete && busyId === confirmDelete.item.ref_booking_id)}
              onClick={(e) => { e.preventDefault(); doDelete(); }}
              className="bg-[#9B2226] text-white hover:bg-[#83191d]">
              {(busyId && confirmDelete && busyId === confirmDelete.item.ref_booking_id)
                ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const ADMIN_SECTIONS = [
  {
    id: "equipes", label: "Équipes & Zones", icon: Users2,
    desc: "Zones (territoires), CDP, chefs d'équipe, équipes et recruteurs.",
    tabs: [
      { value: "zones", label: "Zones", icon: Map, Panel: ZonesManager },
      { value: "users", label: "Utilisateurs", icon: Users2, Panel: UsersPanel },
      { value: "chefs", label: "Chefs d'équipe", icon: UserRound, Panel: ChefsPanel },
      { value: "equipes", label: "Équipes", icon: Users, Panel: EquipesPanel },
      { value: "rds", label: "RD", icon: UserCog, Panel: RdPanel },
      { value: "rebookings", label: "Rebookings terrain", icon: RefreshCw, Panel: RebookingsPanel },
      { value: "diagnostic", label: "Diagnostic", icon: Activity, Panel: DiagnosticPanel },
    ],
  },
  {
    id: "referentiels", label: "Référentiels & Données", icon: Building2,
    desc: "Associations, mandats, périmètres, codes campagne, lignes et données.",
    tabs: [
      { value: "assoc", label: "Associations", icon: Building2, Panel: AssocPanel },
      { value: "mandats", label: "Mandats", icon: FileCheck2, Panel: MandatsPanel },
      { value: "perimetres", label: "Périmètres", icon: Compass, Panel: PerimetresPanel },
      { value: "codes", label: "Codes campagne", icon: Barcode, Panel: CodesPanel },
      { value: "lignes", label: "Lignes", icon: GitBranch, Panel: LignesPanel },
      { value: "data", label: "Données", icon: Database, Panel: DataPanel },
    ],
  },
  {
    id: "config", label: "Configuration", icon: SlidersHorizontal,
    desc: "Paramètres métier, configuration de l'envoi d'email et journal d'activité.",
    tabs: [
      { value: "settings", label: "Paramètres", icon: SlidersHorizontal, Panel: SettingsPanel },
      { value: "smtp", label: "Envoi email", icon: Mail, Panel: SmtpPanel },
      { value: "telephonie", label: "Téléphonie", icon: Phone, Panel: TelephoniePanel },
      { value: "sauvegardes", label: "Sauvegardes", icon: DatabaseBackup, Panel: BackupsPanel },
      { value: "connexions", label: "Connexions", icon: Activity, Panel: ConnexionsPanel },
      { value: "journal", label: "Journal", icon: ScrollText, Panel: JournalPanel },
    ],
  },
];

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} o`;
  if (n < 1_048_576) return `${(n / 1024).toFixed(1)} Ko`;
  if (n < 1_073_741_824) return `${(n / 1_048_576).toFixed(1)} Mo`;
  return `${(n / 1_073_741_824).toFixed(2)} Go`;
}
function fmtFullDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      timeZone: "Europe/Paris",
    });
  } catch { return "—"; }
}

const BACKUP_STATUS = {
  success: { label: "Réussie", color: "#1F9D55", bg: "rgba(31,157,85,0.12)", Icon: CheckCircle2 },
  running: { label: "En cours", color: "#B4531B", bg: "rgba(238,114,46,0.12)", Icon: Loader2 },
  failed: { label: "Échouée", color: "#9B2226", bg: "rgba(214,69,69,0.12)", Icon: AlertTriangle },
};

function BackupsPanel() {
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState(null);
  const [running, setRunning] = useState(false);
  const [retention, setRetention] = useState("");
  const [savingRet, setSavingRet] = useState(false);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/backups/status"),
        api.get("/admin/backups?limit=30"),
      ]);
      setStatus(s.data); setRows(l.data);
      setRetention((prev) => (prev === "" ? String(s.data.retention_days ?? 7) : prev));
    } catch { /* silencieux */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Polling tant qu'une sauvegarde tourne
  useEffect(() => {
    if (!status?.running) return;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [status?.running]); // eslint-disable-line

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/admin/backups/run");
      toast.success("Sauvegarde lancée — elle apparaîtra dans quelques secondes.");
      setTimeout(load, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lancement impossible");
    } finally { setRunning(false); }
  };

  const saveRetention = async () => {
    const days = Number(retention);
    if (!Number.isInteger(days) || days < 1 || days > 365) {
      toast.error("La rétention doit être un entier entre 1 et 365 jours.");
      return;
    }
    setSavingRet(true);
    try {
      await api.put("/admin/backups/retention", { retention_days: days });
      toast.success(`Rétention réglée sur ${days} jour(s).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally { setSavingRet(false); }
  };

  if (!status) return <Loading />;

  const last = status.last_success;
  const stale = status.stale;
  const busy = status.running || running;

  return (
    <div className="space-y-4" data-testid="backups-panel">
      {/* Présentation */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--surface-2)]">
            <DatabaseBackup className="h-5 w-5 text-[var(--shariti-orange)]" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold">Sauvegarde automatique de la base</h3>
            <p className="mt-1 text-sm text-muted-ink">
              Un <span className="font-semibold text-ink">dump complet</span> de la base (données + fichiers :
              logos et mandats) est réalisé <span className="font-semibold text-ink">chaque nuit à 03h00 (heure de Paris)</span> et
              stocké sur un espace <span className="font-semibold text-ink">externe et persistant</span> (Object Storage),
              indépendant du serveur. Les enregistrements d'appels résident chez Twilio et ne sont pas concernés.
              La procédure de restauration est documentée dans <span className="font-mono text-[12px]">RESTORE.md</span>.
            </p>
          </div>
        </div>

        {/* Badges d'état */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${status.scheduler_active ? "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]" : "bg-[rgba(214,69,69,0.1)] text-[#9B2226]"}`} data-testid="backup-scheduler-state">
            {status.scheduler_active ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {status.scheduler_active ? "Planificateur actif" : "Planificateur arrêté"}
          </span>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${status.configured ? "bg-[rgba(47,111,237,0.12)] text-[#1D4ED8]" : "bg-[rgba(214,69,69,0.1)] text-[#9B2226]"}`} data-testid="backup-storage-state">
            <HardDrive className="h-3.5 w-3.5" />
            {status.configured ? "Stockage configuré" : "Stockage indisponible"}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--surface-2)] px-2.5 py-1 text-[11px] font-semibold text-muted-ink" data-testid="backup-next-run">
            <Clock className="h-3.5 w-3.5" /> Prochaine : {fmtFullDateTime(status.next_run)}
          </span>
        </div>
      </div>

      {/* Dernière sauvegarde + action */}
      <div className={`rounded-2xl border p-5 ${stale ? "border-[rgba(214,69,69,0.35)] bg-[rgba(214,69,69,0.04)]" : "border-[var(--border-soft)] bg-white"}`} data-testid="backup-last-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {stale
                ? <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[rgba(214,69,69,0.12)]"><ShieldAlert className="h-4 w-4 text-[#9B2226]" /></span>
                : <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[rgba(31,157,85,0.12)]"><ShieldCheck className="h-4 w-4 text-[#1F9D55]" /></span>}
              <h3 className="text-sm font-bold">Dernière sauvegarde réussie</h3>
            </div>
            {last ? (
              <div className="mt-2 space-y-1 text-sm">
                <div data-testid="backup-last-date"><span className="text-muted-ink">Date : </span><span className="font-semibold">{fmtFullDateTime(last.created_at)}</span></div>
                <div><span className="text-muted-ink">Taille : </span><span className="font-semibold" data-testid="backup-last-size">{fmtBytes(last.size_bytes)}</span>{last.duration_sec != null && <span className="text-muted-ink"> · {last.duration_sec}s</span>}</div>
                {stale && (
                  <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1 text-xs font-bold text-[#9B2226]" data-testid="backup-stale-warning">
                    <AlertTriangle className="h-3.5 w-3.5" /> Sauvegarde périmée (plus de 24 h) — vérifiez le planificateur.
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-2 text-sm text-[#9B2226] font-semibold" data-testid="backup-none">
                Aucune sauvegarde réussie pour l'instant. Lancez-en une pour initialiser le filet de sécurité.
              </p>
            )}
          </div>
          <Button onClick={runNow} disabled={busy || !status.configured}
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="backup-run-btn">
            {busy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Sauvegarde en cours…</> : <><DatabaseBackup className="mr-1.5 h-4 w-4" /> Lancer une sauvegarde maintenant</>}
          </Button>
        </div>
      </div>

      {/* Rétention */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <h3 className="text-sm font-bold">Rétention</h3>
        <p className="mt-1 text-xs text-muted-ink">
          Nombre de jours de sauvegardes conservées et restaurables (glissant). Les archives plus anciennes ne sont plus proposées à la restauration.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[11px]">Jours conservés</Label>
            <Input type="number" min={1} max={365} value={retention}
              onChange={(e) => setRetention(e.target.value)}
              className="mt-1 h-9 w-32" data-testid="backup-retention-input" />
          </div>
          <Button variant="secondary" onClick={saveRetention} disabled={savingRet}
            className="border border-[var(--border-soft)] bg-white" data-testid="backup-retention-save">
            {savingRet ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Enregistrer</>}
          </Button>
          <span className="pb-2 text-xs text-muted-ink" data-testid="backup-retained-count">
            {status.retained_count} sauvegarde(s) actuellement conservée(s).
          </span>
        </div>
      </div>

      {/* Historique */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold">Historique des sauvegardes</h3>
          <button onClick={load} className="inline-flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-[11px] font-semibold text-muted-ink transition-colors hover:border-[var(--shariti-orange)] hover:text-[var(--shariti-orange)]" data-testid="backup-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Rafraîchir
          </button>
        </div>
        {!rows ? <Loading /> : rows.length === 0 ? (
          <div className="mt-4 rounded-xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] px-4 py-6 text-center text-xs text-muted-ink" data-testid="backup-history-empty">
            Aucune sauvegarde enregistrée pour l'instant.
          </div>
        ) : (
          <div className="mt-3 overflow-hidden rounded-xl border border-[var(--border-soft)]">
            <table className="w-full text-sm" data-testid="backup-history-table">
              <thead>
                <tr className="bg-[var(--surface-2)] text-left text-[11px] uppercase tracking-wide text-muted-ink">
                  <th className="px-3 py-2 font-bold">Date</th>
                  <th className="px-3 py-2 font-bold">Statut</th>
                  <th className="px-3 py-2 font-bold">Taille</th>
                  <th className="px-3 py-2 font-bold">Type</th>
                  <th className="px-3 py-2 font-bold">Conservée</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const st = BACKUP_STATUS[r.status] || BACKUP_STATUS.failed;
                  return (
                    <tr key={r.id} className="border-t border-[var(--border-soft)]" data-testid={`backup-row-${r.id}`}>
                      <td className="px-3 py-2 font-semibold">{fmtFullDateTime(r.created_at)}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ color: st.color, background: st.bg }}>
                          <st.Icon className={`h-3 w-3 ${r.status === "running" ? "animate-spin" : ""}`} /> {st.label}
                        </span>
                        {r.status === "failed" && r.error && <div className="mt-1 max-w-[280px] truncate text-[11px] text-[#9B2226]" title={r.error}>{r.error}</div>}
                      </td>
                      <td className="px-3 py-2">{r.status === "success" ? fmtBytes(r.size_bytes) : "—"}</td>
                      <td className="px-3 py-2 text-muted-ink">{r.trigger === "manual" ? "Manuelle" : "Automatique"}</td>
                      <td className="px-3 py-2">{r.status === "success" ? (r.retained ? <span className="text-[#0F5A2F]">Oui</span> : <span className="text-muted-ink">Archivée</span>) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const set = (k) => (e) => setS((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      GROUPS.forEach((g) => g.fields.forEach(([k]) => { payload[k] = Number(s[k]); }));
      await api.put("/admin/settings", payload);
      toast.success("Paramètres enregistrés");
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  if (!s) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 @3xl/page:grid-cols-2">
        {GROUPS.map((g) => (
          <div key={g.title} className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
            <h3 className="mb-3 text-sm font-bold">{g.title}</h3>
            <div className="grid grid-cols-2 gap-3">
              {g.fields.map(([k, label]) => (
                <div key={k}><Label className="text-[11px]">{label}</Label>
                  <Input type="number" step="0.01" value={s[k] ?? ""} onChange={set(k)} className="mt-1 h-9" data-testid={`setting-${k}`} /></div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <BillingRefCard />
      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[var(--shariti-orange)] text-white shadow-[var(--shadow-md)] hover:bg-[#E56622]" data-testid="settings-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Enregistrer les paramètres</>}
        </Button>
      </div>
    </div>
  );
}

function BillingRefCard() {
  const [value, setValue] = useState("");
  const [initial, setInitial] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/performance/cdp-billing/settings")
      .then((r) => {
        const v = String(r.data?.reference_value_per_enseigne ?? "");
        setValue(v); setInitial(v);
      })
      .catch(() => { setValue(""); setInitial(""); })
      .finally(() => setLoading(false));
  }, []);
  const save = async () => {
    const num = Number(value);
    if (!(num >= 0)) { toast.error("Valeur de référence invalide"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/performance/cdp-billing/settings", { reference_value_per_enseigne: num });
      const v = String(data.reference_value_per_enseigne);
      setValue(v); setInitial(v);
      toast.success("Valeur de référence enregistrée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5" data-testid="billing-ref-card">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--surface-2)]"><Wallet className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
        <div className="min-w-0">
          <h3 className="text-sm font-bold">Facturation — valeur de référence</h3>
          <p className="mt-0.5 text-xs text-muted-ink">
            Valeur attribuée à chaque « enseigne trouvée » pour calculer la rentabilité (marge) des CDP.
            Modifiable également depuis <span className="font-semibold text-ink">Suivi CDP → Facturation</span>.
          </p>
        </div>
      </div>
      {loading ? (
        <div className="mt-4"><Loader2 className="h-4 w-4 animate-spin text-[var(--shariti-orange)]" /></div>
      ) : (
        <div className="mt-4 flex items-end gap-3">
          <div>
            <Label className="text-[11px]">Valeur de référence / enseigne (€)</Label>
            <Input type="number" step="0.01" min="0" value={value} onChange={(e) => setValue(e.target.value)} className="mt-1 h-9 w-48" data-testid="billing-ref-input" />
          </div>
          <Button onClick={save} disabled={saving || value === initial} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="billing-ref-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Enregistrer</>}
          </Button>
        </div>
      )}
    </div>
  );
}

function ListCard({ children }) {
  return <div className="overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white">{children}</div>;
}
function Loading() { return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[var(--shariti-orange)]" /></div>; }

const ROLE_LABEL = { admin: "Admin", cdp: "CDP", chef: "Chef d'équipe", prospecteur: "Prospecteur terrain", superviseur: "Superviseur" };
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}
function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

// --- Temps de travail par période (suivi des heures des CDP) ----------------
const PERIOD_MODES = [
  { v: "jour", l: "Jour" },
  { v: "semaine", l: "Semaine" },
  { v: "mois", l: "Mois" },
  { v: "annee", l: "Année" },
  { v: "perso", l: "Période" },
];
const ROLE_TT = { admin: "Admin", cdp: "CDP", chef: "Chef d'équipe", prospecteur: "Prospecteur terrain", superviseur: "Superviseur" };

function ttRange(mode, anchor, range) {
  if (mode === "semaine") return { start: startOfWeek(anchor, { weekStartsOn: 1 }), end: endOfWeek(anchor, { weekStartsOn: 1 }) };
  if (mode === "mois") return { start: startOfMonth(anchor), end: endOfMonth(anchor) };
  if (mode === "annee") return { start: startOfYear(anchor), end: endOfYear(anchor) };
  if (mode === "perso") { const f = range?.from || anchor; return { start: f, end: range?.to || f }; }
  return { start: anchor, end: anchor }; // jour
}
function ttLabel(mode, anchor, range) {
  if (mode === "semaine") {
    const s = startOfWeek(anchor, { weekStartsOn: 1 }); const e = endOfWeek(anchor, { weekStartsOn: 1 });
    return `${fmtDate(s, "d MMM", { locale: frLocale })} – ${fmtDate(e, "d MMM yyyy", { locale: frLocale })}`;
  }
  if (mode === "mois") return fmtDate(anchor, "MMMM yyyy", { locale: frLocale });
  if (mode === "annee") return fmtDate(anchor, "yyyy", { locale: frLocale });
  if (mode === "perso") {
    if (range?.from && range?.to) return `${fmtDate(range.from, "d MMM", { locale: frLocale })} – ${fmtDate(range.to, "d MMM yyyy", { locale: frLocale })}`;
    if (range?.from) return fmtDate(range.from, "d MMM yyyy", { locale: frLocale });
    return "Choisir une période…";
  }
  return fmtDate(anchor, "EEEE d MMMM yyyy", { locale: frLocale }); // jour
}
const ttIso = (d) => fmtDate(d, "yyyy-MM-dd");
const ttEur = (n) => (n == null ? "—" : Number(n).toLocaleString("fr-FR", { style: "currency", currency: "EUR" }));

function TempsTravailPanel() {
  const [mode, setMode] = useState("mois");
  const [anchor, setAnchor] = useState(new Date());
  const [range, setRange] = useState(undefined);   // {from,to} pour le mode « Période »
  const [scope, setScope] = useState("cdp");        // cdp | all
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calOpen, setCalOpen] = useState(false);

  const { start, end } = ttRange(mode, anchor, range);
  const canQuery = mode !== "perso" || (range?.from && range?.to);

  useEffect(() => {
    let cancel = false;
    const run = async () => {
      if (!canQuery) { setData(null); return; }
      setLoading(true);
      try {
        const res = await api.get("/admin/activity/temps-travail", { params: { start: ttIso(start), end: ttIso(end), scope } });
        if (!cancel) setData(res.data);
      } catch { if (!cancel) setData(null); }
      finally { if (!cancel) setLoading(false); }
    };
    run();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, anchor, range, scope]);

  const shift = (dir) => {
    if (mode === "jour") setAnchor((a) => addDays(a, dir));
    else if (mode === "semaine") setAnchor((a) => addWeeks(a, dir));
    else if (mode === "mois") setAnchor((a) => addMonths(a, dir));
    else if (mode === "annee") setAnchor((a) => addYears(a, dir));
  };
  const rows = data?.rows || [];

  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5" data-testid="temps-travail-panel">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--surface-2)]"><Wallet className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
        <div>
          <h3 className="text-sm font-bold">Temps de travail par période</h3>
          <p className="text-[11px] text-muted-ink">Total d'heures actives par CDP · montant indicatif = heures × taux horaire du profil (aide au suivi, pas un bulletin de paie).</p>
        </div>
      </div>

      {/* Contrôles : granularité + navigation/période + périmètre */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList data-testid="tt-mode">
            {PERIOD_MODES.map((m) => <TabsTrigger key={m.v} value={m.v} data-testid={`tt-mode-${m.v}`}>{m.l}</TabsTrigger>)}
          </TabsList>
        </Tabs>

        {mode !== "perso" ? (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8 border-[var(--border-soft)]" onClick={() => shift(-1)} title="Précédent" data-testid="tt-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="min-w-[10rem] text-center text-sm font-bold capitalize" data-testid="tt-period-label">{ttLabel(mode, anchor, range)}</span>
            <Button variant="outline" size="icon" className="h-8 w-8 border-[var(--border-soft)]" onClick={() => shift(1)} title="Suivant" data-testid="tt-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        ) : (
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="border-[var(--border-soft)]" data-testid="tt-custom-trigger">
                <CalendarDays className="mr-1.5 h-3.5 w-3.5" />{ttLabel("perso", anchor, range)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" data-testid="tt-custom-calendar">
              <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={2} weekStartsOn={1} locale={frLocale} defaultMonth={range?.from || anchor} />
            </PopoverContent>
          </Popover>
        )}

        <label className="ml-auto flex cursor-pointer items-center gap-2 text-[11px] font-medium text-muted-ink" data-testid="tt-scope-toggle">
          <Checkbox checked={scope === "all"} onCheckedChange={(c) => setScope(c ? "all" : "cdp")} data-testid="tt-scope-checkbox" />
          Inclure admin &amp; chefs
        </label>
      </div>

      {/* Tableau */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[560px] text-[13px]">
          <thead>
            <tr className="border-b border-[var(--border-soft)] text-left text-[10px] uppercase tracking-wide text-muted-ink">
              <th className="px-2 py-2 font-semibold">Collaborateur</th>
              <th className="px-2 py-2 text-right font-semibold">Jours actifs</th>
              <th className="px-2 py-2 text-right font-semibold">Temps de travail</th>
              <th className="px-2 py-2 text-right font-semibold">Taux (€/h)</th>
              <th className="px-2 py-2 text-right font-semibold">Montant indicatif</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-2 py-6"><div className="grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-[var(--shariti-orange)]" /></div></td></tr>
            )}
            {!loading && !canQuery && (
              <tr><td colSpan={5} className="px-2 py-6 text-center text-muted-ink" data-testid="tt-need-range">Sélectionnez une date de début et de fin.</td></tr>
            )}
            {!loading && canQuery && rows.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-6 text-center text-muted-ink" data-testid="tt-empty">Aucune activité sur cette période.</td></tr>
            )}
            {!loading && canQuery && rows.map((r) => (
              <tr key={r.user_id} className={`border-b border-[var(--border-soft)] last:border-0 ${r.active ? "" : "opacity-60"}`} data-testid={`tt-row-${r.user_id}`}>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-2">
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[10px] font-bold" style={{ background: r.role === "cdp" ? (r.color || "#FCDB53") : "var(--shariti-orange)", color: r.role === "cdp" ? "#181818" : "#fff" }}>
                      {(r.user_name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-ink">{r.user_name}</div>
                      <div className="text-[10px] text-muted-ink">{ROLE_TT[r.role] || r.role}{!r.active ? " · désactivé" : ""}</div>
                    </div>
                  </div>
                </td>
                <td className="px-2 py-2 text-right tabular-nums text-muted-ink" data-testid={`tt-days-${r.user_id}`}>{r.days_count}</td>
                <td className="px-2 py-2 text-right font-bold tabular-nums text-ink" data-testid={`tt-hours-${r.user_id}`}>
                  {r.active_label}
                  <span className="ml-1 text-[10px] font-normal text-muted-ink">({r.hours.toLocaleString("fr-FR")} h)</span>
                </td>
                <td className="px-2 py-2 text-right tabular-nums text-muted-ink">{r.role === "cdp" && r.billing_type === "hourly" && r.hourly_rate ? ttEur(r.hourly_rate) : "—"}</td>
                <td className="px-2 py-2 text-right font-bold tabular-nums" data-testid={`tt-amount-${r.user_id}`}>
                  {r.amount != null ? <span className="text-[var(--shariti-orange)]">{ttEur(r.amount)}</span> : <span className="text-muted-ink">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
          {!loading && canQuery && rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-[var(--border-soft)] font-bold">
                <td className="px-2 py-2.5" data-testid="tt-total-label">Total ({rows.length})</td>
                <td className="px-2 py-2.5" />
                <td className="px-2 py-2.5 text-right tabular-nums" data-testid="tt-total-hours">{data.total_label} <span className="text-[10px] font-normal text-muted-ink">({(data.total_hours || 0).toLocaleString("fr-FR")} h)</span></td>
                <td className="px-2 py-2.5" />
                <td className="px-2 py-2.5 text-right tabular-nums text-[var(--shariti-orange)]" data-testid="tt-total-amount">{data.total_amount ? ttEur(data.total_amount) : "—"}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function ConnexionsPanel() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/admin/activity/connexions"); setData(data); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  // Rafraîchissement auto toutes les 30 s pour le statut "en ligne".
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const manualRefresh = async () => { setRefreshing(true); await load(); setRefreshing(false); };

  if (!data) return <Loading />;

  return (
    <div className="space-y-4" data-testid="connexions-panel">
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--surface-2)]"><Activity className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
            <div>
              <h3 className="text-sm font-bold">Connexions & temps de travail</h3>
              <p className="text-[11px] text-muted-ink">Temps actif = durée réellement passée dans l'application (battements toutes les minutes). Indicatif.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(31,157,85,0.12)] px-2.5 py-1 text-[11px] font-bold text-[#0F5A2F]" data-testid="online-count">
              <span className="h-2 w-2 animate-pulse rounded-full bg-[#1F9D55]" />{data.online_count} en ligne
            </span>
            <Button size="sm" variant="outline" className="border-[var(--border-soft)]" onClick={manualRefresh} disabled={refreshing} data-testid="connexions-refresh">
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />Actualiser
            </Button>
          </div>
        </div>

        <div className="mt-4 grid gap-2.5 @lg/page:grid-cols-2 @4xl/page:grid-cols-3">
          {data.users.map((u) => (
            <div key={u.user_id} className={`rounded-xl border p-3 ${u.online ? "border-[rgba(31,157,85,0.4)] bg-[rgba(31,157,85,0.04)]" : "border-[var(--border-soft)] bg-[var(--surface-2)]"}`} data-testid={`connexion-user-${u.user_id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${u.online ? "bg-[#1F9D55]" : "bg-[rgba(24,24,24,0.2)]"}`} />
                  <span className="truncate text-sm font-bold">{u.user_name}</span>
                  <span className="shrink-0 rounded-full bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase text-muted-ink">{ROLE_LABEL[u.role] || u.role}</span>
                </div>
                {u.online && <span className="shrink-0 text-[10px] font-bold text-[#0F5A2F]">En ligne</span>}
              </div>
              <div className="mt-2 flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-ink">Actif aujourd'hui</div>
                  <div className="text-xl font-extrabold leading-tight" data-testid={`connexion-today-${u.user_id}`}>{u.active_today_label}</div>
                </div>
                <div className="text-right text-[11px] text-muted-ink">
                  <div>7 j : <span className="font-semibold text-ink">{u.active_7d_label}</span></div>
                  <div>{u.sessions_count} session(s)</div>
                </div>
              </div>
              <div className="mt-1.5 flex items-center gap-1 text-[10px] text-muted-ink">
                <Clock className="h-3 w-3" />Dernière connexion : {fmtDateTime(u.last_login)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Temps de travail par période (suivi des heures des CDP) */}
      <TempsTravailPanel />

      {/* Journal des sessions */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-bold"><History className="h-4 w-4 text-[var(--shariti-orange)]" />Journal des sessions ({data.sessions.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-[var(--border-soft)] text-left text-[10px] uppercase tracking-wide text-muted-ink">
                <th className="px-2 py-2 font-semibold">Personne</th>
                <th className="px-2 py-2 font-semibold">Connexion</th>
                <th className="px-2 py-2 font-semibold">Dernière activité</th>
                <th className="px-2 py-2 font-semibold">Temps actif</th>
                <th className="px-2 py-2 font-semibold">Amplitude</th>
                <th className="px-2 py-2 font-semibold">Statut</th>
              </tr>
            </thead>
            <tbody>
              {data.sessions.length === 0 && <tr><td colSpan={6} className="px-2 py-6 text-center text-muted-ink">Aucune session enregistrée.</td></tr>}
              {data.sessions.map((s) => (
                <tr key={s.id} className="border-b border-[var(--border-soft)] last:border-0" data-testid={`session-row-${s.id}`}>
                  <td className="px-2 py-2"><span className="font-semibold">{s.user_name}</span> <span className="text-[10px] text-muted-ink">{ROLE_LABEL[s.role] || s.role}</span></td>
                  <td className="px-2 py-2 text-muted-ink">{fmtDateTime(s.login_at)}</td>
                  <td className="px-2 py-2 text-muted-ink">{fmtTime(s.last_seen_at)}</td>
                  <td className="px-2 py-2 font-bold text-ink">{s.active_label}</td>
                  <td className="px-2 py-2 text-muted-ink">{s.span_label}</td>
                  <td className="px-2 py-2">
                    {s.online
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(31,157,85,0.12)] px-2 py-0.5 text-[10px] font-bold text-[#0F5A2F]"><span className="h-1.5 w-1.5 rounded-full bg-[#1F9D55]" />En ligne</span>
                      : <span className="text-[10px] text-muted-ink">Terminée</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function AssocPanel() {
  const [rows, setRows] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const load = () => api.get("/associations").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  if (!rows) return <Loading />;
  return (
    <div className="space-y-3" data-testid="assoc-panel">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-ink">
          Créez une association, puis ajoutez son logo et son email de contact. Le logo apparaît dans la signature des emails,
          sur la page de confirmation et dans le planning.
        </p>
        <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="assoc-create-open"
          className="shrink-0 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]">
          <Plus className="mr-1.5 h-4 w-4" /> Nouvelle association
        </Button>
      </div>
      <ListCard>
        {rows.length === 0 && <div className="p-6 text-center text-xs text-muted-ink">Aucune association. Créez-en une avec le bouton ci-dessus.</div>}
        {rows.map((a) => <AssocRow key={a.id} a={a} onChange={load} />)}
      </ListCard>
      <NewAssocDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
    </div>
  );
}

// Palette de couleurs proposées pour une nouvelle association (évite les couleurs brutes).
const ASSOC_COLORS = ["#EE722E", "#F2C300", "#E4572E", "#2A9D8F", "#C1121F", "#457B9D", "#8B5CF6", "#EC4899", "#14B8A6", "#1F9D55", "#2F6FED", "#F59E0B"];

function NewAssocDialog({ open, onOpenChange, onCreated }) {
  const fileRef = useRef(null);
  const [name, setName] = useState("");
  const [acronym, setAcronym] = useState("");
  const [color, setColor] = useState(ASSOC_COLORS[0]);
  const [email, setEmail] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setName(""); setAcronym(""); setColor(ASSOC_COLORS[0]); setEmail(""); setLogoFile(null); };
  const submit = async () => {
    if (!name.trim() || !acronym.trim()) { toast.error("Nom et sigle obligatoires."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/associations", {
        name: name.trim(), acronym: acronym.trim().toUpperCase(),
        color, email: email.trim() || null,
      });
      // Logo facultatif, envoyé juste après la création.
      if (logoFile && data?.id) {
        const fd = new FormData(); fd.append("file", logoFile);
        await api.post(`/admin/associations/${data.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Association « ${acronym.trim().toUpperCase()} » créée`);
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Création impossible");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="assoc-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-[var(--shariti-orange)]" /> Nouvelle association</DialogTitle>
          <DialogDescription>Renseignez les informations de base. Le mandat (PDF) et les départements couverts se gèrent ensuite dans leurs onglets dédiés.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px]">Nom complet *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Secours Populaire Français" className="mt-1 h-9" data-testid="assoc-create-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Sigle *</Label>
              <Input value={acronym} onChange={(e) => setAcronym(e.target.value.toUpperCase())} placeholder="Ex. SPF" className="mt-1 h-9 uppercase" data-testid="assoc-create-acronym" />
            </div>
            <div>
              <Label className="text-[11px]">Email de contact</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="contact@asso.fr" className="mt-1 h-9" data-testid="assoc-create-email" />
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Couleur</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="assoc-create-colors">
              {ASSOC_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)}
                  className={`h-7 w-7 rounded-full border-2 transition-transform ${color === c ? "scale-110 border-ink" : "border-white"}`}
                  style={{ background: c }} title={c} data-testid={`assoc-create-color-${c}`} />
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Logo (facultatif)</Label>
            <div className="mt-1 flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                onChange={(e) => setLogoFile(e.target.files?.[0] || null)} data-testid="assoc-create-logo-input" />
              <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="border-[var(--border-soft)]" data-testid="assoc-create-logo-pick">
                <Upload className="mr-1.5 h-4 w-4" /> {logoFile ? "Changer le logo" : "Choisir un logo"}
              </Button>
              {logoFile && <span className="truncate text-xs text-muted-ink">{logoFile.name}</span>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }} data-testid="assoc-create-cancel">Annuler</Button>
          <Button onClick={submit} disabled={busy} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="assoc-create-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer l'association"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssocRow({ a, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const [email, setEmail] = useState(a.email || "");
  const [savingEmail, setSavingEmail] = useState(false);
  const logoUrl = `${api.defaults.baseURL}/assets/association-logo/${a.id}?t=${bust}`;
  const emailDirty = (email || "").trim() !== (a.email || "");

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      await api.put(`/admin/associations/${a.id}/contact`, { email });
      toast.success(`Email « ${a.acronym} » enregistré`);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSavingEmail(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/admin/associations/${a.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Logo « ${a.acronym} » mis à jour`);
      setBust(Date.now());
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'envoi du logo");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/associations/${a.id}/logo`);
      toast.success("Logo supprimé");
      onChange();
    } catch {
      toast.error("Échec de la suppression");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 border-b border-[var(--border-soft)] p-4 last:border-0" data-testid={`assoc-${a.acronym}`}>
      <div className="flex min-w-0 items-center gap-3">
        {a.has_logo ? (
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl border border-black/10 bg-white p-1.5">
            <img src={logoUrl} alt={a.acronym} className="max-h-11 max-w-full object-contain" data-testid={`assoc-logo-img-${a.acronym}`} />
          </div>
        ) : (
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-xl border border-black/10 text-xs font-extrabold text-white" style={{ background: a.color }}>
            {a.acronym}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate font-bold">{a.name}</div>
          <div className="text-xs text-muted-ink">{a.acronym} • {a.regions_covered?.length || 0} départements couverts</div>
          {a.paris_last_week_constraint && <span className="mt-1 inline-block rounded-full bg-[rgba(238,114,46,0.12)] px-2 py-0.5 text-[10px] font-semibold text-[#8A3A10]">Paris — dernière semaine du mois</span>}
          <div className="mt-2 flex max-w-md items-center gap-1.5">
            <Mail className="h-3.5 w-3.5 shrink-0 text-muted-ink" />
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
              placeholder="Email de contact (renouvellement de mandat)"
              className="h-8 text-xs" data-testid={`assoc-email-${a.acronym}`} />
            {emailDirty && (
              <Button size="sm" onClick={saveEmail} disabled={savingEmail}
                className="h-8 shrink-0 bg-[var(--shariti-orange)] px-2 text-white hover:bg-[#E56622]"
                data-testid={`assoc-email-save-${a.acronym}`}>
                {savingEmail ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Enregistrer"}
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid={`assoc-logo-input-${a.acronym}`} />
        <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} className="border-[var(--border-soft)]" data-testid={`assoc-logo-upload-${a.acronym}`}>
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
          {a.has_logo ? "Remplacer" : "Ajouter un logo"}
        </Button>
        {a.has_logo && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={remove} data-testid={`assoc-logo-delete-${a.acronym}`}>
            <Trash2 className="h-4 w-4 text-[#9B2226]" />
          </Button>
        )}
      </div>
    </div>
  );
}

function LignesPanel() {
  const [rows, setRows] = useState(null);
  const [cdps, setCdps] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [chefs, setChefs] = useState([]);
  const [perimetres, setPerimetres] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [canaux, setCanaux] = useState([]);
  const [territoires, setTerritoires] = useState([]);
  const [edit, setEdit] = useState(undefined); // undefined=fermé, null=création, obj=édition
  const [reassign, setReassign] = useState(null); // ligne en cours de réattribution CDP
  const [view, setView] = useState("actives"); // actives | archivees
  const [archived, setArchived] = useState([]);
  const [showLog, setShowLog] = useState(false); // historique des réattributions
  const load = () => api.get("/lignes").then((r) => setRows(r.data));
  const loadArchived = () => api.get("/admin/lignes/archived").then((r) => setArchived(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadArchived();
    api.get("/cdps").then((r) => setCdps(r.data));
    api.get("/equipes").then((r) => setEquipes(r.data));
    api.get("/chefs-equipe").then((r) => setChefs(r.data));
    api.get("/perimetres").then((r) => setPerimetres(r.data));
    api.get("/associations").then((r) => setAssociations(r.data));
    api.get("/canaux").then((r) => setCanaux(r.data));
    api.get("/territoires").then((r) => setTerritoires(r.data));
  }, []);
  if (!rows) return <Loading />;
  const cdp = (id) => cdps.find((c) => c.id === id);
  const byId = (arr, id) => arr.find((x) => x.id === id);
  const remove = async (l) => {
    try {
      const { data } = await api.delete(`/admin/lignes/${l.id}`);
      if (data.mode === "archived")
        toast.success(`Ligne archivée — ${data.bookings} réservation(s) conservée(s).`);
      else
        toast.success("Ligne supprimée définitivement.");
      load();
      loadArchived();
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };
  const restore = async (l) => {
    try {
      await api.post(`/admin/lignes/${l.id}/restore`);
      toast.success(`Ligne « ${l.identifiant} » restaurée.`);
      load();
      loadArchived();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restauration impossible"); }
  };
  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-bold">Lignes de planning</div>
          <p className="text-xs text-muted-ink">Une ligne = association · canal · code campagne · lieu · équipe – chef · CDP. C'est la rangée que le CDP voit dans son planning.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowLog(true)} data-testid="reassign-log-btn">
            <History className="mr-1 h-4 w-4" /> Historique des réattributions
          </Button>
          <Button onClick={() => setEdit(null)} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="ligne-new-btn">
            <Plus className="mr-1 h-4 w-4" /> Nouvelle ligne
          </Button>
        </div>
      </div>
      <div className="mb-3 inline-flex rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-0.5" data-testid="lignes-view-toggle">
        {[["actives", `Actives (${rows.length})`], ["archivees", `Archivées (${archived.length})`]].map(([v, label]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`lignes-view-${v}`}
            className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${view === v ? "bg-white text-ink shadow-[var(--shadow-sm)]" : "text-muted-ink hover:text-ink"}`}>
            {label}
          </button>
        ))}
      </div>
      {view === "actives" && (
      <ListCard>
        {rows.map((l) => {
          const c = cdp(l.cdp_id);
          const eq = byId(equipes, l.equipe_id);
          const chef = byId(chefs, l.chef_equipe_id);
          const perim = byId(perimetres, l.perimetre_id);
          const asso = byId(associations, l.association_id);
          return (
            <div key={l.id} className="flex items-center justify-between gap-3 border-b border-[var(--border-soft)] p-4 last:border-0" data-testid={`ligne-${l.id}`}>
              <div className="min-w-0">
                <div className="font-bold">{l.identifiant}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                  {asso && <span className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-2)] px-2 py-0.5 font-semibold" data-testid={`ligne-asso-${l.id}`}><Building2 className="h-3 w-3" />{asso.acronym}</span>}
                  {l.code_campagne && <Badge variant="secondary" className="bg-[var(--surface-2)] text-[10px] font-mono">{l.code_campagne}</Badge>}
                  {eq && <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(47,111,237,0.1)] px-2 py-0.5 font-semibold text-[#1D4ED8]" data-testid={`ligne-equipe-${l.id}`}><Users className="h-3 w-3" />{eq.nom}</span>}
                  {chef && <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(238,114,46,0.1)] px-2 py-0.5 font-semibold text-[#8A3A10]" data-testid={`ligne-chef-${l.id}`}><UserRound className="h-3 w-3" />{chef.nom}</span>}
                  {perim ? <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(31,157,85,0.1)] px-2 py-0.5 font-semibold text-[#0F5A2F]" data-testid={`ligne-perimetre-${l.id}`}><Compass className="h-3 w-3" />{perim.nom}</span>
                    : l.zones_label && <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(31,157,85,0.1)] px-2 py-0.5 font-semibold text-[#0F5A2F]"><Compass className="h-3 w-3" />{l.zones_label}</span>}
                  {l.date_debut && <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(240,190,20,0.22)] px-2 py-0.5 font-semibold text-[#5A4200]" data-testid={`ligne-date-debut-${l.id}`}><CalendarRange className="h-3 w-3" />Débute le {ligneWeekLabel(l.date_debut)}</span>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {c && <span className="h-3 w-3 rounded-full border border-black/10" style={{ background: c.color }} />}
                <span className="text-xs font-semibold">{c?.name || "—"}</span>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setReassign(l)} title="Réattribuer à un autre CDP" data-testid={`ligne-reassign-${l.id}`}>
                  <UserCog className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEdit(l)} title="Modifier la ligne" data-testid={`ligne-edit-${l.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#D64545]" title="Supprimer la ligne" data-testid={`ligne-delete-${l.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer la ligne « {l.identifiant} » ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Si cette ligne n'a <strong>aucune réservation</strong>, elle est supprimée définitivement.
                        Sinon elle est <strong>archivée</strong> : son historique est conservé, mais elle disparaît
                        du planning et des listes (assignation, sélecteurs).
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid={`ligne-delete-cancel-${l.id}`}>Annuler</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(l)} className="bg-[#D64545] hover:bg-[#C63D3D]" data-testid={`ligne-delete-confirm-${l.id}`}>Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          );
        })}
      </ListCard>
      )}
      {view === "archivees" && (
        <ListCard>
          {archived.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-ink" data-testid="archived-empty">Aucune ligne archivée.</div>
          ) : archived.map((l) => {
            const c = cdp(l.cdp_id);
            return (
              <div key={l.id} className="flex items-center justify-between gap-3 border-b border-[var(--border-soft)] p-4 last:border-0" data-testid={`archived-ligne-${l.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-bold">
                    {l.identifiant}
                    <Badge variant="secondary" className="bg-[rgba(240,190,20,0.22)] text-[10px] text-[#5A4200]">Archivée</Badge>
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-ink">
                    {l.code_campagne && <span className="font-mono">{l.code_campagne}</span>}
                    {c && <span>· {c.name}</span>}
                    <span>· {l.bookings_count || 0} réservation(s) conservée(s)</span>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => restore(l)} data-testid={`ligne-restore-${l.id}`}>
                  <RefreshCw className="mr-1 h-3.5 w-3.5" /> Restaurer
                </Button>
              </div>
            );
          })}
        </ListCard>
      )}
      <LigneEditDialog ligne={edit} open={edit !== undefined} onClose={() => setEdit(undefined)}
        cdps={cdps} equipes={equipes} setEquipes={setEquipes} chefs={chefs} setChefs={setChefs}
        perimetres={perimetres} associations={associations} canaux={canaux} territoires={territoires}
        onSaved={() => { setEdit(undefined); load(); }} />
      <ReassignLigneDialog ligne={reassign} cdps={cdps} open={!!reassign}
        onClose={() => setReassign(null)} onSaved={() => { setReassign(null); load(); loadArchived(); }} />
      <ReassignmentLogDialog open={showLog} onClose={() => setShowLog(false)} cdps={cdps} />
    </>
  );
}

// Historique des réattributions de lignes (journal filtré action=reassign_ligne).
function ReassignmentLogDialog({ open, onClose }) {
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    if (open) {
      setLogs(null);
      api.get("/admin/activity", { params: { action: "reassign_ligne", limit: 100 } })
        .then((r) => setLogs(r.data)).catch(() => setLogs([]));
    }
  }, [open]);
  const fmt = (ts) => { try { return new Date(ts).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ts; } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="reassign-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4" />Historique des réattributions</DialogTitle>
          <DialogDescription>Qui a réattribué quelle ligne, quand, et de quel CDP à quel CDP.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-2 overflow-y-auto">
          {logs === null ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-ink"><Loader2 className="h-4 w-4 animate-spin" />Chargement…</div>
          ) : logs.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-ink" data-testid="reassign-log-empty">Aucune réattribution enregistrée pour l'instant.</div>
          ) : logs.map((a, i) => (
            <div key={a.id || i} className="rounded-lg border border-[var(--border-soft)] bg-white p-3" data-testid={`reassign-log-${i}`}>
              <div className="text-[12px] font-medium text-ink">{a.details}</div>
              <div className="mt-1 flex items-center gap-1.5 text-[10px] text-muted-ink">
                <UserRound className="h-3 w-3" />{a.user_name || "—"}
                <span>·</span>
                <Clock className="h-3 w-3" />{fmt(a.timestamp)}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="reassign-log-close">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Réattribution rapide d'une ligne à un autre CDP (sans recréer la ligne).
// Les réservations >= date choisie basculent au nouveau CDP ; l'historique reste intact.
function ReassignLigneDialog({ ligne, cdps, open, onClose, onSaved }) {
  const [cdpId, setCdpId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setCdpId(""); setFromDate(new Date().toISOString().slice(0, 10)); }
  }, [open, ligne]);
  if (!ligne) return null;
  const currentCdp = cdps.find((c) => c.id === ligne.cdp_id);
  const submit = async () => {
    if (!cdpId) { toast.error("Choisissez le CDP cible."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/lignes/${ligne.id}/reassign`, { cdp_id: cdpId, from_date: fromDate || undefined });
      toast.success(`Ligne réattribuée à ${data.new_cdp_name} — ${data.moved} réservation(s) déplacée(s), ${data.kept} conservée(s) au CDP d'origine.`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Réattribution impossible."); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="ligne-reassign-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Réattribuer la ligne</DialogTitle>
          <DialogDescription>
            « {ligne.identifiant} » — actuellement : <strong>{currentCdp?.name || "aucun CDP"}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px]">Nouveau CDP titulaire</Label>
            <Select value={cdpId || undefined} onValueChange={setCdpId}>
              <SelectTrigger className="mt-1 h-9" data-testid="reassign-cdp"><SelectValue placeholder="Choisir un CDP" /></SelectTrigger>
              <SelectContent>
                {cdps.filter((c) => c.id !== ligne.cdp_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Réservations réattribuées à partir du</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1 h-9" data-testid="reassign-from-date" />
            <p className="mt-1 text-[10px] text-muted-ink">
              Les réservations <strong>à partir de cette date</strong> passent au nouveau CDP.
              L'historique <strong>avant</strong> reste au CDP d'origine (paie &amp; stats préservées).
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="reassign-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="reassign-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Réattribuer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const NONE_LIGNE = "__none__";

// Lundi de la semaine (libellé français) pour une date ISO — affichage de date_debut.
function ligneWeekLabel(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    const day = (d.getDay() + 6) % 7; // 0 = lundi
    d.setDate(d.getDate() - day);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}


// Compose un identifiant lisible : "AIF - SP (PERFORMERS Karim)"
function composeLigneId(asso, canal, equipe, chef) {
  const acr = asso?.acronym || "";
  const cc = canal?.code || "";
  const eqNom = equipe?.nom || "";
  const chefPrenom = chef?.nom ? String(chef.nom).trim().split(/\s+/)[0] : "";
  const paren = [eqNom, chefPrenom].filter(Boolean).join(" ");
  let s = acr;
  if (cc) s += (s ? " - " : "") + cc;
  if (paren) s += ` (${paren})`;
  return s.trim();
}

function LigneEditDialog({ ligne, open, onClose, onSaved, cdps, equipes, setEquipes, chefs, setChefs, perimetres, associations, canaux, territoires }) {
  const isCreate = ligne === null;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [idTouched, setIdTouched] = useState(false);
  const [lieuTouched, setLieuTouched] = useState(false);
  const [newEquipe, setNewEquipe] = useState("");
  const [newChef, setNewChef] = useState("");
  const [creating, setCreating] = useState("");

  useEffect(() => {
    if (!open) return;
    const l = ligne || {};
    setForm({
      identifiant: l.identifiant || "",
      association_id: l.association_id || "",
      canal_id: l.canal_id || "",
      code_campagne: l.code_campagne || "",
      cdp_id: l.cdp_id || "",
      equipe_id: l.equipe_id || "",
      chef_equipe_id: l.chef_equipe_id || "",
      perimetre_id: l.perimetre_id || "",
      zones_label: l.zones_label || "",
      date_debut: l.date_debut || "",
    });
    setIdTouched(!isCreate);   // en édition, on ne réécrit pas l'identifiant automatiquement
    setLieuTouched(!isCreate || !!l.zones_label);
    setNewEquipe(""); setNewChef("");
  }, [open, ligne]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v === NONE_LIGNE ? "" : v }));

  const asso = associations.find((a) => a.id === form.association_id);
  const canal = canaux.find((c) => c.id === form.canal_id);
  const equipe = equipes.find((e) => e.id === form.equipe_id);
  const chef = chefs.find((c) => c.id === form.chef_equipe_id);

  // Départements de la zone du CDP sélectionné (pour prioriser les assos + auto-lieu).
  const selectedCdp = cdps.find((c) => c.id === form.cdp_id);
  const zoneTerr = (selectedCdp?.territoire_ids || [])
    .map((tid) => territoires.find((t) => t.id === tid)).filter(Boolean);
  const zoneDepts = new Set(zoneTerr.flatMap((t) => t.departments || []));

  // Auto-composition de l'identifiant tant que l'admin ne l'a pas édité à la main.
  useEffect(() => {
    if (!open || idTouched) return;
    const composed = composeLigneId(asso, canal, equipe, chef);
    if (composed) setForm((f) => ({ ...f, identifiant: composed }));
  }, [form.association_id, form.canal_id, form.equipe_id, form.chef_equipe_id, open]); // eslint-disable-line

  // Auto-remplissage du lieu depuis le territoire du CDP, tant que non édité.
  useEffect(() => {
    if (!open || lieuTouched) return;
    if (zoneTerr.length) {
      const t = zoneTerr[0];
      const label = `${t.name}${(t.departments || []).length ? ` (${t.departments.join(", ")})` : ""}`;
      setForm((f) => ({ ...f, zones_label: label }));
    }
  }, [form.cdp_id, open]); // eslint-disable-line

  const assoCovers = (a) => (a.regions_covered || []).some((d) => zoneDepts.has(d));
  const sortedAssos = [...associations].sort((a, b) => {
    if (zoneDepts.size) {
      const ca = assoCovers(a), cb = assoCovers(b);
      if (ca !== cb) return ca ? -1 : 1;
    }
    return (a.acronym || "").localeCompare(b.acronym || "");
  });

  const createEquipe = async () => {
    if (!newEquipe.trim()) return;
    setCreating("equipe");
    try {
      const r = await api.post("/admin/equipes", { nom: newEquipe.trim(), cdp_id: form.cdp_id || null, association_id: form.association_id || null });
      setEquipes((arr) => [...arr, r.data]);
      set("equipe_id", r.data.id);
      setNewEquipe("");
      toast.success("Équipe créée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setCreating(""); }
  };
  const createChef = async () => {
    if (!newChef.trim()) return;
    setCreating("chef");
    try {
      const r = await api.post("/admin/chefs-equipe", { nom: newChef.trim() });
      setChefs((arr) => [...arr, r.data]);
      set("chef_equipe_id", r.data.id);
      setNewChef("");
      toast.success("Chef d'équipe créé");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setCreating(""); }
  };

  const submit = async () => {
    if (!form.identifiant.trim()) { toast.error("Le nom de la ligne est requis"); return; }
    if (!form.association_id) { toast.error("L'association est requise"); return; }
    setSaving(true);
    try {
      const base = isCreate ? { source: "manuel", status: "active" } : { ...ligne };
      const payload = {
        ...base,
        identifiant: form.identifiant.trim(),
        association_id: form.association_id,
        canal_id: form.canal_id || null,
        code_campagne: (form.code_campagne || "").trim(),
        cdp_id: form.cdp_id || null,
        equipe_id: form.equipe_id || null,
        chef_equipe_id: form.chef_equipe_id || null,
        perimetre_id: form.perimetre_id || null,
        zones_label: (form.zones_label || "").trim(),
        date_debut: form.date_debut || null,
      };
      await api.post("/admin/lignes", payload);
      toast.success(isCreate
        ? (form.date_debut
            ? `Ligne créée — visible dans le planning à partir de la semaine du ${ligneWeekLabel(form.date_debut)}.`
            : "Ligne créée — elle apparaît dans le planning du CDP.")
        : "Ligne mise à jour");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  if (ligne === undefined) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="ligne-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{isCreate ? "Nouvelle ligne" : "Modifier la ligne"}</DialogTitle>
          <DialogDescription>
            {isCreate ? "Créez une ligne pour un CDP. Elle apparaîtra dans son planning immédiatement, ou à partir de la date de début si vous en définissez une."
              : "Ajustez la ligne et ses rattachements (association, canal, CDP, équipe, chef, lieu)."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* CDP en premier : conditionne la priorisation des assos et le lieu auto */}
          <div>
            <Label className="text-[11px]">CDP titulaire</Label>
            <Select value={form.cdp_id || NONE_LIGNE} onValueChange={(v) => set("cdp_id", v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="ligne-form-cdp"><SelectValue placeholder="Choisir un CDP" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_LIGNE}>— Aucun —</SelectItem>
                {cdps.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {zoneTerr.length > 0 && (
              <p className="mt-1 text-[10px] text-muted-ink">Périmètre du CDP : {zoneTerr.map((t) => `${t.name} (${(t.departments || []).join(", ")})`).join(" · ")}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Association *</Label>
              <Select value={form.association_id || ""} onValueChange={(v) => set("association_id", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="ligne-form-association"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {sortedAssos.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.acronym}{zoneDepts.size && assoCovers(a) ? "  ✓ zone" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Canal</Label>
              <Select value={form.canal_id || NONE_LIGNE} onValueChange={(v) => set("canal_id", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="ligne-form-canal"><SelectValue placeholder="Canal" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_LIGNE}>— Aucun —</SelectItem>
                  {canaux.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Équipe : sélection ou création à la volée */}
          <div>
            <Label className="text-[11px]">Équipe</Label>
            <Select value={form.equipe_id || NONE_LIGNE} onValueChange={(v) => set("equipe_id", v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="ligne-form-equipe"><SelectValue placeholder="Équipe" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_LIGNE}>— Aucune —</SelectItem>
                {equipes.map((e) => <SelectItem key={e.id} value={e.id}>{e.nom}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="mt-1.5 flex items-center gap-1.5">
              <Input value={newEquipe} onChange={(e) => setNewEquipe(e.target.value)} placeholder="Créer une équipe (nom)…" className="h-8 text-xs" data-testid="ligne-form-new-equipe" />
              <Button type="button" variant="secondary" className="h-8 shrink-0 border border-[var(--border-soft)] bg-white px-2 text-xs" disabled={!newEquipe.trim() || creating === "equipe"} onClick={createEquipe} data-testid="ligne-form-new-equipe-btn">
                {creating === "equipe" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="mr-1 h-3.5 w-3.5" />Créer</>}
              </Button>
            </div>
          </div>

          {/* Chef d'équipe : sélection ou création à la volée */}
          <div>
            <Label className="text-[11px]">Chef d'équipe</Label>
            <Select value={form.chef_equipe_id || NONE_LIGNE} onValueChange={(v) => set("chef_equipe_id", v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="ligne-form-chef"><SelectValue placeholder="Chef d'équipe" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_LIGNE}>— Aucun —</SelectItem>
                {chefs.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="mt-1.5 flex items-center gap-1.5">
              <Input value={newChef} onChange={(e) => setNewChef(e.target.value)} placeholder="Créer un chef (nom)…" className="h-8 text-xs" data-testid="ligne-form-new-chef" />
              <Button type="button" variant="secondary" className="h-8 shrink-0 border border-[var(--border-soft)] bg-white px-2 text-xs" disabled={!newChef.trim() || creating === "chef"} onClick={createChef} data-testid="ligne-form-new-chef-btn">
                {creating === "chef" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="mr-1 h-3.5 w-3.5" />Créer</>}
              </Button>
            </div>
          </div>

          {/* Lieu / zone (auto-rempli depuis le territoire du CDP) */}
          <div>
            <Label className="text-[11px]">Lieu / zone</Label>
            <Input value={form.zones_label || ""} onChange={(e) => { setLieuTouched(true); set("zones_label", e.target.value); }}
              placeholder="ex : Alsace (67, 68)" className="mt-1 h-9" data-testid="ligne-form-lieu" />
            <p className="mt-1 text-[10px] text-muted-ink">Affiché comme « lieu » dans le planning. Pré-rempli depuis le périmètre du CDP.</p>
          </div>

          {/* Début d'affichage dans le planning (optionnel) */}
          <div>
            <Label className="text-[11px]">Début d'affichage dans le planning</Label>
            <Input type="date" value={form.date_debut || ""} onChange={(e) => set("date_debut", e.target.value)}
              className="mt-1 h-9 w-full" data-testid="ligne-form-date-debut" />
            <p className="mt-1 text-[10px] text-muted-ink">
              {form.date_debut
                ? `La ligne apparaîtra à partir de la semaine du ${ligneWeekLabel(form.date_debut)}, puis pour toutes les semaines suivantes.`
                : "Laissez vide pour afficher la ligne immédiatement (comportement par défaut)."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Code campagne</Label>
              <Input value={form.code_campagne || ""} onChange={(e) => set("code_campagne", e.target.value)} placeholder="auto si vide" className="mt-1 h-9 font-mono" data-testid="ligne-form-code" />
            </div>
            <div>
              <Label className="text-[11px]">Périmètre (avancé)</Label>
              <Select value={form.perimetre_id || NONE_LIGNE} onValueChange={(v) => set("perimetre_id", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="ligne-form-perimetre"><SelectValue placeholder="Optionnel" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_LIGNE}>— Aucun —</SelectItem>
                  {perimetres.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Identifiant (auto-composé, éditable) */}
          <div>
            <Label className="text-[11px]">Nom de la ligne (identifiant) *</Label>
            <div className="mt-1 flex items-center gap-1.5">
              <Input value={form.identifiant || ""} onChange={(e) => { setIdTouched(true); set("identifiant", e.target.value); }}
                className="h-9 font-semibold" data-testid="ligne-form-identifiant" />
              <Button type="button" variant="secondary" className="h-9 shrink-0 border border-[var(--border-soft)] bg-white px-2 text-xs"
                title="Recomposer automatiquement" onClick={() => { setIdTouched(false); const c = composeLigneId(asso, canal, equipe, chef); if (c) set("identifiant", c); }} data-testid="ligne-form-recompose">
                <Wand2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="ligne-form-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="ligne-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isCreate ? "Créer la ligne" : "Enregistrer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InactiveSection({ count, label = "Inactifs", children }) {
  const [open, setOpen] = useState(false);
  if (!count) return null;
  return (
    <div className="overflow-hidden rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)]" data-testid="inactive-section">
      <button onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left transition-colors hover:bg-[#EFE7E1]"
        data-testid="inactive-toggle" aria-expanded={open}>
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-muted-ink">
          <span className="grid h-5 min-w-5 place-items-center rounded-full bg-white px-1 text-[10px] font-bold text-muted-ink">{count}</span>
          {label} — masqués de la liste active
        </span>
        <ChevronDown className={`h-4 w-4 text-muted-ink transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="border-t border-[var(--border-soft)] bg-white opacity-75">{children}</div>}
    </div>
  );
}

function ChefsPanel() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(undefined); // undefined=closed, null=create, obj=edit
  const load = () => api.get("/chefs-equipe").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  if (!rows) return <Loading />;
  const filtered = rows.filter((c) => !q || `${c.nom} ${c.email || ""}`.toLowerCase().includes(q.toLowerCase()));
  const aCompleter = rows.filter((c) => c.a_completer).length;
  const activeRows = filtered.filter((c) => c.active !== false);
  const inactiveRows = filtered.filter((c) => c.active === false);
  const remove = async (c) => { await api.delete(`/admin/chefs-equipe/${c.id}`); toast.success("Chef d'équipe désactivé"); load(); };
  const reactivate = async (c) => { await api.post("/admin/chefs-equipe", { ...c, active: true }); toast.success("Chef d'équipe réactivé"); load(); };
  const renderChefRow = (c, inactive) => (
    <div key={c.id} className="flex items-center justify-between gap-3 border-b border-[var(--border-soft)] p-3 last:border-0 hover:bg-[var(--surface-2)]" data-testid={`chef-row-${c.id}`}>
      <div className="flex min-w-0 items-center gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--surface-2)] text-xs font-bold"><UserRound className="h-4 w-4 text-[var(--shariti-orange)]" /></span>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5"><span className="truncate font-bold">{c.nom}</span>
            {inactive && <Badge variant="outline" className="text-[10px] text-muted-ink">inactif</Badge>}
            {c.a_completer && <Badge className="bg-[rgba(233,184,0,0.35)] text-[#5A4200] text-[10px]">à compléter</Badge>}
          </div>
          <div className="text-xs text-muted-ink">{[c.email, c.tel].filter(Boolean).join(" · ") || "Coordonnées manquantes"}</div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {inactive ? (
          <Button variant="ghost" size="sm" className="h-8 gap-1 text-[#0F5A2F]" onClick={() => reactivate(c)} data-testid={`chef-reactivate-${c.id}`}><RotateCcw className="h-4 w-4" />Réactiver</Button>
        ) : (
          <>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEdit(c)} title="Modifier" data-testid={`chef-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-[#D64545]" title="Désactiver" data-testid={`chef-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Désactiver « {c.nom} » ?</AlertDialogTitle>
                  <AlertDialogDescription>Le chef d'équipe ne sera plus proposé. Il rejoint la section « Inactifs » (réactivable à tout moment). Les lignes existantes conservent leur référence.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel data-testid={`chef-delete-cancel-${c.id}`}>Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={() => remove(c)} className="bg-[#D64545] hover:bg-[#C63D3D]" data-testid={`chef-delete-confirm-${c.id}`}>Désactiver</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un chef d'équipe…" className="h-9 w-64 pl-8" data-testid="chef-search" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          {aCompleter > 0 && <Badge className="bg-[rgba(233,184,0,0.35)] text-[#5A4200] text-[10px]">{aCompleter} à compléter</Badge>}
          <span className="text-xs text-muted-ink" data-testid="chef-count">{activeRows.length} chef(s)</span>
          <Button size="sm" onClick={() => setEdit(null)} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="chef-new"><Plus className="mr-1.5 h-4 w-4" />Nouveau chef</Button>
        </div>
      </div>
      <ListCard>
        {activeRows.length === 0 && <div className="p-6 text-center text-xs text-muted-ink">Aucun chef d'équipe actif.</div>}
        {activeRows.map((c) => renderChefRow(c, false))}
      </ListCard>
      <InactiveSection count={inactiveRows.length}>
        {inactiveRows.map((c) => renderChefRow(c, true))}
      </InactiveSection>
      <ChefEditDialog chef={edit} open={edit !== undefined} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); load(); }} />
    </div>
  );
}

function ChefEditDialog({ chef, open, onClose, onSaved }) {
  const isEdit = !!chef;
  const [form, setForm] = useState({ nom: "", email: "", tel: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(chef ? { nom: chef.nom || "", email: chef.email || "", tel: chef.tel || "" } : { nom: "", email: "", tel: "" });
  }, [open, chef]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.nom.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      const payload = isEdit ? { ...chef, ...form } : form;
      await api.post("/admin/chefs-equipe", payload);
      toast.success(isEdit ? "Chef d'équipe mis à jour" : "Chef d'équipe créé");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="chef-edit-dialog">
        <DialogHeader><DialogTitle className="text-base">{isEdit ? "Modifier le chef d'équipe" : "Nouveau chef d'équipe"}</DialogTitle>
          <DialogDescription>Coordonnées utilisées pour la diffusion hebdomadaire du planning (email + WhatsApp).</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-[11px]">Nom *</Label><Input value={form.nom} onChange={(e) => set("nom", e.target.value)} className="mt-1 h-9" data-testid="chef-form-nom" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px]">Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 h-9" data-testid="chef-form-email" /></div>
            <div><Label className="text-[11px]">Téléphone</Label><Input value={form.tel} onChange={(e) => set("tel", e.target.value)} className="mt-1 h-9" data-testid="chef-form-tel" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="chef-form-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="chef-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EquipesPanel() {
  const [rows, setRows] = useState(null);
  const [chefs, setChefs] = useState([]);
  const [cdps, setCdps] = useState([]);
  const [assocs, setAssocs] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(undefined);
  const load = () => api.get("/equipes").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/chefs-equipe").then((r) => setChefs(r.data));
    api.get("/cdps").then((r) => setCdps(r.data));
    api.get("/associations").then((r) => setAssocs(r.data));
  }, []);
  if (!rows) return <Loading />;
  const byId = (arr, id) => arr.find((x) => x.id === id);
  const filtered = rows.filter((e) => !q || e.nom.toLowerCase().includes(q.toLowerCase()));
  const activeRows = filtered.filter((e) => e.active !== false);
  const inactiveRows = filtered.filter((e) => e.active === false);
  const remove = async (e) => { await api.delete(`/admin/equipes/${e.id}`); toast.success("Équipe désactivée"); load(); };
  const reactivate = async (e) => { await api.post("/admin/equipes", { ...e, active: true }); toast.success("Équipe réactivée"); load(); };
  const renderEquipeRow = (e, inactive) => {
    const chef = byId(chefs, e.chef_equipe_id);
    const cdp = byId(cdps, e.cdp_id);
    const assoc = byId(assocs, e.association_id);
    return (
      <div key={e.id} className="flex items-center justify-between gap-3 border-b border-[var(--border-soft)] p-3 last:border-0 hover:bg-[var(--surface-2)]" data-testid={`equipe-row-${e.id}`}>
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--surface-2)]"><Users className="h-4 w-4 text-[#1D4ED8]" /></span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5"><span className="truncate font-bold">{e.nom}</span>
              {inactive && <Badge variant="outline" className="text-[10px] text-muted-ink">inactive</Badge>}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-ink">
              {chef && <span className="inline-flex items-center gap-1"><UserRound className="h-3 w-3" />{chef.nom}</span>}
              {cdp && <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full border border-black/10" style={{ background: cdp.color }} />{cdp.name}</span>}
              {assoc && <Badge variant="secondary" className="bg-[var(--surface-2)] text-[10px]">{assoc.acronym}</Badge>}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {inactive ? (
            <Button variant="ghost" size="sm" className="h-8 gap-1 text-[#0F5A2F]" onClick={() => reactivate(e)} data-testid={`equipe-reactivate-${e.id}`}><RotateCcw className="h-4 w-4" />Réactiver</Button>
          ) : (
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEdit(e)} title="Modifier" data-testid={`equipe-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-[#D64545]" title="Désactiver" data-testid={`equipe-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Désactiver « {e.nom} » ?</AlertDialogTitle>
                    <AlertDialogDescription>L'équipe ne sera plus proposée. Elle rejoint la section « Inactifs » (réactivable à tout moment). Les lignes existantes conservent leur référence.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel data-testid={`equipe-delete-cancel-${e.id}`}>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={() => remove(e)} className="bg-[#D64545] hover:bg-[#C63D3D]" data-testid={`equipe-delete-confirm-${e.id}`}>Désactiver</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>
    );
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une équipe…" className="h-9 w-64 pl-8" data-testid="equipe-search" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-ink" data-testid="equipe-count">{activeRows.length} équipe(s)</span>
          <Button size="sm" onClick={() => setEdit(null)} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="equipe-new"><Plus className="mr-1.5 h-4 w-4" />Nouvelle équipe</Button>
        </div>
      </div>
      <ListCard>
        {activeRows.length === 0 && <div className="p-6 text-center text-xs text-muted-ink">Aucune équipe active.</div>}
        {activeRows.map((e) => renderEquipeRow(e, false))}
      </ListCard>
      <InactiveSection count={inactiveRows.length}>
        {inactiveRows.map((e) => renderEquipeRow(e, true))}
      </InactiveSection>
      <EquipeEditDialog equipe={edit} chefs={chefs} cdps={cdps} assocs={assocs} open={edit !== undefined} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); load(); }} />
    </div>
  );
}

const NONE = "__none__";
function EquipeEditDialog({ equipe, chefs, cdps, assocs, open, onClose, onSaved }) {
  const isEdit = !!equipe;
  const [form, setForm] = useState({ nom: "", chef_equipe_id: NONE, cdp_id: NONE, association_id: NONE });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(equipe
      ? { nom: equipe.nom || "", chef_equipe_id: equipe.chef_equipe_id || NONE, cdp_id: equipe.cdp_id || NONE, association_id: equipe.association_id || NONE }
      : { nom: "", chef_equipe_id: NONE, cdp_id: NONE, association_id: NONE });
  }, [open, equipe]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.nom.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      const clean = { nom: form.nom,
        chef_equipe_id: form.chef_equipe_id === NONE ? null : form.chef_equipe_id,
        cdp_id: form.cdp_id === NONE ? null : form.cdp_id,
        association_id: form.association_id === NONE ? null : form.association_id };
      const payload = isEdit ? { ...equipe, ...clean } : clean;
      await api.post("/admin/equipes", payload);
      toast.success(isEdit ? "Équipe mise à jour" : "Équipe créée");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="equipe-edit-dialog">
        <DialogHeader><DialogTitle className="text-base">{isEdit ? "Modifier l'équipe" : "Nouvelle équipe"}</DialogTitle>
          <DialogDescription>Une équipe terrain rattachée à un chef d'équipe et à un CDP titulaire.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-[11px]">Nom *</Label><Input value={form.nom} onChange={(e) => set("nom", e.target.value)} className="mt-1 h-9" data-testid="equipe-form-nom" /></div>
          <div><Label className="text-[11px]">Chef d'équipe</Label>
            <Select value={form.chef_equipe_id} onValueChange={(v) => set("chef_equipe_id", v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="equipe-form-chef"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value={NONE}>— Aucun —</SelectItem>{chefs.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px]">CDP titulaire</Label>
              <Select value={form.cdp_id} onValueChange={(v) => set("cdp_id", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="equipe-form-cdp"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>— Aucun —</SelectItem>{cdps.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-[11px]">Association</Label>
              <Select value={form.association_id} onValueChange={(v) => set("association_id", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="equipe-form-assoc"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>— Aucune —</SelectItem>{assocs.map((a) => <SelectItem key={a.id} value={a.id}>{a.acronym}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="equipe-form-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="equipe-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PerimetresPanel() {
  const [rows, setRows] = useState(null);
  const [assocs, setAssocs] = useState([]);
  const [canaux, setCanaux] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(undefined);
  const load = () => api.get("/perimetres").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/associations").then((r) => setAssocs(r.data));
    api.get("/canaux").then((r) => setCanaux(r.data));
  }, []);
  if (!rows) return <Loading />;
  const byId = (arr, id) => arr.find((x) => x.id === id);
  const filtered = rows.filter((p) => !q || `${p.nom} ${p.zones_label || ""}`.toLowerCase().includes(q.toLowerCase()));
  const remove = async (p) => { await api.delete(`/admin/perimetres/${p.id}`); toast.success("Périmètre désactivé"); load(); };
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3 text-xs text-muted-ink">
        Un <span className="font-semibold text-ink">périmètre</span> combine un canal et une zone géographique (ex : « Sites privés Paris &amp; IDF »). Il sert de clé pour la résolution automatique des codes campagne (Association + Périmètre + Date).
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un périmètre…" className="h-9 w-64 pl-8" data-testid="perim-search" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-ink" data-testid="perim-count">{filtered.length} périmètre(s)</span>
          <Button size="sm" onClick={() => setEdit(null)} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="perim-new"><Plus className="mr-1.5 h-4 w-4" />Nouveau périmètre</Button>
        </div>
      </div>
      <ListCard>
        {filtered.length === 0 && <div className="p-6 text-center text-xs text-muted-ink">Aucun périmètre.</div>}
        {filtered.map((p) => {
          const assoc = byId(assocs, p.association_id);
          const canal = byId(canaux, p.canal_id);
          return (
            <div key={p.id} className="flex items-center justify-between gap-3 border-b border-[var(--border-soft)] p-3 last:border-0 hover:bg-[var(--surface-2)]" data-testid={`perim-row-${p.id}`}>
              <div className="flex min-w-0 items-center gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--surface-2)]"><Compass className="h-4 w-4 text-[#0F5A2F]" /></span>
                <div className="min-w-0">
                  <div className="truncate font-bold">{p.nom}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-ink">
                    {assoc && <Badge variant="secondary" className="bg-[var(--surface-2)] text-[10px]">{assoc.acronym}</Badge>}
                    {canal && <span>{canal.name}</span>}
                    {p.departments?.length > 0 && <span>Dépts : {p.departments.join(", ")}</span>}
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEdit(p)} title="Modifier" data-testid={`perim-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-[#D64545]" title="Désactiver" data-testid={`perim-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Désactiver « {p.nom} » ?</AlertDialogTitle>
                      <AlertDialogDescription>Le périmètre ne sera plus proposé. Les lignes/codes existants conservent leur référence.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel data-testid={`perim-delete-cancel-${p.id}`}>Annuler</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(p)} className="bg-[#D64545] hover:bg-[#C63D3D]" data-testid={`perim-delete-confirm-${p.id}`}>Désactiver</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          );
        })}
      </ListCard>
      <PerimetreEditDialog perimetre={edit} assocs={assocs} canaux={canaux} open={edit !== undefined} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); load(); }} />
    </div>
  );
}

function PerimetreEditDialog({ perimetre, assocs, canaux, open, onClose, onSaved }) {
  const isEdit = !!perimetre;
  const [form, setForm] = useState({ nom: "", association_id: NONE, canal_id: NONE, zones_label: "", departments: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(perimetre
      ? { nom: perimetre.nom || "", association_id: perimetre.association_id || NONE, canal_id: perimetre.canal_id || NONE, zones_label: perimetre.zones_label || "", departments: (perimetre.departments || []).join(", ") }
      : { nom: "", association_id: NONE, canal_id: NONE, zones_label: "", departments: "" });
  }, [open, perimetre]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.nom.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      const clean = { nom: form.nom, zones_label: form.zones_label || null,
        association_id: form.association_id === NONE ? null : form.association_id,
        canal_id: form.canal_id === NONE ? null : form.canal_id,
        departments: form.departments.split(",").map((d) => d.trim()).filter(Boolean) };
      const payload = isEdit ? { ...perimetre, ...clean } : clean;
      await api.post("/admin/perimetres", payload);
      toast.success(isEdit ? "Périmètre mis à jour" : "Périmètre créé");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="perim-edit-dialog">
        <DialogHeader><DialogTitle className="text-base">{isEdit ? "Modifier le périmètre" : "Nouveau périmètre"}</DialogTitle>
          <DialogDescription>Colonne des tableaux de codes campagne (canal + zone géographique).</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-[11px]">Nom *</Label><Input value={form.nom} onChange={(e) => set("nom", e.target.value)} placeholder="Sites privés Paris & IDF" className="mt-1 h-9" data-testid="perim-form-nom" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px]">Association</Label>
              <Select value={form.association_id} onValueChange={(v) => set("association_id", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="perim-form-assoc"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>— Aucune —</SelectItem>{assocs.map((a) => <SelectItem key={a.id} value={a.id}>{a.acronym}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-[11px]">Canal</Label>
              <Select value={form.canal_id} onValueChange={(v) => set("canal_id", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="perim-form-canal"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>— Aucun —</SelectItem>{canaux.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label className="text-[11px]">Libellé zones (legacy)</Label><Input value={form.zones_label} onChange={(e) => set("zones_label", e.target.value)} placeholder="93AIF, 78AIF" className="mt-1 h-9" data-testid="perim-form-zones" /></div>
          <div><Label className="text-[11px]">Départements (séparés par des virgules)</Label><Input value={form.departments} onChange={(e) => set("departments", e.target.value)} placeholder="75, 92, 93" className="mt-1 h-9" data-testid="perim-form-depts" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="perim-form-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="perim-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RdPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [statutFilter, setStatutFilter] = useState("all");
  const [edit, setEdit] = useState(undefined); // undefined=closed, null=create, obj=edit
  const [merge, setMerge] = useState(null);

  const load = () => api.get("/rds/overview").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;

  const filtered = data.rds.filter((rd) => {
    if (statutFilter === "a_completer" && rd.statut !== "a_completer") return false;
    if (statutFilter === "actif" && rd.statut === "a_completer") return false;
    if (q) { const s = `${rd.name} ${rd.code || ""}`.toLowerCase(); if (!s.includes(q.toLowerCase())) return false; }
    return true;
  });
  const shown = filtered.slice(0, 80);
  const aCompleter = data.rds.filter((r) => r.statut === "a_completer").length;

  const remove = async (rd) => {
    const { data: res } = await api.delete(`/rds/${rd.id}`);
    toast.success(res.orphan_bulletins ? `RD supprimé (${res.orphan_bulletins} bulletins orphelins)` : "RD supprimé");
    load();
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un RD…" className="h-9 w-64 pl-8" data-testid="rd-search" />
        </div>
        <Select value={statutFilter} onValueChange={setStatutFilter}>
          <SelectTrigger className="h-9 w-44" data-testid="rd-statut-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les RD</SelectItem>
            <SelectItem value="actif">Actifs</SelectItem>
            <SelectItem value="a_completer">À compléter ({aCompleter})</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-ink" data-testid="rd-count">{filtered.length} RD</span>
          <Button size="sm" onClick={() => setEdit(null)} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="rd-new"><Plus className="mr-1.5 h-4 w-4" />Nouveau RD</Button>
        </div>
      </div>

      <ListCard>
        {shown.length === 0 && <div className="p-6 text-center text-xs text-muted-ink">Aucun RD.</div>}
        {shown.map((rd) => (
          <div key={rd.id} className="flex items-center justify-between gap-3 border-b border-[var(--border-soft)] p-3 last:border-0 hover:bg-[var(--surface-2)]" data-testid={`rd-row-${rd.id}`}>
            <button onClick={() => navigate(`/rds/${rd.id}`)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--surface-2)] text-xs font-bold">{(rd.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</span>
              <div className="min-w-0 flex-1">
                <div className="font-bold leading-tight break-words">{rd.name}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-ink">
                  {rd.code && <Badge variant="secondary" className="bg-[var(--surface-2)] text-[10px]">{rd.code}</Badge>}
                  <span>{rd.total_bulletins.toLocaleString("fr-FR")} bulletins · {Math.round(rd.montant_total)} €</span>
                  {rd.statut === "a_completer" && <Badge className="bg-[rgba(233,184,0,0.35)] text-[#5A4200] text-[10px]">à compléter</Badge>}
                  {rd.can_rebook && <Badge className="gap-1 bg-[rgba(238,114,46,0.14)] text-[10px] text-[#8A3A10]" data-testid={`rd-rebook-badge-${rd.id}`}><RefreshCw className="h-2.5 w-2.5" />Rebooking</Badge>}
                </div>
              </div>
            </button>
            <div className="flex shrink-0 items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEdit(rd)} title="Modifier" data-testid={`rd-edit-${rd.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMerge(rd)} title="Fusionner" data-testid={`rd-merge-${rd.id}`}><GitMerge className="h-4 w-4" /></Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[#D64545]" title="Supprimer" data-testid={`rd-delete-${rd.id}`}><Trash2 className="h-4 w-4" /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer « {rd.name} » ?</AlertDialogTitle>
                    <AlertDialogDescription>Les {rd.total_bulletins} bulletins liés seront conservés mais sans RD. Pour un doublon, préférez la fusion.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`rd-delete-cancel-${rd.id}`}>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={() => remove(rd)} className="bg-[#D64545] hover:bg-[#C63D3D]" data-testid={`rd-delete-confirm-${rd.id}`}>Supprimer</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
        {filtered.length > shown.length && <div className="p-3 text-center text-[11px] text-muted-ink">{filtered.length - shown.length} RD supplémentaires — affinez la recherche.</div>}
      </ListCard>

      <RdEditDialog rd={edit} open={edit !== undefined} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); load(); }} />
      <RdMergeDialog source={merge} open={!!merge} onClose={() => setMerge(null)} onMerged={() => { setMerge(null); load(); }} />
    </div>
  );
}

function RdEditDialog({ rd, open, onClose, onSaved }) {
  const isEdit = !!rd;
  const [form, setForm] = useState({ name: "", code: "", phone: "", email: "", statut: "actif", can_rebook: false });
  const [dispo, setDispo] = useState(emptyDispo());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(rd
        ? { name: rd.name || "", code: rd.code || "", phone: rd.phone || "", email: rd.email || "", statut: rd.statut || "actif", can_rebook: !!rd.can_rebook }
        : { name: "", code: "", phone: "", email: "", statut: "actif", can_rebook: false });
      setDispo((rd && rd.disponibilites) ? rd.disponibilites : emptyDispo());
    }
  }, [open, rd]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/rds/${rd.id}`, form);
        await api.put(`/rds/${rd.id}/disponibilites`, dispo);
      } else {
        await api.post("/rds", form);
      }
      toast.success(isEdit ? "RD mis à jour" : "RD créé");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="rd-edit-dialog">
        <DialogHeader><DialogTitle className="text-base">{isEdit ? "Modifier le RD" : "Nouveau RD"}</DialogTitle>
          <DialogDescription>Fiche recruteur (RD). Le statut « à compléter » signale un RD auto-créé à enrichir.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px]">Nom complet *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 h-9" data-testid="rd-form-name" /></div>
            <div><Label className="text-[11px]">Code recruteur</Label><Input value={form.code} onChange={(e) => set("code", e.target.value)} className="mt-1 h-9" data-testid="rd-form-code" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px]">Téléphone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1 h-9" data-testid="rd-form-phone" /></div>
            <div><Label className="text-[11px]">Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 h-9" data-testid="rd-form-email" /></div>
          </div>
          <div><Label className="text-[11px]">Statut</Label>
            <Select value={form.statut} onValueChange={(v) => set("statut", v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="rd-form-statut"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="actif">Actif</SelectItem><SelectItem value="a_completer">À compléter</SelectItem></SelectContent>
            </Select></div>
          <div className="flex items-start justify-between gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-[13px] font-bold"><RefreshCw className="h-3.5 w-3.5 text-[var(--shariti-orange)]" /> Autoriser le rebooking</div>
              <p className="mt-0.5 text-[11px] leading-snug text-muted-ink">Permet à ce RD de rebooker un spot sur le terrain depuis son espace (prime 50 €/spot). Désactivé par défaut.</p>
            </div>
            <Switch checked={!!form.can_rebook} onCheckedChange={(v) => set("can_rebook", v)} data-testid="rd-form-can-rebook" />
          </div>
          {isEdit && (
            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3">
              <div className="mb-1 flex items-center gap-1.5 text-[13px] font-bold"><CalendarClock className="h-3.5 w-3.5 text-[var(--shariti-orange)]" /> Disponibilités hebdomadaires</div>
              <p className="mb-2 text-[11px] leading-snug text-muted-ink">Ce que le collaborateur a annoncé (ex. « lundi 16h–20h »). Sert de rappel visuel dans l'Assistant de placement au moment de le programmer.</p>
              <DispoEditor value={dispo} onChange={setDispo} compact />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="rd-form-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="rd-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RdMergeDialog({ source, open, onClose, onMerged }) {
  const [target, setTarget] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [pickOpen, setPickOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  useEffect(() => { if (open) { setTarget(null); setQ(""); } }, [open]);
  useEffect(() => {
    if (!pickOpen) return;
    const t = setTimeout(async () => { const { data } = await api.get("/rds", { params: { search: q || undefined, limit: 20 } }); setResults(data.filter((r) => r.id !== source?.id)); }, 250);
    return () => clearTimeout(t);
  }, [q, pickOpen, source]);
  const confirm = async () => {
    if (!target) return;
    setMerging(true);
    try {
      const { data } = await api.post("/rds/merge", { source_id: source.id, target_id: target.id });
      toast.success(`Fusion effectuée (${data.bulletins_reassigned} bulletins réattribués)`);
      onMerged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de fusion"); }
    finally { setMerging(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="rd-merge-dialog">
        <DialogHeader><DialogTitle className="text-base">Fusionner un doublon</DialogTitle>
          <DialogDescription>Réattribue tous les bulletins et présences de « {source?.name} » vers le RD cible, puis supprime « {source?.name} ».</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <Label className="text-[11px]">RD cible (conservé)</Label>
          <Popover open={pickOpen} onOpenChange={setPickOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start border-[var(--border-soft)]" data-testid="rd-merge-target"><Search className="mr-1.5 h-3.5 w-3.5" /><span className="truncate text-xs">{target ? `${target.name}${target.code ? ` (${target.code})` : ""}` : "Choisir le RD cible…"}</span></Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput placeholder="Rechercher le RD cible…" value={q} onValueChange={setQ} data-testid="rd-merge-search" />
                <CommandList><CommandEmpty>Aucun RD.</CommandEmpty><CommandGroup>
                  {results.map((rd) => (
                    <CommandItem key={rd.id} value={rd.id} onSelect={() => { setTarget(rd); setPickOpen(false); }} data-testid={`rd-merge-opt-${rd.id}`}>
                      <span className="text-xs font-semibold">{rd.name}</span>{rd.code && <span className="ml-2 text-[10px] text-muted-ink">{rd.code}</span>}
                    </CommandItem>
                  ))}
                </CommandGroup></CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="rd-merge-cancel">Annuler</Button>
          <Button onClick={confirm} disabled={!target || merging} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="rd-merge-confirm">
            {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : "Fusionner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SmtpPanel() {
  const [cfg, setCfg] = useState(null);
  const [password, setPassword] = useState("");
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testFeedback, setTestFeedback] = useState(null); // {ok, text}
  const load = () => api.get("/admin/smtp").then((r) => setCfg(r.data));
  useEffect(() => { load(); }, []);
  if (!cfg) return <Loading />;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    if (cfg.enabled && (!cfg.host || !cfg.sender_email)) { toast.error("Hôte et email expéditeur requis pour activer l'envoi"); return; }
    setSaving(true);
    try {
      const payload = { ...cfg }; delete payload.has_password; delete payload.configured;
      if (password) payload.password = password; else payload.password = "";
      const { data } = await api.post("/admin/smtp", payload);
      setCfg(data); setPassword("");
      toast.success("Configuration SMTP enregistrée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const test = async (forceProvider) => {
    if (!testTo) { toast.error("Saisissez une adresse de test"); return; }
    setTesting(true);
    setTestFeedback(null);
    try {
      const { data } = await api.post("/admin/smtp/test", { to: testTo, ...(forceProvider ? { provider: forceProvider } : {}) });
      const pn = data?.provider_name ? ` (${data.provider_name})` : "";
      setTestFeedback({ ok: true, text: `Email de test${pn} envoyé à ${testTo}. Vérifiez la boîte de réception.` });
      toast.success(`Email de test${pn} envoyé à ${testTo}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Échec du test (réponse inattendue du serveur).";
      setTestFeedback({ ok: false, text: msg });
      toast.error("Échec du test — voir le détail ci-dessous.");
    } finally { setTesting(false); }
  };

  return (
    <div className="max-w-2xl space-y-4" data-testid="smtp-panel">
      <div className="flex items-center gap-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-xs text-muted-ink">
        <Mail className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
        <span>Tant que l'envoi n'est pas activé et configuré, les emails restent journalisés dans l'<span className="font-semibold text-ink">Outbox</span> (aucun envoi réel).</span>
      </div>

      {/* Choix du fournisseur d'envoi */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <h3 className="mb-1 text-sm font-bold">Fournisseur d'envoi</h3>
        <p className="mb-3 text-xs text-muted-ink">
          Choisissez par quel canal partent les e-mails. <span className="font-semibold text-ink">Resend</span> utilise une API HTTPS,
          insensible aux blocages d'adresse IP des serveurs SMTP (recommandé pour les envois automatiques).
        </p>
        <div className="grid gap-3 @2xl/page:grid-cols-2">
          <div>
            <Label className="text-[11px]">Canal d'envoi</Label>
            <Select value={cfg.provider || "auto"} onValueChange={(v) => set("provider", v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="email-provider"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automatique (SMTP si configuré, sinon Resend)</SelectItem>
                <SelectItem value="smtp">Infomaniak (SMTP)</SelectItem>
                <SelectItem value="resend">Resend (API HTTPS)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <div className={`w-full rounded-xl border px-3 py-2 text-[12px] ${cfg.resend_configured
              ? "border-[rgba(31,157,85,0.35)] bg-[rgba(31,157,85,0.08)] text-[#0F5A2F]"
              : "border-[var(--border-soft)] bg-[var(--surface-2)] text-muted-ink"}`} data-testid="resend-status">
              {cfg.resend_configured
                ? <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 shrink-0" /> Resend configuré · expéditeur <span className="font-semibold">{cfg.resend_sender}</span></span>
                : <span className="flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 shrink-0" /> Resend non configuré (clé API à fournir à l'équipe technique)</span>}
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={save} disabled={saving} variant="outline" className="border-[var(--border-soft)]" data-testid="email-provider-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" />Enregistrer le canal</>}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div><h3 className="text-sm font-bold">Serveur SMTP</h3><p className="text-xs text-muted-ink">Identifiants de votre serveur d'envoi (fournis par votre hébergeur email).</p></div>
          <div className="flex items-center gap-2">
            <Label htmlFor="smtp-enabled" className="text-xs">Envoi réel</Label>
            <Switch id="smtp-enabled" checked={!!cfg.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="smtp-enabled" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 @2xl/page:grid-cols-2">
          <div className="@2xl/page:col-span-2"><Label className="text-[11px]">Hôte SMTP</Label><Input value={cfg.host || ""} onChange={(e) => set("host", e.target.value)} placeholder="smtp.gmail.com" className="mt-1 h-9" data-testid="smtp-host" /></div>
          <div><Label className="text-[11px]">Port</Label><Input type="number" value={cfg.port || ""} onChange={(e) => set("port", parseInt(e.target.value) || 0)} placeholder="587" className="mt-1 h-9" data-testid="smtp-port" /></div>
          <div><Label className="text-[11px]">Sécurité</Label>
            <Select value={cfg.security || "starttls"} onValueChange={(v) => set("security", v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="smtp-security"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="starttls">STARTTLS (587)</SelectItem>
                <SelectItem value="ssl">SSL/TLS (465)</SelectItem>
                <SelectItem value="none">Aucune</SelectItem>
              </SelectContent>
            </Select></div>
          <div><Label className="text-[11px]">Identifiant</Label><Input value={cfg.username || ""} onChange={(e) => set("username", e.target.value)} placeholder="contact@shariti.fr" className="mt-1 h-9" data-testid="smtp-username" /></div>
          <div><Label className="text-[11px]">Mot de passe</Label><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder={cfg.has_password ? "•••••••• (inchangé)" : "Mot de passe / clé d'application"} className="mt-1" inputClassName="h-9" data-testid="smtp-password" /></div>
          <div><Label className="text-[11px]">Email expéditeur</Label><Input value={cfg.sender_email || ""} onChange={(e) => set("sender_email", e.target.value)} placeholder="contact@shariti.fr" className="mt-1 h-9" data-testid="smtp-sender-email" /></div>
          <div><Label className="text-[11px]">Nom expéditeur</Label><Input value={cfg.sender_name || ""} onChange={(e) => set("sender_name", e.target.value)} placeholder="Shariti" className="mt-1 h-9" data-testid="smtp-sender-name" /></div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="smtp-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" />Enregistrer</>}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <h3 className="mb-1 flex items-center gap-2 text-sm font-bold"><Send className="h-4 w-4 text-[var(--shariti-orange)]" />Tester l'envoi</h3>
        <p className="mb-3 text-xs text-muted-ink">Envoie un email de test. Vous pouvez tester le canal sélectionné, ou forcer SMTP / Resend.</p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]"><Label className="text-[11px]">Adresse de test</Label><Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="vous@exemple.fr" className="mt-1 h-9" data-testid="smtp-test-to" /></div>
          <Button variant="outline" onClick={() => test()} disabled={testing} className="border-[var(--border-soft)]" data-testid="smtp-test-btn">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" />Tester le canal choisi</>}
          </Button>
          <Button variant="ghost" onClick={() => test("smtp")} disabled={testing} className="text-muted-ink" data-testid="smtp-test-smtp-btn">SMTP</Button>
          <Button variant="ghost" onClick={() => test("resend")} disabled={testing} className="text-muted-ink" data-testid="smtp-test-resend-btn">Resend</Button>
        </div>
        {testFeedback && (
          <div className={`mt-3 flex items-start gap-2 rounded-xl border px-3 py-2.5 text-[12px] ${testFeedback.ok
            ? "border-[rgba(31,157,85,0.35)] bg-[rgba(31,157,85,0.08)] text-[#0F5A2F]"
            : "border-[rgba(214,69,69,0.35)] bg-[rgba(214,69,69,0.07)] text-[#7a2a2d]"}`}
            data-testid="smtp-test-feedback">
            {testFeedback.ok
              ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span className="leading-relaxed">{testFeedback.text}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function TelephoniePanel() {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [cons, setCons] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/calls/settings").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/calls/config").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/calls/consumption").then((r) => setCons(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!cfg) return <Loading />;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/calls/settings", cfg);
      setCfg(data);
      toast.success("Paramètres de téléphonie enregistrés");
      api.get("/calls/consumption").then((r) => setCons(r.data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const Line = ({ ok, label, value }) => (
    <div className="flex items-center gap-2 text-xs">
      <span className={`h-2 w-2 rounded-full ${ok ? "bg-[#1F9D55]" : "bg-[#D64545]"}`} />
      <span className="text-muted-ink">{label}</span>
      {value && <span className="font-semibold text-ink">{value}</span>}
    </div>
  );

  return (
    <div className="max-w-2xl space-y-4" data-testid="telephonie-panel">
      {/* Statut config */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-bold"><Phone className="h-4 w-4 text-[var(--shariti-orange)]" />État de la téléphonie</h3>
        {status ? (
          <div className="grid gap-2 @xl/page:grid-cols-2">
            <Line ok={status.account} label="Compte Twilio" />
            <Line ok={status.api_key} label="Clé API voix" />
            <Line ok={status.twiml_app} label="Application vocale (TwiML)" />
            <Line ok={status.ready} label="Numéro appelant" value={status.phone_number || (status.number_pending ? "en attente d'activation" : "aucun")} />
            <Line ok={status.transcription_ready} label="Transcription (Deepgram)"
              value={status.transcription_ready ? `Clé configurée ✓ …${status.transcription_key_hint || ""}` : "clé API manquante"} />
          </div>
        ) : <Loading />}
        {status && !status.transcription_ready && (
          <div className="mt-3 flex items-start gap-2 rounded-xl bg-[rgba(252,219,83,0.25)] px-3 py-2 text-[11px] text-[#5A4200]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>La transcription automatique nécessite une clé <span className="font-semibold">DEEPGRAM_API_KEY</span> (à fournir). Sans elle, l'analyse IA se base uniquement sur les notes du CDP.</span>
          </div>
        )}
      </div>

      {/* Consommation (mois en cours) */}
      <ConsumptionCard cons={cons} />

      {/* Objectifs de pilotage */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <h3 className="mb-1 flex items-center gap-2 text-sm font-bold"><Target className="h-4 w-4 text-[var(--shariti-orange)]" />Objectifs de pilotage</h3>
        <p className="mb-4 text-xs text-muted-ink">Objectifs quotidiens par défaut (surchargables par CDP). Utilisés par le cockpit « Pilotage » du Suivi CDP.</p>
        <div className="grid grid-cols-1 gap-3 @2xl/page:grid-cols-2">
          <div>
            <Label className="text-[11px]">Appels / jour (défaut)</Label>
            <Input type="number" value={cfg.calls_per_day_target ?? 50} onChange={(e) => set("calls_per_day_target", parseInt(e.target.value) || 0)}
              className="mt-1 h-9" data-testid="target-calls-day" />
          </div>
          <div>
            <Label className="text-[11px]">Spots décrochés / jour (défaut)</Label>
            <Input type="number" value={cfg.spots_per_day_target ?? 3} onChange={(e) => set("spots_per_day_target", parseInt(e.target.value) || 0)}
              className="mt-1 h-9" data-testid="target-spots-day" />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-[var(--surface-2)] px-3 py-2.5">
          <div className="flex items-center gap-2 text-xs">
            <Activity className="h-4 w-4 text-[var(--shariti-orange)]" />
            <span className="font-semibold">Objectifs proportionnels aux heures paramétrées du CDP</span>
          </div>
          <Switch checked={!!cfg.target_proportional_to_hours} onCheckedChange={(v) => set("target_proportional_to_hours", v)} data-testid="target-proportional" />
        </div>
        <p className="mt-1.5 text-[10px] text-muted-ink">Ex : un CDP à 7 h/j se voit assigner un objectif proportionnellement plus élevé qu'un CDP à 4 h/j.</p>

        <div className="mt-4 border-t border-[var(--border-soft)] pt-4">
          <div className="grid grid-cols-1 gap-3 @2xl/page:grid-cols-2">
            <div>
              <Label className="text-[11px]">Seuil mini d'appels (fiabilité statistique)</Label>
              <Input type="number" min="1" value={cfg.heatmap_min_sample ?? 5}
                onChange={(e) => {
                  const v = parseInt(e.target.value) || 1;
                  setCfg((c) => ({ ...c, heatmap_min_sample: v, pareto_trend_min_sample: v }));
                }}
                className="mt-1 h-9" data-testid="target-min-sample" />
            </div>
            <div className="flex items-end">
              <p className="text-[10px] text-muted-ink">En-dessous de ce volume, un créneau n'est pas recommandé (heatmap) et aucune tendance n'est affichée sur les causes de refus (Pareto). Défaut&nbsp;: 5 appels — évite les « 100 % sur 1 appel » trompeurs.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Enregistrement & annonce légale */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div><h3 className="text-sm font-bold">Enregistrement & conformité</h3><p className="text-xs text-muted-ink">En France, l'interlocuteur doit être informé de l'enregistrement (RGPD).</p></div>
          <div className="flex items-center gap-2">
            <Label htmlFor="rec-enabled" className="text-xs">Enregistrer</Label>
            <Switch id="rec-enabled" checked={!!cfg.recording_enabled} onCheckedChange={(v) => set("recording_enabled", v)} data-testid="rec-enabled" />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-xl bg-[var(--surface-2)] px-3 py-2.5">
          <div className="flex items-center gap-2 text-xs">
            <ShieldAlert className="h-4 w-4 text-[var(--shariti-orange)]" />
            <span className="font-semibold">Annonce légale au décrocher</span>
          </div>
          <Switch checked={!!cfg.notice_enabled} onCheckedChange={(v) => set("notice_enabled", v)} data-testid="notice-enabled" />
        </div>
        <div className="mt-3">
          <Label className="text-[11px]">Message d'annonce (lu au prospect)</Label>
          <Textarea value={cfg.recording_notice || ""} onChange={(e) => set("recording_notice", e.target.value)}
            className="mt-1 min-h-[70px] text-sm" data-testid="notice-text" />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 @2xl/page:grid-cols-2">
          <div>
            <Label className="text-[11px]">Durée de conservation (jours)</Label>
            <Input type="number" value={cfg.retention_days ?? 180} onChange={(e) => set("retention_days", parseInt(e.target.value) || 0)}
              className="mt-1 h-9" data-testid="retention-days" />
            <p className="mt-1 text-[10px] text-muted-ink">Purge automatique quotidienne au-delà de ce délai. Défaut : 180 j (6 mois).</p>
          </div>
          <div>
            <Label className="text-[11px]">Budget mensuel d'alerte (€)</Label>
            <Input type="number" value={cfg.monthly_budget_eur ?? 50} onChange={(e) => set("monthly_budget_eur", parseFloat(e.target.value) || 0)}
              className="mt-1 h-9" data-testid="budget-eur" />
            <p className="mt-1 text-[10px] text-muted-ink">Une alerte apparaît dans le cockpit et le tableau de bord dès 80 % du budget.</p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 @2xl/page:grid-cols-2">
          <div>
            <Label className="text-[11px]">Coût Twilio / minute (€)</Label>
            <Input type="number" step="0.001" value={cfg.twilio_cost_per_min_eur ?? 0.15} onChange={(e) => set("twilio_cost_per_min_eur", parseFloat(e.target.value) || 0)}
              className="mt-1 h-9" data-testid="cost-twilio" />
          </div>
          <div>
            <Label className="text-[11px]">Coût transcription / minute (€)</Label>
            <Input type="number" step="0.0001" value={cfg.deepgram_cost_per_min_eur ?? 0.0045} onChange={(e) => set("deepgram_cost_per_min_eur", parseFloat(e.target.value) || 0)}
              className="mt-1 h-9" data-testid="cost-deepgram" />
          </div>
        </div>
      </div>

      {/* Automatisations IA */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <h3 className="mb-3 text-sm font-bold">Automatisations</h3>
        <div className="space-y-2.5">
          <div className="flex items-center justify-between gap-3 rounded-xl bg-[var(--surface-2)] px-3 py-2.5">
            <span className="text-xs font-semibold">Transcrire automatiquement chaque appel</span>
            <Switch checked={!!cfg.auto_transcribe} onCheckedChange={(v) => set("auto_transcribe", v)} data-testid="auto-transcribe" />
          </div>
          <div className="flex items-center justify-between gap-3 rounded-xl bg-[var(--surface-2)] px-3 py-2.5">
            <span className="text-xs font-semibold">Lancer l'analyse IA automatiquement (transcription + notes)</span>
            <Switch checked={!!cfg.auto_analyze} onCheckedChange={(v) => set("auto_analyze", v)} data-testid="auto-analyze" />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="telephonie-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" />Enregistrer</>}
        </Button>
      </div>
    </div>
  );
}

function ConsumptionCard({ cons }) {
  if (!cons) return null;
  const metrics = [
    { icon: PhoneCall, label: "Appels", value: cons.calls, sub: `${cons.reached} aboutis` },
    { icon: Clock, label: "Minutes d'appel", value: cons.call_minutes, sub: `${cons.transcribed_minutes} min transcrites` },
    { icon: Wallet, label: "Coût du mois", value: `${cons.total_cost} €`, sub: `Twilio ${cons.twilio_cost} € · STT ${cons.deepgram_cost} €` },
    { icon: Target, label: "Coût moyen / appel", value: `${cons.avg_cost_per_call} €`, sub: cons.cost_per_spot != null ? `${cons.cost_per_spot} € / spot décroché` : "—" },
  ];
  const pct = Math.min(100, cons.budget_pct || 0);
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5" data-testid="consumption-card">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-bold"><Wallet className="h-4 w-4 text-[var(--shariti-orange)]" />Consommation — mois {cons.month}</h3>
        {cons.data_mode === "demo" && <span className="rounded-full bg-[rgba(252,219,83,0.5)] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-[#5A4200]" data-testid="consumption-demo">Démo</span>}
      </div>
      <div className="grid grid-cols-2 gap-3 @2xl/page:grid-cols-4">
        {metrics.map((m) => (
          <div key={m.label} className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid={`consumption-${m.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-ink"><m.icon className="h-3.5 w-3.5" />{m.label}</div>
            <div className="mt-1 text-lg font-extrabold tabular-nums leading-none">{m.value}</div>
            <div className="mt-1 text-[10px] text-muted-ink">{m.sub}</div>
          </div>
        ))}
      </div>
      {cons.budget > 0 && (
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-[11px] font-semibold">
            <span className="text-muted-ink">Budget mensuel</span>
            <span className={cons.over_budget ? "text-[#C0392B]" : "text-ink"}>{cons.total_cost} € / {cons.budget} € ({cons.budget_pct}%)</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-[var(--surface-2)]">
            <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: cons.over_budget ? "#C0392B" : cons.budget_pct >= 80 ? "#EE722E" : "#1F9D55" }} />
          </div>
          {cons.over_budget && (
            <p className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-[#C0392B]"><AlertTriangle className="h-3.5 w-3.5" />Budget dépassé ce mois-ci.</p>
          )}
        </div>
      )}
    </div>
  );
}

const MANDAT_STATUS_UI = {
  expired: { label: "Expiré", cls: "bg-[rgba(214,69,69,0.14)] text-[#B23333]" },
  expiring_soon: { label: "Expire bientôt", cls: "bg-[rgba(233,184,0,0.22)] text-[#7A5A00]" },
  valid: { label: "Valide", cls: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]" },
  missing: { label: "Manquant", cls: "bg-[rgba(214,69,69,0.14)] text-[#B23333]" },
};
const fmtMandatDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

function MandatsPanel() {
  const [mandats, setMandats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [assocs, setAssocs] = useState([]);
  const [uploadAssoc, setUploadAssoc] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingRef, setLoadingRef] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);
  const [requesting, setRequesting] = useState("");
  const fileRef = useRef(null);
  const load = () => {
    api.get("/mandats").then((r) => setMandats(r.data));
    api.get("/mandats/alerts").then((r) => setAlerts(r.data || [])).catch(() => setAlerts([]));
  };
  useEffect(() => {
    load();
    api.get("/associations").then((r) => { setAssocs(r.data); setUploadAssoc(r.data[0]?.id || ""); });
  }, []);
  if (!mandats) return <Loading />;

  const loadReference = async () => {
    setLoadingRef(true);
    try {
      const { data } = await api.post("/admin/mandats/load-reference");
      toast.success(`${data.loaded.length} mandat(s) chargé(s), ${data.skipped.length} déjà présent(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoadingRef(false); }
  };
  const onFile = async (file) => {
    if (!file || !uploadAssoc) { toast.error("Choisissez une association"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("association_id", uploadAssoc);
      await api.post("/admin/mandats/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Mandat importé et activé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'import"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const activate = async (m) => { await api.post(`/admin/mandats/${m.id}/activate`); toast.success("Mandat activé"); load(); };
  const remove = async (m) => { await api.delete(`/admin/mandats/${m.id}`); toast.success("Mandat supprimé"); load(); };
  const download = async (m) => {
    try {
      const res = await api.get(`/mandats/${m.id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Téléchargement impossible"); }
  };
  const requestMandat = async (associationId) => {
    setRequesting(associationId);
    try {
      const { data } = await api.post(`/admin/mandats/request/${associationId}`, {});
      toast.success(`Demande de mandat envoyée à ${data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi"); }
    finally { setRequesting(""); }
  };
  const sendAlertsNow = async () => {
    setSendingAlert(true);
    try {
      const { data } = await api.post("/admin/mandats/alerts/send");
      if (data.sent > 0) toast.success(`Alerte envoyée à ${data.sent} administrateur(s)`);
      else toast.warning(data.reason || "Aucun email n'a pu être envoyé — vérifiez la configuration d'envoi ou les adresses des administrateurs.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi"); }
    finally { setSendingAlert(false); }
  };

  const byAssoc = assocs.map((a) => ({ assoc: a, items: mandats.filter((m) => m.association_id === a.id) }))
    .filter((g) => g.items.length > 0 || g.assoc.active !== false);

  return (
    <div className="space-y-4" data-testid="mandats-panel">
      {alerts.length > 0 && (
        <div className="rounded-2xl border border-[rgba(214,69,69,0.35)] bg-[rgba(214,69,69,0.06)] p-4" data-testid="mandats-alert-banner">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-bold text-[#B23333]">
              <ShieldAlert className="h-4 w-4" />
              {alerts.length} mandat{alerts.length > 1 ? "s" : ""} à renouveler
            </div>
            <Button size="sm" variant="outline" onClick={sendAlertsNow} disabled={sendingAlert}
              className="h-8 border-[rgba(214,69,69,0.4)] text-[#B23333] hover:bg-[rgba(214,69,69,0.08)]"
              data-testid="mandats-send-alert">
              {sendingAlert ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
              Envoyer l'alerte aux admins
            </Button>
          </div>
          <p className="mt-1 text-xs text-[rgba(24,24,24,0.7)]">
            Demandez un mandat à jour aux associations concernées, puis importez-le ci-dessous. Le nouveau mandat sera automatiquement joint aux emails de confirmation.
          </p>
          <div className="mt-2 space-y-1.5">
            {alerts.map((al, i) => {
              const ui = MANDAT_STATUS_UI[al.status] || MANDAT_STATUS_UI.valid;
              const detail = al.status === "missing" ? "aucun mandat actif"
                : al.status === "expired" ? `expiré le ${fmtMandatDate(al.valid_until)}`
                  : `expire le ${fmtMandatDate(al.valid_until)} · J-${al.days_left}`;
              return (
                <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white/70 px-2.5 py-1.5" data-testid={`mandat-alert-${al.association_acronym || i}`}>
                  <div className="flex items-center gap-2">
                    <Badge className={`${ui.cls} text-[11px] font-semibold`}>{al.association_acronym || "?"}</Badge>
                    <span className="text-xs text-[rgba(24,24,24,0.7)]">{detail}</span>
                  </div>
                  {al.association_id && (
                    <Button size="sm" variant="ghost" onClick={() => requestMandat(al.association_id)} disabled={requesting === al.association_id}
                      className="h-7 px-2 text-[11px] font-semibold text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.08)]"
                      data-testid={`mandat-request-${al.association_acronym || i}`}>
                      {requesting === al.association_id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Mail className="mr-1 h-3 w-3" />}
                      Demander un nouveau mandat
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--surface-2)]"><FileCheck2 className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-bold">Lettres de mandat</h3>
            <p className="mt-1 text-sm text-muted-ink">Le mandat <span className="font-semibold text-ink">actif</span> de chaque association est automatiquement joint aux emails de confirmation.</p>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <Button size="sm" onClick={loadReference} disabled={loadingRef} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="mandats-load-reference">
                {loadingRef ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="mr-1.5 h-4 w-4" />Charger les mandats de référence</>}
              </Button>
              <div className="flex items-end gap-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2">
                <div><Label className="text-[10px]">Association</Label>
                  <Select value={uploadAssoc} onValueChange={setUploadAssoc}>
                    <SelectTrigger className="mt-1 h-8 w-48" data-testid="mandats-upload-assoc"><SelectValue /></SelectTrigger>
                    <SelectContent>{assocs.map((a) => <SelectItem key={a.id} value={a.id}>{a.acronym} — {a.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <input ref={fileRef} type="file" accept=".pdf" className="hidden" data-testid="mandats-file-input" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="border-[var(--border-soft)] bg-white" data-testid="mandats-upload-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-1.5 h-4 w-4" />Importer un PDF</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {byAssoc.length === 0 && <ListCard><div className="p-6 text-center text-xs text-muted-ink">Aucune association.</div></ListCard>}
        {byAssoc.map(({ assoc, items }) => (
          <div key={assoc.id} className="rounded-2xl border border-[var(--border-soft)] bg-white p-4" data-testid={`mandats-assoc-${assoc.id}`}>
            <div className="mb-2 flex items-center gap-2">
              <Badge variant="secondary" className="bg-[var(--surface-2)] text-[11px] font-bold">{assoc.acronym}</Badge>
              <span className="text-sm font-bold">{assoc.name}</span>
              {items.length === 0 && <span className="ml-auto text-[11px] text-[#8A3A10]">Aucun mandat</span>}
            </div>
            {items.length > 0 && (
              <div className="divide-y divide-[var(--border-soft)]">
                {items.map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-3 py-2" data-testid={`mandat-row-${m.id}`}>
                    <div className="flex min-w-0 items-center gap-2">
                      <FileCheck2 className="h-4 w-4 shrink-0 text-muted-ink" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-semibold">{m.filename}</span>
                          {m.active && <Badge className="bg-[rgba(31,157,85,0.15)] text-[10px] text-[#0F5A2F]"><Star className="mr-0.5 h-2.5 w-2.5" />Actif</Badge>}
                          {m.active && m.status && (MANDAT_STATUS_UI[m.status] || null) && (
                            <Badge className={`${MANDAT_STATUS_UI[m.status].cls} text-[10px] font-semibold`} data-testid={`mandat-status-${m.id}`}>
                              {MANDAT_STATUS_UI[m.status].label}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] text-muted-ink">
                          <CalendarRange className="h-3 w-3 shrink-0" />
                          <span>Validité jusqu'au {fmtMandatDate(m.valid_until)}</span>
                          <span className="text-[rgba(24,24,24,0.35)]">·</span>
                          <span>{Math.round((m.size || 0) / 1024)} Ko · {m.source === "seed_reference" ? "référence" : "importé"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Aperçu" onClick={() => download(m)} data-testid={`mandat-download-${m.id}`}><Download className="h-4 w-4" /></Button>
                      {!m.active && <Button variant="ghost" size="icon" className="h-8 w-8 text-[#1F9D55]" title="Activer" onClick={() => activate(m)} data-testid={`mandat-activate-${m.id}`}><Star className="h-4 w-4" /></Button>}
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-[#D64545]" title="Supprimer" data-testid={`mandat-delete-${m.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Supprimer ce mandat ?</AlertDialogTitle>
                            <AlertDialogDescription>« {m.filename} » sera définitivement supprimé.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel data-testid={`mandat-delete-cancel-${m.id}`}>Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(m)} className="bg-[#D64545] hover:bg-[#C63D3D]" data-testid={`mandat-delete-confirm-${m.id}`}>Supprimer</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CodesPanel() {
  const [ov, setOv] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [assocFilter, setAssocFilter] = useState("all");
  const fileRef = useRef(null);
  const load = () => api.get("/codes-campagne/overview").then((r) => setOv(r.data));
  useEffect(() => { load(); }, []);

  const loadReference = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/codes-campagne/import/preview-url", {});
      setPreview(data);
      toast.success("Fichier analysé");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'analyse"); }
    finally { setBusy(false); }
  };
  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/codes-campagne/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success("Fichier analysé");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'analyse"); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post("/admin/codes-campagne/import/commit", { import_id: preview.import_id });
      toast.success(`${data.codes_created} codes importés (${data.associations_created} assos, ${data.perimetres_created} périmètres créés)`);
      setPreview(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'import"); }
    finally { setCommitting(false); }
  };

  if (!ov) return <Loading />;
  const rep = preview?.report;
  const filteredCodes = (ov.codes || []).filter((c) => assocFilter === "all" || c.association_id === assocFilter).slice(0, 200);

  return (
    <div className="space-y-4" data-testid="codes-panel">
      {/* Import */}
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--surface-2)]"><Barcode className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-bold">Import du référentiel des codes campagne</h3>
            <p className="mt-1 text-sm text-muted-ink">Le fichier Excel « GESTION DES CODES CAMPAGNES » (1 feuille par association) est analysé, les périmètres (colonnes) et validités multi-mois sont détectés automatiquement.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={loadReference} disabled={busy} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="codes-load-reference">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="mr-1.5 h-4 w-4" />Charger le fichier de référence</>}
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" data-testid="codes-file-input" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="border-[var(--border-soft)]" data-testid="codes-upload"><Upload className="mr-1.5 h-4 w-4" />Importer un fichier…</Button>
            </div>
          </div>
        </div>

        {rep && (
          <div className="mt-4 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-4" data-testid="codes-preview">
            <div className="grid grid-cols-2 gap-3 @2xl/page:grid-cols-4">
              {[["Associations", rep.associations_count], ["Périmètres", rep.perimetres_count], ["Codes détectés", rep.codes_count], ["Avertissements", rep.warnings.length]].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-[var(--border-soft)] bg-white p-3"><div className="text-lg font-extrabold leading-none">{v}</div><div className="text-[11px] text-muted-ink">{l}</div></div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {rep.sheets.filter((s) => s.association).map((s) => (
                <span key={s.sheet} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold" data-testid={`codes-sheet-${s.association}`}><CheckCircle2 className="h-3 w-3 text-[#1F9D55]" />{s.association} · {s.codes} codes / {s.perimetres} périm.</span>
              ))}
            </div>
            {rep.warnings.length > 0 && (
              <div className="mt-2 space-y-1">{rep.warnings.map((w, i) => (
                <div key={i} className="flex items-center gap-1.5 text-[11px] text-[#8A3A10]"><AlertTriangle className="h-3 w-3" />{w}</div>
              ))}</div>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setPreview(null)} data-testid="codes-cancel">Annuler</Button>
              <Button size="sm" onClick={commit} disabled={committing} className="bg-[#1F9D55] text-white hover:bg-[#17823F]" data-testid="codes-commit">
                {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-1.5 h-4 w-4" />Importer {rep.codes_count} codes</>}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Rotation automatique (génération des codes mensuels) */}
      <RotationTool associations={ov.associations} perimetres={ov.perimetres} onDone={load} />

      {/* Testeur de résolution */}
      <CodeResolver associations={ov.associations} perimetres={ov.perimetres} />

      {/* Liste des codes */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={assocFilter} onValueChange={setAssocFilter}>
            <SelectTrigger className="h-9 w-56" data-testid="codes-assoc-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les associations</SelectItem>
              {ov.associations.map((a) => <SelectItem key={a.id} value={a.id}>{a.acronym} — {a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="ml-auto text-xs text-muted-ink" data-testid="codes-total">{ov.total} codes au total</span>
        </div>
        <ListCard>
          {filteredCodes.length === 0 && <div className="p-6 text-center text-xs text-muted-ink">Aucun code. Importez le référentiel ci-dessus.</div>}
          {filteredCodes.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 border-b border-[var(--border-soft)] p-3 last:border-0" data-testid={`code-row-${c.id}`}>
              <div className="flex min-w-0 items-center gap-3">
                <Badge variant="secondary" className="bg-[var(--surface-2)] font-mono text-[11px] shrink-0">{c.code}</Badge>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{c.perimetre_nom || "—"}</div>
                  <div className="text-[11px] text-muted-ink">{c.association_acronym}{c.months_count ? ` · ${c.months_count} mois` : ""}</div>
                </div>
              </div>
              <div className="shrink-0 text-right text-[11px] text-muted-ink">
                {c.valid_from && c.valid_to ? <span>{frenchDate(c.valid_from)} → {frenchDate(c.valid_to)}</span> : (c.period || "")}
              </div>
            </div>
          ))}
          {ov.codes.filter((c) => assocFilter === "all" || c.association_id === assocFilter).length > filteredCodes.length &&
            <div className="p-3 text-center text-[11px] text-muted-ink">Affinez le filtre pour voir les codes restants.</div>}
        </ListCard>
      </div>
    </div>
  );
}

const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

function RotationTool({ associations, perimetres, onDone }) {
  const [aid, setAid] = useState("");
  const [pid, setPid] = useState("");
  const [base, setBase] = useState("");
  const now = new Date();
  const [startMonth, setStartMonth] = useState(String(now.getMonth() + 1));
  const [startYear, setStartYear] = useState(String(now.getFullYear()));
  const [months, setMonths] = useState("4");
  const [startLetter, setStartLetter] = useState("A");
  const [busy, setBusy] = useState(false);
  const perims = perimetres.filter((p) => p.association_id === aid);

  // Aperçu client : un code par mois avec lettre finale incrémentée.
  const buildPreview = () => {
    const out = [];
    const b = base.trim();
    const sm = parseInt(startMonth, 10), sy = parseInt(startYear, 10), n = parseInt(months, 10);
    const sl = (startLetter || "A").toUpperCase().charCodeAt(0);
    if (!b || !(sm >= 1 && sm <= 12) || !sy || !(n >= 1 && n <= 24) || sl < 65 || sl > 90) return out;
    let y = sy, m = sm;
    for (let i = 0; i < n; i++) {
      const oc = sl + i;
      if (oc > 90) break;
      out.push({ code: b + String.fromCharCode(oc), label: `${MONTHS_FR[m - 1]} ${y}` });
      m++; if (m > 12) { m = 1; y++; }
    }
    return out;
  };
  const preview = buildPreview();

  const generate = async () => {
    if (!aid || !base.trim()) { toast.error("Association et code de base requis."); return; }
    if (preview.length === 0) { toast.error("Paramètres invalides."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/codes-campagne/rotation", {
        association_id: aid, perimetre_id: pid || null, base_code: base.trim(),
        start_year: parseInt(startYear, 10), start_month: parseInt(startMonth, 10),
        months_count: parseInt(months, 10), start_letter: startLetter.toUpperCase(),
      });
      toast.success(`${data.count} code(s) généré(s) : la rotation mensuelle est active.`);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5" data-testid="rotation-tool">
      <h3 className="mb-1 flex items-center gap-2 text-sm font-bold"><RefreshCw className="h-4 w-4 text-[var(--shariti-orange)]" />Rotation automatique mensuelle</h3>
      <p className="mb-3 text-xs text-muted-ink">
        Génère un code par mois en incrémentant la dernière lettre (A → B → C…). Ex. base <span className="font-mono">26SFXEA507</span> à partir de septembre →
        <span className="font-mono"> 26SFXEA507A</span> (sept), <span className="font-mono">…B</span> (oct), <span className="font-mono">…C</span> (nov)… Le planning adopte le bon code à chaque mois automatiquement.
      </p>
      <div className="grid grid-cols-1 gap-3 @2xl/page:grid-cols-3">
        <div>
          <Label className="text-[11px]">Association *</Label>
          <Select value={aid} onValueChange={(v) => { setAid(v); setPid(""); }}>
            <SelectTrigger className="mt-1 h-9" data-testid="rotation-assoc"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>{associations.map((a) => <SelectItem key={a.id} value={a.id}>{a.acronym} — {a.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Périmètre</Label>
          <Select value={pid || "none"} onValueChange={(v) => setPid(v === "none" ? "" : v)} disabled={!aid}>
            <SelectTrigger className="mt-1 h-9" data-testid="rotation-perim"><SelectValue placeholder={aid ? "Tous / aucun" : "—"} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Sans périmètre</SelectItem>
              {perims.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Code de base *</Label>
          <Input value={base} onChange={(e) => setBase(e.target.value.toUpperCase())} placeholder="Ex. 26SFXEA507" className="mt-1 h-9 font-mono" data-testid="rotation-base" />
        </div>
        <div>
          <Label className="text-[11px]">Mois de départ</Label>
          <Select value={startMonth} onValueChange={setStartMonth}>
            <SelectTrigger className="mt-1 h-9" data-testid="rotation-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS_FR.map((mn, i) => <SelectItem key={i} value={String(i + 1)}>{mn}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Année</Label>
          <Input type="number" value={startYear} onChange={(e) => setStartYear(e.target.value)} className="mt-1 h-9" data-testid="rotation-year" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px]">Nb de mois</Label>
            <Input type="number" min="1" max="24" value={months} onChange={(e) => setMonths(e.target.value)} className="mt-1 h-9" data-testid="rotation-months" />
          </div>
          <div>
            <Label className="text-[11px]">Lettre de départ</Label>
            <Input maxLength={1} value={startLetter} onChange={(e) => setStartLetter(e.target.value.toUpperCase().slice(0, 1))} className="mt-1 h-9 uppercase" data-testid="rotation-letter" />
          </div>
        </div>
      </div>
      {preview.length > 0 && (
        <div className="mt-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="rotation-preview">
          <div className="mb-1.5 text-[11px] font-bold text-muted-ink">Aperçu ({preview.length} code{preview.length > 1 ? "s" : ""})</div>
          <div className="flex flex-wrap gap-1.5">
            {preview.map((p) => (
              <span key={p.code} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-soft)] bg-white px-2 py-0.5 text-[11px]">
                <span className="font-mono font-bold">{p.code}</span><span className="text-muted-ink">{p.label}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="mt-3 flex justify-end">
        <Button onClick={generate} disabled={busy || preview.length === 0} className="h-9 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="rotation-generate">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="mr-1.5 h-4 w-4" />Générer la rotation</>}
        </Button>
      </div>
    </div>
  );
}

function CodeResolver({ associations, perimetres }) {
  const [aid, setAid] = useState("");
  const [pid, setPid] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const perims = perimetres.filter((p) => p.association_id === aid);
  const resolve = async () => {
    if (!aid || !pid || !date) { toast.error("Association, périmètre et date requis"); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/codes-campagne/resolve", { params: { association_id: aid, perimetre_id: pid, date } });
      setResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5" data-testid="code-resolver">
      <h3 className="mb-1 flex items-center gap-2 text-sm font-bold"><Wand2 className="h-4 w-4 text-[var(--shariti-orange)]" />Testeur de résolution automatique</h3>
      <p className="mb-3 text-xs text-muted-ink">Vérifiez le code campagne résolu pour une combinaison Association + Périmètre + Date.</p>
      <div className="grid grid-cols-1 gap-3 @2xl/page:grid-cols-4">
        <div><Label className="text-[11px]">Association</Label>
          <Select value={aid} onValueChange={(v) => { setAid(v); setPid(""); setResult(undefined); }}>
            <SelectTrigger className="mt-1 h-9" data-testid="resolver-assoc"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>{associations.map((a) => <SelectItem key={a.id} value={a.id}>{a.acronym}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-[11px]">Périmètre</Label>
          <Select value={pid} onValueChange={(v) => { setPid(v); setResult(undefined); }} disabled={!aid}>
            <SelectTrigger className="mt-1 h-9" data-testid="resolver-perim"><SelectValue placeholder={aid ? "Choisir…" : "—"} /></SelectTrigger>
            <SelectContent>{perims.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-[11px]">Date</Label><Input type="date" value={date} onChange={(e) => { setDate(e.target.value); setResult(undefined); }} className="mt-1 h-9" data-testid="resolver-date" /></div>
        <div className="flex items-end"><Button onClick={resolve} disabled={busy} className="h-9 w-full bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="resolver-run">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Résoudre"}</Button></div>
      </div>
      {result !== undefined && (
        <div className={`mt-3 rounded-xl border p-3 ${result.resolved ? "border-[rgba(31,157,85,0.35)] bg-[rgba(31,157,85,0.08)]" : "border-[rgba(214,69,69,0.3)] bg-[rgba(214,69,69,0.06)]"}`} data-testid="resolver-result">
          {result.resolved
            ? <div className="flex items-center gap-2 text-sm font-semibold text-[#0F5A2F]"><CheckCircle2 className="h-4 w-4" /><span className="font-mono">{result.code}</span><span className="font-normal text-muted-ink">valide du {frenchDate(result.valid_from)} au {frenchDate(result.valid_to)}</span></div>
            : <div className="flex items-center gap-2 text-sm font-semibold text-[#9B2226]"><AlertTriangle className="h-4 w-4" />{result.message}</div>}
        </div>
      )}
    </div>
  );
}

const CDP_COLORS = ["#FCDB53", "#EE722E", "#2F6FED", "#1F9D55", "#8B5CF6", "#EC4899", "#14B8A6", "#F59E0B", "#EF4444", "#6366F1", "#0EA5E9", "#84CC16"];
// Jours de la semaine (0=lundi … 6=dimanche) pour le sélecteur de rythme CDP.
const WEEKDAYS = [["Lun", 0], ["Mar", 1], ["Mer", 2], ["Jeu", 3], ["Ven", 4], ["Sam", 5], ["Dim", 6]];
const fmtDateFr = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); } catch { return iso; } };
const ROLE_OPTS = [
  { v: "admin", l: "Administrateur", desc: "Accès complet à la plateforme" },
  { v: "cdp", l: "CDP", desc: "Chargé de développement partenarial (booking)" },
  { v: "chef", l: "Chef d'équipe", desc: "Consultation planning & diffusion" },
  { v: "terrain", l: "Responsable planning terrain", desc: "Gère uniquement les plannings terrain cochés ci-dessous. Aucun autre accès." },
  { v: "prospecteur", l: "Prospecteur terrain", desc: "Agent mobile, sans zone : voit tous les spots, en crée, pose des créneaux au planning, passe des appels et envoie des emails/négociations. Aide les CDP partout." },
  { v: "lecture_seule", l: "Lecture Seule", desc: "Consulte uniquement le planning. Chaque action de modification est autorisée au cas par cas ci-dessous." },
  { v: "superviseur", l: "Superviseur", desc: "Consultation SEULE. Voit un périmètre de pages restreint (dont « Suivi CDP » sans la facturation) et uniquement les données des CDP qui lui sont attribués ci-dessous. Ne peut effectuer aucune action." },
];

// Tableaux de planning terrain sélectionnables pour le rôle « Responsable planning terrain ».
const TERRAIN_BOARDS = [
  { k: "board_pap", l: "Planning porte-à-porte", d: "Villes & équipes en porte-à-porte." },
  { k: "board_rue_itinerance", l: "Planning rue — itinérance", d: "Missions rue hors Île-de-France (2–3 jours par ville)." },
  { k: "board_rue_fixe", l: "Planning rue — fixe", d: "Missions rue où l'équipe réside dans la ville (Paris & IDF)." },
];

// Permissions granulaires du rôle « Lecture Seule » (toutes désactivées par défaut).
const VIEWER_PERMISSIONS = [
  { k: "edit_spots", l: "Ajouter / retirer un spot", d: "Placer ou supprimer un créneau dans une cellule." },
  { k: "drag_drop", l: "Glisser-déposer", d: "Déplacer un créneau et réorganiser les lignes." },
  { k: "manage_lines", l: "Créer / retirer une ligne", d: "Ajouter une nouvelle ligne ou la retirer d'une semaine." },
  { k: "edit_period", l: "Modifier la période d'une ligne", d: "Changer la semaine de début / de fin d'une ligne." },
  { k: "diffuse", l: "Diffuser / relier un chef", d: "Valider la semaine et relier une fiche chef d'équipe." },
  { k: "declare_coordination", l: "Déclarer à la coordination", d: "Marquer un créneau comme déclaré à la coordination et accéder au suivi dédié (chargée des opérations)." },
  { k: "manage_equipe", l: "Gérer l'équipe du jour", d: "Ouvrir le bouton « Équipe » d'un créneau pour ajouter ou retirer des personnes (affectations du jour)." },
  { k: "manage_teams", l: "Gérer les équipes", d: "Page Équipes : créer / renommer / retirer une équipe, affecter les recruteurs (départs, mutations), gérer présence et photos." },
  { k: "broadcast", l: "Diffuser des annonces", d: "Page Annonces : composer et diffuser un message à une association (pop-up dans l'espace des collaborateurs), et consulter les statistiques de lecture." },
];

function UsersPanel() {
  const [rows, setRows] = useState(null);
  const [zones, setZones] = useState([]);
  const [assocMap, setAssocMap] = useState({});
  const [edit, setEdit] = useState(undefined); // undefined = fermé, null = création, obj = édition
  const [delTarget, setDelTarget] = useState(null);     // suppression définitive
  const [deacTarget, setDeacTarget] = useState(null);   // désactivation (réversible)
  const [filter, setFilter] = useState("actifs");       // actifs | desactives | tous
  const [busyId, setBusyId] = useState(null);

  // On charge TOUS les comptes (actifs + désactivés) pour pouvoir les filtrer.
  const load = () => api.get("/admin/users").then((r) => setRows(r.data));
  const loadZones = () => api.get("/admin/zones/overview").then((r) => setZones(r.data.zones || [])).catch(() => setZones([]));
  useEffect(() => {
    load(); loadZones();
    api.get("/associations").then((r) => {
      const m = {}; (r.data || []).forEach((a) => { m[a.id] = a; }); setAssocMap(m);
    }).catch(() => {});
  }, []);

  const isActive = (u) => u.active !== false;

  const toggleActive = async (u, active) => {
    if (!u) return;
    setBusyId(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/active`, { active });
      toast.success(active ? `« ${u.name} » réactivé` : `« ${u.name} » désactivé`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusyId(null); }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Utilisateur « ${u.name} » supprimé définitivement`);
      setDelTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  if (!rows) return <Loading />;
  const ROLE = { admin: "Administrateur", cdp: "CDP", chef: "Chef d'équipe", lecture_seule: "Lecture seule", pap: "Porte-à-porte", terrain: "Responsable terrain", prospecteur: "Prospecteur terrain", superviseur: "Superviseur" };
  const actifsCount = rows.filter(isActive).length;
  const desactivesCount = rows.length - actifsCount;
  const shown = rows.filter((u) => filter === "tous" ? true : filter === "desactives" ? !isActive(u) : isActive(u));

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList data-testid="users-filter">
            <TabsTrigger value="actifs" data-testid="users-filter-actifs">Actifs ({actifsCount})</TabsTrigger>
            <TabsTrigger value="desactives" data-testid="users-filter-desactives">Désactivés ({desactivesCount})</TabsTrigger>
            <TabsTrigger value="tous" data-testid="users-filter-tous">Tous ({rows.length})</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button size="sm" onClick={() => setEdit(null)} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="user-new">
          <Plus className="mr-1.5 h-4 w-4" />Nouvel utilisateur
        </Button>
      </div>
      <ListCard>
        {shown.length === 0 && <div className="p-6 text-center text-xs text-muted-ink" data-testid="users-empty">Aucun utilisateur dans cette vue.</div>}
        {shown.map((u) => {
          const uZones = zones.filter((z) => z.cdp_id === u.id);
          const active = isActive(u);
          return (
            <div key={u.id} className={`flex items-center justify-between gap-3 border-b border-[var(--border-soft)] p-4 last:border-0 ${active ? "" : "bg-[var(--surface-2)]/40"}`} data-testid={`user-${u.email}`}>
              <div className="flex min-w-0 items-center gap-3">
                <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-bold ${active ? "" : "opacity-45 grayscale"}`} style={{ background: u.role === "cdp" ? u.color : "var(--shariti-orange)", color: u.role === "cdp" ? "#181818" : "#fff" }}>
                  {(u.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`truncate font-bold ${active ? "" : "text-muted-ink"}`}>{u.name}</span>
                    {!active && <span className="shrink-0 rounded-full bg-[rgba(24,24,24,0.08)] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-muted-ink" data-testid={`user-badge-desactive-${u.email}`}>Désactivé</span>}
                  </div>
                  <div className="truncate text-xs text-muted-ink">{u.email}</div>
                  {u.role === "cdp" && (
                    <div className="mt-0.5 flex flex-wrap items-center gap-1">
                      {u.objectives?.daily_spots ? (
                        <span className="rounded-full bg-[rgba(238,114,46,0.1)] px-1.5 py-0.5 text-[10px] font-semibold text-[#8A3A10]" data-testid={`user-obj-${u.email}`}>Objectif {u.objectives.daily_spots}/j</span>
                      ) : null}
                      {uZones.length > 0 && (
                        <span className="rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-medium text-muted-ink" title={uZones.map((z) => z.name).join(", ")}>
                          {uZones.length} zone{uZones.length > 1 ? "s" : ""}
                        </span>
                      )}
                      {(u.association_ids || []).map((aid) => {
                        const a = assocMap[aid];
                        if (!a) return null;
                        return (
                          <span key={aid} data-testid={`user-assoc-badge-${u.email}-${aid}`} title={`Gère ${a.acronym || a.name}`}
                            className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white"
                            style={{ background: a.color || "#EE722E" }}>
                            {a.acronym || a.name}
                          </span>
                        );
                      })}
                      {u.telephone ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-medium text-muted-ink" data-testid={`user-tel-${u.email}`}><Phone className="h-2.5 w-2.5" />{u.telephone}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(238,114,46,0.14)] px-1.5 py-0.5 text-[10px] font-semibold text-[#8A3A10]" data-testid={`user-tel-missing-${u.email}`} title="Ajoutez un numéro pour activer les boutons Appeler / WhatsApp côté terrain">
                          <Phone className="h-2.5 w-2.5" />Tél. manquant
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <span className="hidden rounded-full bg-[var(--surface-2)] px-2.5 py-1 text-[11px] font-semibold sm:inline-block">{ROLE[u.role] || u.role}</span>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEdit(u)} title="Modifier" data-testid={`user-edit-${u.email}`}><Pencil className="h-4 w-4" /></Button>
                {active ? (
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[#B26A00] hover:bg-[rgba(238,114,46,0.10)]" disabled={busyId === u.id} onClick={() => setDeacTarget(u)} title="Désactiver le compte" data-testid={`user-deactivate-${u.email}`}><UserX className="h-4 w-4" /></Button>
                ) : (
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[#0F5A2F] hover:bg-[rgba(31,157,85,0.10)]" disabled={busyId === u.id} onClick={() => toggleActive(u, true)} title="Réactiver le compte" data-testid={`user-reactivate-${u.email}`}><UserCheck className="h-4 w-4" /></Button>
                )}
                <Button variant="ghost" size="icon" className="h-8 w-8 text-[#D64545] hover:bg-[rgba(214,69,69,0.08)]" onClick={() => setDelTarget(u)} title="Supprimer définitivement" data-testid={`user-del-${u.email}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          );
        })}
      </ListCard>

      <UserDialog open={edit !== undefined} user={edit} zones={zones} reloadZones={loadZones}
        cdps={(rows || []).filter((u) => u.role === "cdp")}
        onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); load(); loadZones(); }} />

      {/* Désactivation (réversible, conserve les statistiques) */}
      <AlertDialog open={!!deacTarget} onOpenChange={(o) => !o && setDeacTarget(null)}>
        <AlertDialogContent data-testid="user-deactivate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Désactiver ce compte ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {deacTarget?.name} » ({deacTarget?.email}) ne pourra plus se connecter avec ses identifiants.
              Ses statistiques et son historique sont <strong>conservés</strong> — vous pourrez le réactiver à tout moment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-deactivate-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => { toggleActive(deacTarget, false); setDeacTarget(null); }} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="user-deactivate-confirm">Désactiver</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Suppression définitive (irréversible) */}
      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="user-del-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer définitivement ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {delTarget?.name} » ({delTarget?.email}) sera <strong>définitivement supprimé</strong> (action irréversible) et ses zones seront libérées.
              Pour bloquer l'accès tout en conservant les statistiques, préférez plutôt « Désactiver ».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-del-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove(delTarget)} className="bg-[#D64545] text-white hover:bg-[#B93A3A]" data-testid="user-del-confirm">Supprimer définitivement</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// --- Ligne « zone » dans la fenêtre utilisateur : case zone entière + drill-down par département ---
function UserZoneRow({ zone, userId, checked, onToggleZone, deptSel, onToggleDept }) {
  const [open, setOpen] = useState(false);
  const canSplit = !zone.is_mutualise && (zone.departments || []).length >= 2 && !checked;
  const takenByOther = zone.cdp_id && (!userId || zone.cdp_id !== userId);
  return (
    <div className={`rounded-md border border-[var(--border-soft)] bg-white p-2 ${checked ? "ring-1 ring-[rgba(238,114,46,0.4)]" : ""}`} data-testid={`user-zone-${zone.code}`}>
      <div className="flex items-center gap-2">
        <Checkbox checked={checked} onCheckedChange={onToggleZone} data-testid={`user-zone-check-${zone.code}`} />
        <span className="min-w-0 flex-1 text-xs">
          <span className="block truncate font-semibold">{zone.name}</span>
          <span className="block truncate text-[10px] text-muted-ink">
            {(zone.departments || []).join(", ") || zone.code}
            {takenByOther && zone.cdp_name ? ` · tenue par ${zone.cdp_name}` : ""}
          </span>
        </span>
        {checked && takenByOther && (
          <span className="shrink-0 rounded-full bg-[rgba(238,114,46,0.12)] px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-[#8A3A10]"
            title="Zone partagée en co-titulaire — elle n'est PAS retirée à son CDP actuel" data-testid={`user-zone-shared-${zone.code}`}>
            partagée
          </span>
        )}
        {canSplit && (
          <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`user-zone-depts-toggle-${zone.code}`}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-[var(--border-soft)] px-2 py-1 text-[10px] font-semibold text-muted-ink transition-colors hover:bg-[var(--surface-2)]">
            <MapPin className="h-3 w-3" />Par dépt
            <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {canSplit && open && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`user-zone-depts-${zone.code}`}>
          {(zone.department_owners || []).map((o) => {
            const key = `${zone.id}::${o.dept}`;
            const sel = !!deptSel[key];
            const ownedByOther = o.cdp_id && o.cdp_id !== userId;
            return (
              <button key={o.dept} type="button" onClick={() => onToggleDept(o.dept)}
                title={sel ? "Attribué à cette personne" : (ownedByOther ? `Actuellement : ${o.cdp_name}` : "Libre")}
                data-testid={`user-zone-dept-${zone.code}-${o.dept}`}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-semibold transition-colors ${sel
                  ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white"
                  : "border-[var(--border-soft)] bg-white text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                <span className="font-mono">{o.dept}</span>
                {sel
                  ? <CheckCircle2 className="h-3 w-3" />
                  : (ownedByOther ? <span className="h-2 w-2 rounded-full" style={{ background: o.cdp_color || "#ccc" }} /> : null)}
              </button>
            );
          })}
          <span className="w-full text-[9px] leading-snug text-muted-ink">Choisis les départements de cette zone à confier à cette personne — les autres restent à leur CDP.</span>
        </div>
      )}
    </div>
  );
}

// --- Créateur de zone à la volée depuis le catalogue des départements de France ---
function NewZoneCreator({ catalog, nz, setNz, onToggleDept, creating, onCreate, onCancel }) {
  const q = (nz.q || "").trim().toLowerCase();
  const regions = (catalog?.regions || []).map((r) => ({
    ...r,
    departments: r.departments.filter((d) =>
      !q || d.code.toLowerCase().includes(q) || (d.name || "").toLowerCase().includes(q) || r.region.toLowerCase().includes(q)),
  })).filter((r) => r.departments.length);
  return (
    <div className="mt-2 rounded-lg border border-[rgba(238,114,46,0.35)] bg-[rgba(238,114,46,0.05)] p-3" data-testid="user-new-zone-creator">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold text-[#8A3A10]">
        <Plus className="h-3.5 w-3.5" />Ouvrir une nouvelle zone
      </div>
      <div className="grid grid-cols-[1fr_120px] gap-2">
        <div>
          <Label className="text-[10px]">Nom de la zone *</Label>
          <Input value={nz.name} onChange={(e) => setNz((f) => ({ ...f, name: e.target.value }))} placeholder="Ex : Nord, Loire…" className="mt-1 h-8 text-xs" data-testid="user-new-zone-name" />
        </div>
        <div>
          <Label className="text-[10px]">Code</Label>
          <Input value={nz.code} onChange={(e) => setNz((f) => ({ ...f, code: e.target.value }))} placeholder="auto" className="mt-1 h-8 font-mono text-xs" data-testid="user-new-zone-code" />
        </div>
      </div>
      <div className="mt-2">
        <div className="mb-1 flex items-center justify-between">
          <Label className="text-[10px]">Départements ({nz.depts.length} sélectionné{nz.depts.length > 1 ? "s" : ""})</Label>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-ink" />
          <Input value={nz.q} onChange={(e) => setNz((f) => ({ ...f, q: e.target.value }))} placeholder="Rechercher un département ou une région…" className="h-8 pl-7 text-xs" data-testid="user-new-zone-search" />
        </div>
        {!catalog ? (
          <div className="flex items-center gap-2 py-3 text-[11px] text-muted-ink"><Loader2 className="h-3.5 w-3.5 animate-spin" />Chargement du catalogue…</div>
        ) : (
          <div className="mt-1.5 max-h-40 space-y-2 overflow-y-auto rounded-md border border-[var(--border-soft)] bg-white p-2" data-testid="user-new-zone-catalog">
            {regions.length === 0 ? (
              <p className="py-2 text-center text-[11px] text-muted-ink">Aucun département ne correspond.</p>
            ) : regions.map((r) => (
              <div key={r.region}>
                <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-muted-ink">{r.region}</div>
                <div className="flex flex-wrap gap-1">
                  {r.departments.map((d) => {
                    const on = nz.depts.includes(d.code);
                    return (
                      <button key={d.code} type="button" onClick={() => onToggleDept(d.code)}
                        title={d.used ? `Déjà rattaché à ${d.used.zone_name}` : d.name}
                        data-testid={`user-new-zone-dept-${d.code}`}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${on
                          ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white"
                          : "border-[var(--border-soft)] bg-white text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                        <span className="font-mono">{d.code}</span>
                        {d.used && !on && <span className="h-1.5 w-1.5 rounded-full bg-[#B26A00]" title="déjà utilisé" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-1 text-[9px] text-muted-ink">Un point orange signale un département déjà rattaché à une autre zone.</p>
      </div>
      <div className="mt-2 flex items-center justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" className="h-8 text-[11px]" onClick={onCancel} data-testid="user-new-zone-cancel">Annuler</Button>
        <Button type="button" size="sm" onClick={onCreate} disabled={creating} className="h-8 bg-[var(--shariti-orange)] text-[11px] text-white hover:bg-[#E56622]" data-testid="user-new-zone-create">
          {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="mr-1 h-3.5 w-3.5" />Créer la zone</>}
        </Button>
      </div>
    </div>
  );
}

function UserDialog({ open, user, zones, reloadZones, cdps, onClose, onSaved }) {
  const isEdit = !!user;
  const empty = { name: "", email: "", role: "cdp", color: CDP_COLORS[0], password: "", daily_spots: "",
    restrict_planning: false, recovery_mode: false, hidden_features: [], telephone: "",
    billing_type: "hourly", hourly_rate: "20.5", rate_per_enseigne: "70", monthly_rate: "1000",
    work_days: [0, 1, 2, 3, 4], hours_per_day: "4", contract_start: "", absences: [],
    supervised_cdp_ids: [], association_ids: [],
    date_debut: "", date_fin: "" };
  const [form, setForm] = useState(empty);
  const [associations, setAssociations] = useState([]);
  const [zoneSel, setZoneSel] = useState({}); // {zoneId: bool}  (attribution zone entière)
  const [deptSel, setDeptSel] = useState({}); // {"zoneId::dept": bool}  (surcharge par département vers CE user)
  const [deptInit, setDeptInit] = useState({}); // snapshot initial {"zoneId::dept": {owner, is_override}}
  const [perms, setPerms] = useState({}); // permissions granulaires (rôle lecture_seule)
  const [saving, setSaving] = useState(false);
  const initedRef = useRef(false);
  // Création de zone à la volée (catalogue France)
  const [showNewZone, setShowNewZone] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [nz, setNz] = useState({ name: "", code: "", depts: [], q: "" });
  const [creatingZone, setCreatingZone] = useState(false);

  useEffect(() => { if (!open) { initedRef.current = false; setShowNewZone(false); setNz({ name: "", code: "", depts: [], q: "" }); } }, [open]);

  // Référentiel des associations (pour le bloc « Associations gérées » d'un CDP).
  useEffect(() => {
    if (!open) return;
    api.get("/associations").then((r) => setAssociations((r.data || []).filter((a) => a.active !== false))).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || initedRef.current) return;
    if (user) {
      const wr = user.work_rhythm || {};
      setForm({
        name: user.name || "", email: user.email || "", role: user.role || "cdp",
        color: user.color || CDP_COLORS[0], password: "",
        restrict_planning: !!user.restrict_planning,
        recovery_mode: !!user.recovery_mode,
        hidden_features: Array.isArray(user.hidden_features) ? [...user.hidden_features] : [],
        telephone: user.telephone || "",
        daily_spots: user.objectives?.daily_spots ? String(user.objectives.daily_spots) : "",
        billing_type: wr.billing_type || "hourly",
        hourly_rate: wr.hourly_rate != null ? String(wr.hourly_rate) : "20.5",
        rate_per_enseigne: wr.rate_per_enseigne != null ? String(wr.rate_per_enseigne) : "70",
        monthly_rate: wr.monthly_rate != null && wr.monthly_rate !== 0 ? String(wr.monthly_rate) : "1000",
        work_days: Array.isArray(wr.work_days) && wr.work_days.length
          ? [...wr.work_days].sort((a, b) => a - b)
          : (wr.days_per_week != null ? Array.from({ length: wr.days_per_week }, (_, i) => i) : [0, 1, 2, 3, 4]),
        hours_per_day: wr.hours_per_day != null ? String(wr.hours_per_day) : "4",
        contract_start: wr.contract_start || "",
        absences: Array.isArray(wr.absences) ? [...wr.absences].sort() : [],
        supervised_cdp_ids: Array.isArray(user.supervised_cdp_ids) ? [...user.supervised_cdp_ids] : [],
        association_ids: Array.isArray(user.association_ids) ? [...user.association_ids] : [],
        date_debut: user.date_debut || "",
        date_fin: user.date_fin || "",
      });
      setPerms(user.permissions || {});
    } else {
      setForm(empty); setPerms({});
    }
    // Sélections de zones (zone entière) + par département, calculées depuis l'état courant des zones.
    // Une zone est « cochée » si l'utilisateur en est titulaire OU co-titulaire (partagée).
    const sel = {}, dsel = {}, dinit = {};
    (zones || []).forEach((z) => {
      const isShared = (z.shared_cdps || []).some((sc) => sc.id === user?.id);
      if (user && (z.cdp_id === user.id || isShared)) sel[z.id] = true;
      (z.department_owners || []).forEach((o) => {
        const key = `${z.id}::${o.dept}`;
        dinit[key] = { owner: o.cdp_id, is_override: o.is_override };
        dsel[key] = !!(user && o.cdp_id === user.id);
      });
    });
    setZoneSel(sel); setDeptSel(dsel); setDeptInit(dinit);
    // Ne verrouille l'init qu'une fois les zones disponibles (ou en création sans zones).
    if (!user || (zones && zones.length)) initedRef.current = true;
  }, [open, user, zones]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [newAbs, setNewAbs] = useState("");
  const toggleWorkDay = (i) => setForm((f) => ({
    ...f,
    work_days: f.work_days.includes(i) ? f.work_days.filter((x) => x !== i) : [...f.work_days, i].sort((a, b) => a - b),
  }));
  const addAbsence = () => {
    if (!newAbs) return;
    setForm((f) => ({ ...f, absences: f.absences.includes(newAbs) ? f.absences : [...f.absences, newAbs].sort() }));
    setNewAbs("");
  };
  const removeAbsence = (d) => setForm((f) => ({ ...f, absences: f.absences.filter((x) => x !== d) }));
  const toggleZone = (id) => setZoneSel((s) => ({ ...s, [id]: !s[id] }));
  const toggleDept = (zoneId, dept) => setDeptSel((s) => { const k = `${zoneId}::${dept}`; return { ...s, [k]: !s[k] }; });
  const toggleHidden = (k) => setForm((f) => {
    const cur = f.hidden_features || [];
    return { ...f, hidden_features: cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k] };
  });
  const toggleAssoc = (id) => setForm((f) => {
    const cur = f.association_ids || [];
    return { ...f, association_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });
  const togglePerm = (k) => setPerms((p) => ({ ...p, [k]: !p[k] }));

  // --- Ouvrir une nouvelle zone (catalogue France) ---
  const openNewZone = async () => {
    setShowNewZone((v) => !v);
    if (!catalog) {
      try { const { data } = await api.get("/admin/zones/fr-departments"); setCatalog(data); }
      catch { toast.error("Impossible de charger le catalogue des départements"); }
    }
  };
  const toggleNzDept = (code) => setNz((f) => ({
    ...f, depts: f.depts.includes(code) ? f.depts.filter((x) => x !== code) : [...f.depts, code],
  }));
  const createZone = async () => {
    const name = nz.name.trim();
    if (!name) { toast.error("Nom de la zone requis"); return; }
    if (!nz.depts.length) { toast.error("Sélectionnez au moins un département"); return; }
    const code = (nz.code.trim() || name).toUpperCase().replace(/\s+/g, "-").slice(0, 12);
    setCreatingZone(true);
    try {
      const { data: z } = await api.post("/admin/zones", { name, code, departments: nz.depts });
      // Pré-sélectionne la nouvelle zone pour CE CDP (attribuée à l'enregistrement).
      if (z?.id) setZoneSel((s) => ({ ...s, [z.id]: form.role === "cdp" }));
      toast.success(`Zone « ${name} » créée`);
      setShowNewZone(false); setNz({ name: "", code: "", depts: [], q: "" });
      if (reloadZones) await reloadZones();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de la création de la zone"); }
    finally { setCreatingZone(false); }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    if (!form.email.trim()) { toast.error("L'email est requis"); return; }
    if (!isEdit && !form.password.trim()) { toast.error("Un mot de passe est requis à la création"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), email: form.email.trim().toLowerCase(),
        role: form.role, color: form.color,
      };
      if (isEdit) payload.id = user.id;
      if (form.password.trim()) payload.password = form.password.trim();
      // Visibilité restreinte du planning : proposée pour le prospecteur terrain uniquement.
      payload.restrict_planning = form.role === "prospecteur" ? !!form.restrict_planning : false;
      // Mode récupération (refus & liste noire) — réservé au rôle CDP.
      payload.recovery_mode = form.role === "cdp" ? !!form.recovery_mode : false;
      // Modules masqués pour cet utilisateur (contrôle de visibilité).
      payload.hidden_features = Array.isArray(form.hidden_features) ? form.hidden_features : [];
      // Téléphone / WhatsApp — permet aux RD sur le terrain de joindre le booker en 1 clic.
      payload.telephone = (form.telephone || "").trim() || null;
      // Superviseur (consultation seule) : CDP dont il voit les données dans « Suivi CDP ».
      // Vidé pour tout autre rôle (sécurité : pas de périmètre résiduel).
      payload.supervised_cdp_ids = form.role === "superviseur" ? (form.supervised_cdp_ids || []) : [];
      // Associations gérées : uniquement pour un CDP (déclaratif, badges + pré-ciblage lignes).
      payload.association_ids = form.role === "cdp" ? (form.association_ids || []) : [];
      // Permissions granulaires : selon le rôle (lecture seule OU responsable terrain), sinon vidées.
      if (form.role === "lecture_seule") {
        const vp = {};
        [...VIEWER_PERMISSIONS, ...TERRAIN_BOARDS].forEach((p) => { vp[p.k] = !!perms[p.k]; });
        // « Gestion complète » d'un tableau : uniquement si le tableau est visible.
        TERRAIN_BOARDS.forEach((b) => { vp[`${b.k}_edit`] = !!perms[b.k] && !!perms[`${b.k}_edit`]; });
        payload.permissions = vp;
      } else if (form.role === "terrain") {
        payload.permissions = TERRAIN_BOARDS.reduce((acc, b) => ({ ...acc, [b.k]: !!perms[b.k] }), {});
      } else {
        payload.permissions = {};
      }
      if (form.role === "cdp") {
        payload.objectives = {
          ...(user?.objectives || {}),
          ...(form.daily_spots ? { daily_spots: Number(form.daily_spots) } : {}),
        };
        if (!form.daily_spots && payload.objectives.daily_spots) delete payload.objectives.daily_spots;
        // Dates de CONTRAT du CDP (début/fin) : pilotent l'attribution DATÉE des
        // couleurs/zones sur le planning (les semaines passées restent au CDP en poste
        // à l'époque, le repreneur ne récupère pas l'historique).
        payload.date_debut = (form.date_debut || "").trim() || null;
        payload.date_fin = (form.date_fin || "").trim() || null;
        // Rythme de travail & facturation du CDP (fusion avec l'existant).
        payload.work_rhythm = {
          ...(user?.work_rhythm || {}),
          billing_type: ["per_enseigne", "forfait", "hourly"].includes(form.billing_type) ? form.billing_type : "hourly",
          hourly_rate: Number(form.hourly_rate) || 0,
          rate_per_enseigne: Number(form.rate_per_enseigne) || 0,
          monthly_rate: Number(form.monthly_rate) || 0,
          days_per_week: (form.work_days || []).length,
          work_days: [...(form.work_days || [])].sort((a, b) => a - b),
          hours_per_day: Number(form.hours_per_day) || 0,
          contract_start: form.contract_start || null,
          absences: [...(form.absences || [])].sort(),
        };
      }
      const { data: saved } = await api.post("/admin/users", payload);
      const uid = saved.id;
      // Diff d'attribution des zones (uniquement pour un CDP NON en mode récupération)
      if (form.role === "cdp" && form.recovery_mode) {
        // Mode récupération : aucune zone. On libère les zones éventuellement tenues.
        const rel = (zones || []).filter((z) => z.cdp_id === uid).map((z) => api.post(`/admin/zones/${z.id}/release`));
        if (rel.length) await Promise.all(rel);
      } else if (form.role === "cdp") {
        // 1) Zones ENTIÈRES — attribution NON DESTRUCTIVE :
        //    • zone libre  -> ce CDP en devient titulaire (assign)
        //    • zone déjà tenue par un autre CDP -> PARTAGE en co-titulaire (share) sans la lui retirer
        //    • on décoche  -> unshare si simple co-titulaire, sinon release (titulaire)
        const zoneOps = [];
        for (const z of zones || []) {
          const want = !!zoneSel[z.id];
          const isTitulaire = z.cdp_id === uid;
          const isShared = (z.shared_cdps || []).some((sc) => sc.id === uid);
          const has = isTitulaire || isShared;
          const takenByOther = z.cdp_id && z.cdp_id !== uid;
          if (want && !has) {
            if (takenByOther) zoneOps.push(api.post(`/admin/zones/${z.id}/share`, { cdp_id: uid }));
            else zoneOps.push(api.post(`/admin/zones/${z.id}/assign`, { cdp_id: uid }));
          } else if (!want && has) {
            if (isShared && !isTitulaire) zoneOps.push(api.post(`/admin/zones/${z.id}/unshare`, { cdp_id: uid }));
            else zoneOps.push(api.post(`/admin/zones/${z.id}/release`));
          }
        }
        if (zoneOps.length) await Promise.all(zoneOps);
        // 2) Surcharges PAR DÉPARTEMENT — seulement pour les zones NON attribuées en entier à ce CDP.
        const deptOps = [];
        for (const z of zones || []) {
          if (zoneSel[z.id] || z.is_mutualise) continue;
          for (const o of (z.department_owners || [])) {
            const key = `${z.id}::${o.dept}`;
            const want = !!deptSel[key];
            const init = deptInit[key] || {};
            const ownedByUser = init.owner === uid;
            if (want && !ownedByUser) {
              deptOps.push(api.post(`/admin/zones/${z.id}/assign-department`, { dept: o.dept, cdp_id: uid }));
            } else if (!want && ownedByUser && init.is_override) {
              deptOps.push(api.post(`/admin/zones/${z.id}/assign-department`, { dept: o.dept, cdp_id: null }));
            }
          }
        }
        if (deptOps.length) await Promise.all(deptOps);
      }
      toast.success(isEdit ? "Utilisateur mis à jour" : "Utilisateur créé");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const sortedZones = [...(zones || [])].sort((a, b) => (a.code || "").localeCompare(b.code || ""));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg overflow-hidden [&>*]:min-w-0" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{isEdit ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle>
          <DialogDescription>{isEdit ? "Modifiez le rôle, les accès et les objectifs." : "Créez un compte et attribuez son rôle et ses accès."}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] min-w-0 space-y-3 overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px]">Nom complet *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 h-9" data-testid="user-form-name" /></div>
            <div><Label className="text-[11px]">Email *</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="prenom@shariti.fr" className="mt-1 h-9" data-testid="user-form-email" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px]">Rôle *</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="mt-1 h-9 w-full" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[11px]">Mot de passe {isEdit ? "" : "*"}</Label>
              <PasswordInput value={form.password} onChange={(e) => set("password", e.target.value)}
                placeholder={isEdit ? "Laisser vide pour conserver" : "Mot de passe initial"} className="mt-1" inputClassName="h-9" data-testid="user-form-password" />
            </div>
          </div>
          <p className="text-[10px] text-muted-ink">{ROLE_OPTS.find((r) => r.v === form.role)?.desc}{form.password.trim() ? " · L'utilisateur devra changer son mot de passe à la 1ʳᵉ connexion." : ""}</p>

          {["cdp", "admin", "prospecteur"].includes(form.role) && (
            <div>
              <Label className="text-[11px]">Téléphone / WhatsApp</Label>
              <Input type="tel" value={form.telephone} onChange={(e) => set("telephone", e.target.value)}
                placeholder="06 12 34 56 78" className="mt-1 h-9" data-testid="user-form-telephone" />
              <p className="mt-1 text-[10px] text-muted-ink">
                Permet aux recruteurs sur le terrain de vous joindre en 1 clic (appel / WhatsApp) en cas de problème d'accès sur un spot que vous avez booké.
              </p>
            </div>
          )}

          {form.role === "prospecteur" && (
            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="user-form-restrict-planning">
              <label className="flex cursor-pointer items-start justify-between gap-3"
                data-testid="restrict-planning-row">
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-[11px] font-bold text-ink">
                    <ShieldCheck className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                    Masquer le planning des autres équipes
                  </span>
                  <span className="mt-0.5 block text-[10px] leading-snug text-muted-ink">
                    Le prospecteur ne verra QUE ses propres créneaux dans le planning. Les créneaux
                    des autres équipes apparaîtront comme « Créneau occupé » (magasin masqué), pour
                    éviter qu'il ne se rende sur des spots déjà couverts. La banque de spots et la
                    prospection restent accessibles.
                  </span>
                </span>
                <Switch checked={!!form.restrict_planning} onCheckedChange={(v) => set("restrict_planning", v)} data-testid="restrict-planning-switch" />
              </label>
            </div>
          )}

          {form.role === "terrain" && (
            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="user-form-boards">
              <div className="flex items-center gap-1.5 text-[11px] font-bold text-ink">
                <ShieldCheck className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                Plannings terrain accessibles
              </div>
              <p className="mt-0.5 text-[10px] text-muted-ink">
                Cochez les plannings que cet utilisateur peut gérer. Il n'aura accès à rien d'autre.
              </p>
              <div className="mt-2 space-y-1">
                {TERRAIN_BOARDS.map((b) => (
                  <label key={b.k}
                    className="flex cursor-pointer items-center justify-between gap-3 rounded-lg bg-white px-2.5 py-2 transition-colors hover:bg-[rgba(238,114,46,0.05)]"
                    data-testid={`board-row-${b.k}`}>
                    <span className="min-w-0">
                      <span className="block text-xs font-semibold text-ink">{b.l}</span>
                      <span className="block text-[10px] leading-snug text-muted-ink">{b.d}</span>
                    </span>
                    <Switch checked={!!perms[b.k]} onCheckedChange={() => togglePerm(b.k)} data-testid={`board-switch-${b.k}`} />
                  </label>
                ))}
              </div>
            </div>
          )}

          {form.role === "superviseur" && (
            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="user-form-supervised">
              <div className="flex items-center gap-1.5 text-[11px] font-bold text-ink">
                <ShieldCheck className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                CDP supervisés
              </div>
              <p className="mt-0.5 text-[10px] leading-snug text-muted-ink">
                Cochez les CDP dont ce superviseur pourra consulter les données dans « Suivi CDP ».
                Il ne verra QUE ces CDP (aucune facturation). Sans sélection, il ne verra aucune donnée.
              </p>
              {(cdps || []).length > 0 && (
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-muted-ink" data-testid="supervised-count">
                    {(form.supervised_cdp_ids || []).length} / {(cdps || []).length} sélectionné(s)
                  </span>
                  <button type="button" data-testid="supervised-select-all"
                    className="text-[10px] font-semibold text-[var(--shariti-orange)] hover:underline"
                    onClick={() => {
                      const allIds = (cdps || []).map((c) => c.id);
                      const allSel = (form.supervised_cdp_ids || []).length === allIds.length;
                      set("supervised_cdp_ids", allSel ? [] : allIds);
                    }}>
                    {(form.supervised_cdp_ids || []).length === (cdps || []).length ? "Tout désélectionner" : "Tout sélectionner"}
                  </button>
                </div>
              )}
              <div className="mt-2 max-h-52 space-y-1 overflow-y-auto">
                {(cdps || []).length === 0 && (
                  <p className="rounded-lg bg-white px-2.5 py-3 text-center text-[10px] text-muted-ink" data-testid="supervised-empty">
                    Aucun CDP disponible. Créez d'abord des comptes CDP.
                  </p>
                )}
                {[...(cdps || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((c) => {
                  const checked = (form.supervised_cdp_ids || []).includes(c.id);
                  const inactive = c.active === false;
                  return (
                    <label key={c.id}
                      className="flex cursor-pointer items-center justify-between gap-3 rounded-lg bg-white px-2.5 py-2 transition-colors hover:bg-[rgba(238,114,46,0.05)]"
                      data-testid={`supervised-cdp-${c.id}`}>
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[9px] font-bold"
                          style={{ background: c.color || "#FCDB53", color: "#181818" }}>
                          {(c.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-xs font-semibold text-ink">{c.name}{inactive ? " · désactivé" : ""}</span>
                          <span className="block truncate text-[10px] text-muted-ink">{c.email}</span>
                        </span>
                      </span>
                      <Checkbox checked={checked} data-testid={`supervised-cdp-switch-${c.id}`}
                        onCheckedChange={() => {
                          const cur = form.supervised_cdp_ids || [];
                          set("supervised_cdp_ids", cur.includes(c.id) ? cur.filter((x) => x !== c.id) : [...cur, c.id]);
                        }} />
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {form.role === "lecture_seule" && (
            <>
            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="user-form-permissions">
              <div className="flex items-center gap-1.5 text-[11px] font-bold text-ink">
                <ShieldCheck className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                Permissions sur le planning
              </div>
              <p className="mt-0.5 text-[10px] text-muted-ink">
                Par défaut, l'utilisateur ne peut que consulter (navigation + filtres). Activez ci-dessous chaque action de modification autorisée.
              </p>
              <div className="mt-2 space-y-1">
                {VIEWER_PERMISSIONS.map((p) => (
                  <label key={p.k}
                    className="flex cursor-pointer items-center justify-between gap-3 rounded-lg bg-white px-2.5 py-2 transition-colors hover:bg-[rgba(238,114,46,0.05)]"
                    data-testid={`perm-row-${p.k}`}>
                    <span className="min-w-0">
                      <span className="block text-xs font-semibold text-ink">{p.l}</span>
                      <span className="block text-[10px] leading-snug text-muted-ink">{p.d}</span>
                    </span>
                    <Switch checked={!!perms[p.k]} onCheckedChange={() => togglePerm(p.k)} data-testid={`perm-switch-${p.k}`} />
                  </label>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="user-form-viewer-boards">
              <div className="flex items-center gap-1.5 text-[11px] font-bold text-ink">
                <DoorOpen className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                Plannings terrain visibles (consultation)
              </div>
              <p className="mt-0.5 text-[10px] text-muted-ink">
                Le planning « site privé » reste toujours visible. Cochez les plannings terrain accessibles, puis activez « Gestion complète » pour autoriser la création / modification / suppression (sinon consultation seule).
              </p>
              <div className="mt-2 space-y-1">
                {TERRAIN_BOARDS.map((b) => (
                  <div key={b.k} className="rounded-lg bg-white" data-testid={`viewer-board-row-${b.k}`}>
                    <label className="flex cursor-pointer items-center justify-between gap-3 px-2.5 py-2 transition-colors hover:bg-[rgba(238,114,46,0.05)]">
                      <span className="min-w-0">
                        <span className="block text-xs font-semibold text-ink">{b.l}</span>
                        <span className="block text-[10px] leading-snug text-muted-ink">{b.d}</span>
                      </span>
                      <Switch checked={!!perms[b.k]} onCheckedChange={() => togglePerm(b.k)} data-testid={`viewer-board-switch-${b.k}`} />
                    </label>
                    {perms[b.k] && (
                      <label className="flex cursor-pointer items-center justify-between gap-3 border-t border-[var(--border-soft)] bg-[var(--surface-2)] px-2.5 py-1.5"
                        data-testid={`viewer-board-editrow-${b.k}`}>
                        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-ink">
                          <Pencil className="h-3 w-3 text-[var(--shariti-orange)]" />
                          Gestion complète (créer / modifier / supprimer)
                        </span>
                        <Switch checked={!!perms[`${b.k}_edit`]} onCheckedChange={() => togglePerm(`${b.k}_edit`)} data-testid={`viewer-board-editswitch-${b.k}`} />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
            </>
          )}

          <div>
            <Label className="text-[11px]">Couleur (repère planning / zones)</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="user-form-colors">
              {CDP_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => set("color", c)}
                  className={`h-7 w-7 rounded-full border-2 transition-transform ${form.color === c ? "scale-110 border-[var(--shariti-ink)]" : "border-transparent hover:scale-105"}`}
                  style={{ background: c }} data-testid={`user-color-${c}`} aria-label={`Couleur ${c}`} />
              ))}
            </div>
          </div>

          {form.role === "cdp" && (
            <>
              {/* Mode récupération (chargé de développement partenarial) : refus & liste noire uniquement */}
              <div className="rounded-xl border border-[rgba(155,34,38,0.28)] bg-[rgba(155,34,38,0.05)] p-3" data-testid="user-recovery-block">
                <label className="flex cursor-pointer items-start gap-3">
                  <Switch checked={!!form.recovery_mode} onCheckedChange={(v) => set("recovery_mode", v)} data-testid="user-recovery-switch" className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-[12px] font-bold text-[#7A1E22]"><RotateCcw className="h-3.5 w-3.5" />Mode reconquête · développement partenarial</span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-muted-ink">
                      Ce chargé de développement partenarial ne gère pas de zones : son assistant d'appel devient
                      un portefeuille de reconquête (tous secteurs), avec tout l'historique et les commentaires,
                      qu'il peut filtrer par département — sans empiéter sur le périmètre des autres CDP.
                    </span>
                  </span>
                </label>
              </div>

              {/* Visibilité / accès — masquer des informations pour cette personne */}
              <div className="rounded-xl border border-[var(--border-soft)] p-3" data-testid="user-visibility-block">
                <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold"><SlidersHorizontal className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Visibilité / accès</div>
                <label className="flex cursor-pointer items-start gap-2">
                  <Checkbox checked={(form.hidden_features || []).includes("bulletins")} onCheckedChange={() => toggleHidden("bulletins")} data-testid="user-hide-bulletins" className="mt-0.5" />
                  <span className="text-[11px] leading-snug">
                    <span className="font-semibold">Masquer les Bulletins</span>
                    <span className="block text-muted-ink">Cache le bloc « Bulletins » du tableau de bord (statistiques + « qui a fait ces bulletins ») ET l'entrée « Bulletins » du menu latéral. La personne ne verra ni les collaborateurs ni les montants.</span>
                  </span>
                </label>
              </div>

              <div className="border-t border-[var(--border-soft)] pt-3">
                <Label className="text-[11px]">Objectif quotidien (spots bookés / jour)</Label>
                <Input type="number" min="1" value={form.daily_spots} onChange={(e) => set("daily_spots", e.target.value)}
                  placeholder="Défaut : 3" className="mt-1 h-9 w-40" data-testid="user-form-objective" />
                <p className="mt-1 text-[10px] text-muted-ink">Utilisé pour le suivi de performance et « objectif du jour atteint ».</p>
              </div>

              {/* Dates de CONTRAT — pilotent l'attribution DATÉE des couleurs/zones du planning */}
              <div className="border-t border-[var(--border-soft)] pt-3" data-testid="user-form-contract-dates">
                <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold"><CalendarRange className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Dates de contrat (couleur du planning)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px]">Début de contrat</Label>
                    <Input type="date" value={form.date_debut || ""} onChange={(e) => set("date_debut", e.target.value)}
                      className="mt-1 h-9 w-full" data-testid="user-form-date-debut" />
                  </div>
                  <div>
                    <Label className="text-[11px]">Fin de contrat</Label>
                    <Input type="date" value={form.date_fin || ""} onChange={(e) => set("date_fin", e.target.value)}
                      className="mt-1 h-9 w-full" data-testid="user-form-date-fin" />
                  </div>
                </div>
                <p className="mt-1.5 text-[10px] leading-snug text-muted-ink">
                  Les créneaux ne prennent la couleur de ce CDP que pour les dates comprises dans son contrat.
                  Ainsi, les semaines <span className="font-semibold">passées</span> gardent la couleur du CDP qui
                  était en poste à ce moment-là (le repreneur d'un secteur ne récupère pas l'historique).
                  Laisser vide = pas de limite. À la création, le début est rempli automatiquement à aujourd'hui ;
                  à la désactivation du compte, la fin est enregistrée automatiquement.
                </p>
              </div>

              <div className="border-t border-[var(--border-soft)] pt-3" data-testid="user-form-billing">
                <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold"><Wallet className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Facturation & rythme de travail</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px]">Type de facturation</Label>
                    <Select value={form.billing_type} onValueChange={(v) => set("billing_type", v)}>
                      <SelectTrigger className="mt-1 h-9 w-full" data-testid="user-form-billing-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">À l'heure (taux horaire)</SelectItem>
                        <SelectItem value="per_enseigne">À l'enseigne trouvée</SelectItem>
                        <SelectItem value="forfait">Au forfait mensuel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.billing_type === "per_enseigne" ? (
                    <div>
                      <Label className="text-[11px]">Tarif / enseigne (€)</Label>
                      <Input type="number" step="0.01" min="0" value={form.rate_per_enseigne} onChange={(e) => set("rate_per_enseigne", e.target.value)} className="mt-1 h-9" data-testid="user-form-rate-enseigne" />
                    </div>
                  ) : form.billing_type === "forfait" ? (
                    <div>
                      <Label className="text-[11px]">Forfait mensuel (€)</Label>
                      <Input type="number" step="0.01" min="0" value={form.monthly_rate} onChange={(e) => set("monthly_rate", e.target.value)} className="mt-1 h-9" data-testid="user-form-monthly-rate" />
                    </div>
                  ) : (
                    <div>
                      <Label className="text-[11px]">Taux horaire (€)</Label>
                      <Input type="number" step="0.01" min="0" value={form.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} className="mt-1 h-9" data-testid="user-form-hourly-rate" />
                    </div>
                  )}
                </div>
                {/* Jours travaillés EXACTS de la semaine (rythme réel : ex. mardi→samedi). */}
                <div className="mt-2">
                  <Label className="text-[11px]">Jours travaillés</Label>
                  <div className="mt-1 flex flex-wrap gap-1.5" data-testid="user-form-workdays">
                    {WEEKDAYS.map(([lbl, i]) => {
                      const on = (form.work_days || []).includes(i);
                      return (
                        <button key={i} type="button" onClick={() => toggleWorkDay(i)}
                          aria-pressed={on} data-testid={`user-workday-${i}`}
                          className={`h-8 min-w-[42px] rounded-lg border px-2 text-[12px] font-semibold transition-colors ${on
                            ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white"
                            : "border-[var(--border-soft)] bg-white text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                          {lbl}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-0.5 text-[10px] text-muted-ink">{(form.work_days || []).length} jour(s) / semaine · cochez les jours réellement travaillés.</p>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px]">Heures / jour{form.billing_type === "hourly" ? "" : " (indicatif)"}</Label>
                    <Input type="number" step="0.5" min="0" value={form.hours_per_day} onChange={(e) => set("hours_per_day", e.target.value)} className="mt-1 h-9" data-testid="user-form-hours-day" />
                  </div>
                  <div>
                    <Label className="text-[11px]">Date d'entrée (contrat)</Label>
                    <Input type="date" value={form.contract_start} onChange={(e) => set("contract_start", e.target.value)} className="mt-1 h-9 w-full" data-testid="user-form-contract-start" />
                  </div>
                </div>
                <p className="mt-1 text-[10px] text-muted-ink">Les jours avant la date d'entrée ne sont pas facturés (prorata d'un démarrage en cours de mois). Laisser vide si sans objet.</p>
                {/* Jours d'absence (congés/récup) : retirés du rythme (prorata forfait / jours facturés). */}
                <div className="mt-2">
                  <Label className="text-[11px]">Jours d'absence (congés, récup…)</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Input type="date" value={newAbs} onChange={(e) => setNewAbs(e.target.value)} className="h-9 w-44" data-testid="user-form-absence-date" />
                    <Button type="button" variant="outline" size="sm" onClick={addAbsence} className="h-9" data-testid="user-form-absence-add"><Plus className="mr-1 h-3.5 w-3.5" />Ajouter</Button>
                  </div>
                  {(form.absences || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="user-form-absences-list">
                      {form.absences.map((d) => (
                        <span key={d} className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[11px] font-medium text-ink" data-testid={`user-absence-${d}`}>
                          {fmtDateFr(d)}
                          <button type="button" onClick={() => removeAbsence(d)} className="text-muted-ink hover:text-[#9B2226]" aria-label={`Retirer ${d}`}><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="mt-0.5 text-[10px] text-muted-ink">Chaque absence retire un jour travaillé (réduit le prorata forfait / les jours facturés).</p>
                </div>
                <p className="mt-1 text-[10px] text-muted-ink">
                  {form.billing_type === "per_enseigne"
                    ? "Le CDP est rémunéré pour chaque enseigne trouvée dans le mois."
                    : form.billing_type === "forfait"
                      ? "Le CDP perçoit un montant fixe chaque mois, quel que soit le nombre d'heures ou d'enseignes (ex. temps plein)."
                      : "Coût mensuel = heures/jour × jours réellement travaillés du mois × taux horaire."}
                </p>
              </div>

              {/* Associations gérées par ce CDP — déclaratif (badges « qui gère quoi » + pré-ciblage des lignes) */}
              <div className="border-t border-[var(--border-soft)] pt-3" data-testid="user-form-associations">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold"><Building2 className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Associations gérées</span>
                <p className="mt-0.5 text-[10px] text-muted-ink">Cochez la ou les associations prises en charge par ce CDP (ex. Handicap International). Sert aux badges « qui gère quoi » dans le planning. N'affecte pas les zones (la géographie reste partagée entre CDP).</p>
                {associations.length === 0 ? (
                  <p className="mt-1.5 text-[11px] text-muted-ink">Aucune association disponible.</p>
                ) : (
                  <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="user-form-assoc-list">
                    {associations.map((a) => {
                      const on = (form.association_ids || []).includes(a.id);
                      return (
                        <button type="button" key={a.id} onClick={() => toggleAssoc(a.id)}
                          data-testid={`user-assoc-${a.id}`} aria-pressed={on}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors ${on ? "border-transparent text-white" : "border-[var(--border-soft)] bg-white text-ink hover:border-[var(--shariti-orange)]"}`}
                          style={on ? { background: a.color || "#EE722E" } : undefined}>
                          {on ? <CheckCircle2 className="h-3 w-3" /> : <Plus className="h-3 w-3 text-muted-ink" />}
                          {a.acronym || a.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {form.recovery_mode ? (
              <div className="rounded-lg border border-[rgba(155,34,38,0.25)] bg-[rgba(155,34,38,0.04)] p-3 text-[11px] leading-snug text-[#7A1E22]" data-testid="user-recovery-zones-note">
                <span className="flex items-center gap-1.5 font-bold"><RotateCcw className="h-3.5 w-3.5" />Portefeuille de reconquête (toutes zones)</span>
                <span className="mt-0.5 block text-muted-ink">Pas de zone à définir : son assistant d'appel contient automatiquement tous les emplacements à reconquérir, filtrables par département.</span>
              </div>
              ) : (
              <div className="border-t border-[var(--border-soft)] pt-3">
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-[11px] font-semibold"><Map className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Zones attribuées</span>
                  <Button type="button" size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]" onClick={openNewZone} data-testid="user-open-new-zone">
                    <Plus className="h-3.5 w-3.5" />Ouvrir une nouvelle zone
                  </Button>
                </div>
                {sortedZones.length === 0 ? (
                  <p className="text-[11px] text-muted-ink">Aucune zone disponible.</p>
                ) : (
                  <div className="grid max-h-56 grid-cols-1 gap-1.5 overflow-y-auto rounded-lg border border-[var(--border-soft)] p-2" data-testid="user-form-zones">
                    {sortedZones.map((z) => (
                      <UserZoneRow key={z.id} zone={z} userId={user?.id}
                        checked={!!zoneSel[z.id]} onToggleZone={() => toggleZone(z.id)}
                        deptSel={deptSel} onToggleDept={(d) => toggleDept(z.id, d)} />
                    ))}
                  </div>
                )}
                {showNewZone && (
                  <NewZoneCreator catalog={catalog} nz={nz} setNz={setNz} onToggleDept={toggleNzDept}
                    creating={creatingZone} onCreate={createZone} onCancel={() => setShowNewZone(false)} />
                )}
                <p className="mt-1 text-[10px] text-muted-ink">Cocher une zone <b>libre</b> l'attribue à ce CDP. Cocher une zone <b>déjà tenue</b> par un autre CDP la <b>partage</b> (co-titulaire) sans la lui retirer. « Par dépt » permet de ne confier que certains départements.</p>
              </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="user-form-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="user-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DemoDataPanel() {
  const [summary, setSummary] = useState(null);
  const [purging, setPurging] = useState(false);
  const load = () => api.get("/admin/demo-data/summary").then((r) => setSummary(r.data));
  useEffect(() => { load(); }, []);
  const purge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/demo-data/purge");
      const d = data.deleted;
      toast.success(`Données de démo purgées : ${d.spots} spots, ${d.bookings} bookings, ${d.bulletins} bulletins`);
      load();
    } catch {
      toast.error("Erreur lors de la purge");
    } finally { setPurging(false); }
  };
  if (!summary) return <Loading />;
  const stats = [
    { icon: Database, label: "Spots de démonstration", value: summary.spots_demo, color: "#EE722E" },
    { icon: CalendarRange, label: "Journées-spot (bookings) démo", value: summary.bookings_demo, color: "#2F6FED" },
    { icon: ScrollText, label: "Bulletins démo", value: summary.bulletins_demo, color: "#E9B800" },
    { icon: ShieldAlert, label: "Spots réels (conservés)", value: summary.spots_reels, color: "#1F9D55" },
  ];
  return (
    <div className="space-y-4" data-testid="demo-data-panel">
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--surface-2)]"><Database className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
          <div>
            <h3 className="text-sm font-bold">Données de démonstration</h3>
            <p className="mt-1 text-sm text-muted-ink">
              Les données de démo (spots fictifs, planning pré-rempli) permettent de découvrir la plateforme.
              Purgez-les pour basculer proprement en usage réel. La <span className="font-semibold text-ink">structure est conservée</span> (associations, territoires, lignes, utilisateurs).
              Après la purge, ré-importez votre fichier Excel pour repartir sur une base 100% réelle.
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 @4xl/page:grid-cols-4">
          {stats.map((st) => (
            <div key={st.label} className="flex items-center gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid={`demo-stat-${st.label}`}>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${st.color}18` }}><st.icon className="h-4 w-4" style={{ color: st.color }} /></div>
              <div><div className="text-lg font-extrabold leading-none" data-testid="demo-stat-value">{st.value}</div><div className="text-[11px] text-muted-ink">{st.label}</div></div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-[rgba(214,69,69,0.28)] bg-[rgba(214,69,69,0.04)] p-5">
        <div>
          <div className="text-sm font-bold text-[#9B2226]">Zone sensible — purge des données de démonstration</div>
          <div className="mt-0.5 text-xs text-muted-ink">Action irréversible. Seules les données marquées « démo » seront supprimées.</div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button disabled={!summary.has_demo || purging} className="bg-[#D64545] text-white hover:bg-[#C63D3D]" data-testid="purge-demo-btn">
              {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-2 h-4 w-4" /> Purger les données de démo</>}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-[#D64545]" /> Confirmer la purge des données de démo</AlertDialogTitle>
              <AlertDialogDescription>
                Vous êtes sur le point de supprimer <span className="font-semibold text-ink">{summary.spots_demo} spots</span>, <span className="font-semibold text-ink">{summary.bookings_demo} journées-spot</span> et <span className="font-semibold text-ink">{summary.bulletins_demo} bulletins</span> de démonstration.
                La structure (associations, territoires, lignes, utilisateurs) est conservée. Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="purge-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={purge} className="bg-[#D64545] hover:bg-[#C63D3D]" data-testid="purge-confirm">Oui, purger</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function HistoricalDataPanel() {
  const [summary, setSummary] = useState(null);
  const [purging, setPurging] = useState(false);
  const load = () => api.get("/admin/historical-data/summary").then((r) => setSummary(r.data));
  useEffect(() => { load(); }, []);
  const purge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/historical-data/purge");
      const d = data.deleted;
      toast.success(`Historique purgé : ${d.spots} spots supprimés, ${d.spots_nettoyes} spots nettoyés`);
      load();
    } catch { toast.error("Erreur lors de la purge"); }
    finally { setPurging(false); }
  };
  if (!summary) return <Loading />;
  const stats = [
    { icon: History, label: "Spots historiques créés", value: summary.spots_historical, color: "#EE722E" },
    { icon: CalendarRange, label: "Exploitations historiques", value: summary.exploitations_historical, color: "#2F6FED" },
    { icon: GitBranch, label: "Codes campagne (référentiel)", value: summary.code_campagne, color: "#B98900" },
  ];
  return (
    <div data-testid="historical-data-panel">
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--surface-2)]"><History className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
          <div>
            <h3 className="text-sm font-bold">Données historiques (fichier de suivi)</h3>
            <p className="mt-1 text-sm text-muted-ink">
              Exploitations passées importées depuis l'ancien fichier de suivi (lecture seule, pour le calcul des disponibilités).
              Purgez-les pour repartir sans historique. Les fiches spots réelles sont conservées (seules les exploitations marquées historiques sont retirées).
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 @3xl/page:grid-cols-3">
          {stats.map((st) => (
            <div key={st.label} className="flex items-center gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid={`hist-stat-${st.label}`}>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${st.color}18` }}><st.icon className="h-4 w-4" style={{ color: st.color }} /></div>
              <div><div className="text-lg font-extrabold leading-none" data-testid="hist-stat-value">{st.value}</div><div className="text-[11px] text-muted-ink">{st.label}</div></div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between rounded-2xl border border-[rgba(214,69,69,0.28)] bg-[rgba(214,69,69,0.04)] p-5">
        <div>
          <div className="text-sm font-bold text-[#9B2226]">Purge des données historiques</div>
          <div className="mt-0.5 text-xs text-muted-ink">Action irréversible. Retire toutes les exploitations historiques et supprime les spots créés par l'import de suivi.</div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button disabled={!summary.has_historical || purging} className="bg-[#D64545] text-white hover:bg-[#C63D3D]" data-testid="purge-hist-btn">
              {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-2 h-4 w-4" /> Purger l'historique</>}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-[#D64545]" /> Confirmer la purge de l'historique</AlertDialogTitle>
              <AlertDialogDescription>
                Vous allez supprimer <span className="font-semibold text-ink">{summary.spots_historical} spots historiques</span>, <span className="font-semibold text-ink">{summary.exploitations_historical} exploitations</span> et <span className="font-semibold text-ink">{summary.code_campagne} codes campagne</span>. Les fiches spots réelles sont conservées. Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="purge-hist-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={purge} className="bg-[#D64545] hover:bg-[#C63D3D]" data-testid="purge-hist-confirm">Oui, purger</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function EvergivingDataPanel() {
  const [summary, setSummary] = useState(null);
  const [purging, setPurging] = useState(false);
  const [purgeCorr, setPurgeCorr] = useState(false);
  const load = () => api.get("/admin/evergiving-data/summary").then((r) => setSummary(r.data));
  useEffect(() => { load(); }, []);
  const purge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/evergiving-data/purge", { purge_correspondances: purgeCorr });
      const d = data.deleted;
      toast.success(`Evergiving purgé : ${d.bulletins} bulletins, ${d.rds} RD, ${d.spots} spots supprimés`);
      load();
    } catch { toast.error("Erreur lors de la purge"); }
    finally { setPurging(false); }
  };
  if (!summary) return <Loading />;
  const stats = [
    { icon: ClipboardList, label: "Bulletins importés", value: summary.bulletins, color: "#1F9D55" },
    { icon: Users2, label: "RD créés (à compléter)", value: summary.rds_a_completer, color: "#2F6FED" },
    { icon: HeartHandshake, label: "Associations créées", value: summary.associations, color: "#EE722E" },
    { icon: MapPin, label: "Spots créés + correspondances", value: `${summary.spots} + ${summary.correspondances}`, color: "#8B5CF6" },
  ];
  return (
    <div data-testid="evergiving-data-panel">
      <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--surface-2)]"><HeartHandshake className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
          <div>
            <h3 className="text-sm font-bold">Données Evergiving (bulletins)</h3>
            <p className="mt-1 text-sm text-muted-ink">
              Bulletins granulaires importés depuis l'export Evergiving (données anonymisées, historiques).
              La purge supprime bulletins, RD/associations/spots auto-créés. Les correspondances de lieux sont
              conservées par défaut (mémoire des validations) pour accélérer les prochains imports.
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 @2xl/page:grid-cols-2 @4xl/page:grid-cols-4">
          {stats.map((st) => (
            <div key={st.label} className="flex items-center gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid={`evg-stat-${st.label}`}>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${st.color}18` }}><st.icon className="h-4 w-4" style={{ color: st.color }} /></div>
              <div><div className="text-lg font-extrabold leading-none" data-testid="evg-stat-value">{st.value}</div><div className="text-[11px] text-muted-ink">{st.label}</div></div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[rgba(214,69,69,0.28)] bg-[rgba(214,69,69,0.04)] p-5">
        <div>
          <div className="text-sm font-bold text-[#9B2226]">Purge des données Evergiving</div>
          <label className="mt-1 flex items-center gap-2 text-xs text-muted-ink">
            <input type="checkbox" checked={purgeCorr} onChange={(e) => setPurgeCorr(e.target.checked)} data-testid="evg-purge-corr" />
            Supprimer aussi les correspondances de lieux mémorisées
          </label>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button disabled={!summary.has_evergiving || purging} className="bg-[#D64545] text-white hover:bg-[#C63D3D]" data-testid="purge-evg-btn">
              {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-2 h-4 w-4" /> Purger Evergiving</>}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-[#D64545]" /> Confirmer la purge Evergiving</AlertDialogTitle>
              <AlertDialogDescription>
                Vous allez supprimer <span className="font-semibold text-ink">{summary.bulletins} bulletins</span>, <span className="font-semibold text-ink">{summary.rds} RD</span>, <span className="font-semibold text-ink">{summary.associations} associations</span> et <span className="font-semibold text-ink">{summary.spots} spots</span> issus de l'import.{purgeCorr ? " Les correspondances de lieux seront également supprimées." : " Les correspondances de lieux seront conservées."} Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="purge-evg-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={purge} className="bg-[#D64545] hover:bg-[#C63D3D]" data-testid="purge-evg-confirm">Oui, purger</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

// Styles de badge par catégorie d'action (journal d'activité).
const JOURNAL_CAT_STYLES = {
  suppression: { label: "Suppression", cls: "bg-[#FDECEC] text-[#C0392B] border-[#F3C9C4]" },
  restauration: { label: "Restauration", cls: "bg-[#E9F7EF] text-[#1F8A4C] border-[#BFE8CF]" },
  deplacement: { label: "Déplacement", cls: "bg-[#EAF2FD] text-[#2563A8] border-[#C4DBF5]" },
  modification: { label: "Modification", cls: "bg-[rgba(238,114,46,0.1)] text-[var(--shariti-orange)] border-[rgba(238,114,46,0.28)]" },
  creation: { label: "Création", cls: "bg-[#E6F6F3] text-[#0E8C7F] border-[#BFE6E0]" },
  annulation: { label: "Annulation", cls: "bg-[#FBF1E3] text-[#B9770E] border-[#F0DCBC]" },
  import_sync: { label: "Import / Sync", cls: "bg-[#F1ECFA] text-[#6D4FB8] border-[#DBCEF0]" },
  communication: { label: "Communication", cls: "bg-[#E8EFFE] text-[#3A5BBF] border-[#CBD9F7]" },
  consultation: { label: "Consultation", cls: "bg-[var(--surface-2)] text-muted-ink border-[var(--border-soft)]" },
  autre: { label: "Autre", cls: "bg-[var(--surface-2)] text-muted-ink border-[var(--border-soft)]" },
};

// Libellés FR des types d'entité concernés par une action.
const JOURNAL_ENTITY_LABELS = {
  booking: "Créneau", spot: "Magasin", ligne: "Ligne", rd: "Collaborateur",
  user: "Utilisateur", territoire: "Zone", equipe: "Équipe", chef_equipe: "Chef d'équipe",
  association: "Association", bulletin: "Bulletin", email: "Email", settings: "Paramètres",
  call: "Appel", mandat: "Mandat", pointage: "Pointage", confirmation: "Confirmation",
  spots: "Magasins", pap: "PAP", demo_data: "Données démo", historical_data: "Données historiques",
  evergiving_data: "Données Evergiving", backup: "Sauvegarde", canal: "Canal", perimetre: "Périmètre",
  code_campagne: "Code campagne",
};

function JournalPanel() {
  const [facets, setFacets] = useState({ actions: [], users: [], categories: [] });
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Filtres
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [action, setAction] = useState("all");
  const [userId, setUserId] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const PAGE = 50;

  useEffect(() => {
    api.get("/admin/activity/facets").then((r) => setFacets(r.data || { actions: [], users: [], categories: [] })).catch(() => {});
  }, []);

  const buildParams = (offset) => {
    const p = { limit: PAGE, offset };
    if (q.trim()) p.q = q.trim();
    if (category !== "all") p.category = category;
    if (action !== "all") p.action = action;
    if (userId !== "all") p.user_id = userId;
    if (start) p.start = start;
    if (end) p.end = end;
    return p;
  };

  const load = async (offset = 0) => {
    const first = offset === 0;
    first ? setLoading(true) : setLoadingMore(true);
    try {
      const { data } = await api.get("/admin/activity", { params: buildParams(offset) });
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
      setRows((prev) => (first ? data.items : [...(prev || []), ...data.items]));
    } catch { toast.error("Chargement du journal impossible."); }
    finally { first ? setLoading(false) : setLoadingMore(false); }
  };

  // Rechargement à chaque changement de filtre (léger debounce pour la recherche texte).
  useEffect(() => {
    const t = setTimeout(() => load(0), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, action, userId, start, end]);

  // Le sélecteur d'action se restreint à la catégorie choisie.
  const actionOptions = category === "all"
    ? facets.actions
    : (facets.actions || []).filter((a) => a.category === category);

  const hasFilters = q || category !== "all" || action !== "all" || userId !== "all" || start || end;
  const resetFilters = () => { setQ(""); setCategory("all"); setAction("all"); setUserId("all"); setStart(""); setEnd(""); };

  return (
    <div className="space-y-3" data-testid="admin-journal">
      {/* Barre de filtres */}
      <div className="space-y-2.5 rounded-2xl border border-[var(--border-soft)] bg-white p-3">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (détail, personne, action, identifiant…)"
              className="h-9 pl-9 text-sm" data-testid="journal-search" />
          </div>
          <Select value={category} onValueChange={(v) => { setCategory(v); setAction("all"); }}>
            <SelectTrigger className="h-9 w-full text-sm lg:w-52" data-testid="journal-filter-category">
              <SelectValue placeholder="Toutes les catégories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              {(facets.categories || []).map((c) => (
                <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="h-9 w-full text-sm lg:w-52" data-testid="journal-filter-user">
              <SelectValue placeholder="Toutes les personnes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les personnes</SelectItem>
              {(facets.users || []).map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} ({u.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="h-9 w-full text-sm sm:w-72" data-testid="journal-filter-action">
              <SelectValue placeholder="Toutes les actions" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Toutes les actions</SelectItem>
              {actionOptions.map((a) => (
                <SelectItem key={a.action} value={a.action}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Label className="text-[11px] font-semibold text-muted-ink">Du</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="h-9 w-[150px] text-sm" data-testid="journal-filter-start" />
            <Label className="text-[11px] font-semibold text-muted-ink">au</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="h-9 w-[150px] text-sm" data-testid="journal-filter-end" />
          </div>
          {hasFilters && (
            <Button variant="outline" size="sm" onClick={resetFilters} data-testid="journal-filter-reset"
              className="h-9 gap-1 text-xs">
              <X className="h-3.5 w-3.5" /> Réinitialiser
            </Button>
          )}
        </div>
      </div>

      {/* Résultats */}
      {loading || rows === null ? (
        <Loading />
      ) : rows.length === 0 ? (
        <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-14 text-center" data-testid="journal-empty">
          <ScrollText className="h-7 w-7 text-muted-ink" />
          <p className="mt-2 text-sm font-bold text-ink">Aucune action ne correspond</p>
          <p className="mt-1 max-w-xs text-xs text-muted-ink">Ajustez les filtres ou la période. Le journal enregistre chaque action sensible (création, modification, déplacement, suppression…).</p>
        </div>
      ) : (
        <>
          <p className="px-1 text-[11px] font-semibold text-muted-ink" data-testid="journal-count">
            {total} action{total > 1 ? "s" : ""} — {rows.length} affichée{rows.length > 1 ? "s" : ""}
          </p>
          <ListCard>
            {rows.map((a) => {
              const cat = JOURNAL_CAT_STYLES[a.category] || JOURNAL_CAT_STYLES.autre;
              return (
                <div key={a.id} className="flex flex-col gap-1 border-b border-[var(--border-soft)] px-4 py-3 last:border-0" data-testid={`journal-row-${a.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cat.cls}`}>{cat.label}</span>
                      <span className="text-sm font-bold text-ink">{a.action_label || a.action}</span>
                      {a.entity_type && (
                        <span className="text-[11px] text-muted-ink">· {JOURNAL_ENTITY_LABELS[a.entity_type] || a.entity_type}</span>
                      )}
                    </div>
                    <span className="shrink-0 whitespace-nowrap text-xs text-muted-ink" title={a.timestamp}>{frenchDateTime(a.timestamp)}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-ink">
                    <span className="font-semibold text-ink">{a.user_name || "Système"}</span>
                    {a.details && <span>— {a.details}</span>}
                  </div>
                </div>
              );
            })}
          </ListCard>
          {hasMore && (
            <div className="grid place-items-center pt-1">
              <Button variant="outline" size="sm" disabled={loadingMore} onClick={() => load(rows.length)}
                data-testid="journal-load-more" className="h-8 gap-1 text-xs">
                {loadingMore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronDown className="h-3.5 w-3.5" />}
                Charger plus
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
